import {
  analyze, bbWorld, buildReport, cleanPolyline, colOf, createSymbol, DEFAULT_SETTINGS, deviceName, embedCustomSymbols, insertMacro,
  keyName, kxy, LIB, LIB_ORDER, loc, makeMacro, symbolItems, type Macro, type MacroSummary,
  insertPageMacro, macroPlaceholders, makePageMacro, type ValueSet,
  addDoors, addLayoutItem, addPlates, autoLayout, blankLayout, doorInside, doorSizes, isDoorDevice, DEFAULT_START, fitScale, frameItems, LAYOUT_SCALES, layoutDevices, layoutItems, parsePlcAddress,
  PLC_KIND, placedKeys, RAIL_H, readdressPlc, snapToRail, toPlate, toSheet, type PanelLayout,
  diffProjects, type ProjectDiff,
  CATALOG, CONTROL_NAMES, addBoardLayout, boardSpecFromSingleLine, boardToText, generateBoard, parseBoardText, sectionAmps, type BoardSpec, boardFromSingleLine, MAX_INCOMER, generateDistribution, generateMotorCircuits, INCOMER_NAMES, capCurrent, type FeederSpec, IO_NAMES, parseFeederList, parseSteps, type IncomerKind, parseIoList, parseMotorList, ratedCurrent, STARTER_NAMES, type Part,
  prefixFor, standardOf, svgFor, SOLENOID_VALVES, cabinet3D, cabinetDepth, cabinetWiring, LAYERS, hiddenLayers, mergeProjects, partText, cablePropsOf, normaliseTitleBlock, MAX_LOGO,
  blackBoxSymbol, pinList, registerCustomSymbols, singleLinePages, getLang, LANGS, loadLang, projectTexts, suggestTranslation, translateProject,
  newId, nextTag, nextTerminal, normaliseProject, normaliseTag, pinName, pinNode, pinsW, potentialOf, PTYPES,
  projectToDxf, ptypeFromName, REPORT_TABS, reportToCsv, type Report as ReportTable, applyAiOps, describeAiOp, type AiOp, markingList, marksToCsv, marksToText, MARK_KINDS, type MarkOptions, route, runChecks, onSeg, PLUG_TYPES, snapV, SHEET, WIRE_COLOURS, WIRE_SECTIONS, wireNode,
  type Analysis, type Placed, type Project, type Pt, type ReportTab, type Sym, type TagMemory, type Wire,
  newReportPages, pageItems, REPORT_PAGE_TABS, reportPageName, syncReportPages, type ReportPageTab,
  applyAssignment, matchAssignmentList, readAssignmentList, plcAutomationML, type AmlOptions, fnOf, customSymbol, isCustomKey, symbolName,
  stampRevision, today,
} from '@enercad/core';
import { api, ApiError, type AiFinding, type AiReview, type LiveState, type ProjectDoc, type ProjectRole } from '../api';
import { addLocalRevision, allowFile, createLocal, getLocal, localHistory, saveLocal } from '../localStore';
import { ensureFronts, ensureParts, frontOf, loadType, loadTypeAll, partOf, partsForType } from '../parts';
import { downloadFile, esc, fmtDate, LOGO, openModal, qs, readFileText, reloadIfStale, safeFileName, toast } from '../ui';
import { aiUpload } from '../pdfImport';
import { NavView } from './navigator';
import { langPicker } from '../lang';
import { itemsToSvg, openPinMarkers, pageLayers, paperSVG, symbolPreview } from './render';
import { t as $t, tk } from '@enercad/core';


type Tool = 'select' | 'wire' | 'place' | 'macro' | 'ldev' | 'lrail' | 'lduct';
type SaveState = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict' | 'offline' | 'file' | 'browser' | 'perm';

interface MoveDrag {
  k: 'move';
  a: Pt;
  snap: string;
  S: { s: Sym; x: number; y: number }[];
  full: { w: Wire; o: Pt[] }[];
  att: { w: Wire; o: Pt[]; e: number }[];
  moved: boolean;
}
type Drag =
  /** btn -1: one finger on a touch screen; tap: what a tap (no move) selects */
  | { k: 'pan'; btn: number; sx: number; sy: number; vx: number; vy: number; moved: boolean; tap?: string }
  | { k: 'lmove'; a: Pt; snap: string; orig: Map<string, { x: number; y: number }>; moved: boolean }
  | { k: 'ldraw'; kind: 'rail' | 'duct'; a: Pt; b: Pt }
  | { k: 'box'; a: Pt; b: Pt }
  | MoveDrag;

const HINT: Record<Tool, string> = {
  select: tk("Tıkla: seç · Sürükle: taşı · Boş alanda sürükle: çerçeveyle seç"),
  wire: tk("Bir uçtan başla, köşeler için tıkla; bir uca ya da tele tıklayınca biter. Esc: bitir"),
  place: tk("Tıkla: yerleştir (etiket otomatik artar) · R: döndür · Esc: bitir"),
  macro: tk("Tıkla: makroyu yerleştir (etiketler yeniden numaralanır) · Esc: vazgeç"),
  ldev: tk("Tıkla: cihazı plakaya yerleştir (raya yakınsa raya oturur) · Esc: vazgeç"),
  lrail: tk("Sürükle: DIN rayı çiz (soldan sağa) · Esc: bitir"),
  lduct: tk("Sürükle: kablo kanalı çiz (köşeden köşeye) · Esc: bitir"),
};
/** For what a local project does not have (it is not on the server). */
const LOCAL_ONLY = () => $t('Yerel projede kullanılamaz: proje sunucuya gönderilmez.');

const SAVE_TEXT: Record<SaveState, string> = {
  saved: tk('Kaydedildi'), dirty: tk("Kaydedilecek…"), saving: tk("Kaydediliyor…"), error: tk("Kaydedilemedi, tekrar denenecek"),
  conflict: tk("Çakışma: kaydedilmedi"), offline: tk("Oturum sona erdi"),
  file: tk('Bilgisayara kaydedildi'), browser: tk('Bu tarayıcıda kaydedildi'), perm: tk('Dosyaya yazılmadı: izin gerekiyor'),
};

const fld = (label: string, inner: string) => `<label class="fld"><span>${label}</span>${inner}</label>`;
const fid = (f: string) => 'f_' + f.replace(/\W/g, '_');
const inp = (f: string, v: unknown, cls = '', ph = '') =>
  `<input data-f="${f}" id="${fid(f)}" class="${cls}" value="${esc(v)}" placeholder="${esc(ph)}" autocomplete="off">`;
const sel = (f: string, v: string, opts: [string, string][]) =>
  `<select data-f="${f}" id="${fid(f)}">${opts.map((o) => `<option value="${esc(o[0])}"${o[0] === v ? ' selected' : ''}>${esc(o[1])}</option>`).join('')}</select>`;

/** symbol types the board generators choose parts for */
const GEN_TYPES = ['lbs3', 'mccb3', 'cb3m', 'cos3', 'cbct', 'efr', 'coil_st', 'cb3n', 'cb1', 'fsd3', 'kwh1', 'kwh3', 'rcd2', 'rcd4', 'spd4', 'emeter', 'ct', 'fuse', 'term', 'coil', 'cap3', 'rpr', 'cable'];

export class Editor {
  private pr: Project;
  private id: string;
  private version: number;
  private cur = 0;
  /** AI features available for this user (review button on the check report) */
  private aiOn = false;
  /** the AI provider reads PDF files (else the first page goes as a picture) */
  private aiPdf = true;
  private sel = new Set<string>();
  private tool: Tool = 'select';
  private placeType: string | null = null;
  private placeRot = 0;
  private undoS: string[] = [];
  private redoS: string[] = [];
  private A!: Analysis;
  private mode: 'sch' | 'rep' | 'nav' = 'sch';
  private navView!: NavView;
  private repTab: ReportTab = 'dev';
  private clip: { S: Sym[]; W: Wire[] } | null = null;
  private view = { x: 0, y: 0, s: 2 };
  private drag: Drag | null = null;
  private draft: Pt[] = [];
  private mouse: Pt = [0, 0];
  private spaceDown = false;
  private opts = { grid: true, net: false };
  private mem: TagMemory = {};
  private nav: 'pages' | 'dev' | 'pot' = 'pages';
  private macros: (MacroSummary & { mine?: boolean; shared?: boolean })[] = [];
  private placeMacro: Macro | null = null;
  /** placeholder values chosen for the macro being placed */
  private macroValues: Record<string, string> = {};
  private placeDev: string | null = null;
  private role: ProjectRole = 'owner';
  /** set by the app shell; enables "save to shared library" options */
  platformAdmin = false;
  /** changes to highlight on the drawing (after "compare with a version") */
  private highlight: { diff: ProjectDiff; label: string } | null = null;
  private get readOnly() {
    return this.role === 'read';
  }
  private macroGhost = '';

  private rev = 0;
  private savedRev = 0;
  private saveState: SaveState = 'saved';
  private saveTimer = 0;
  private saving = false;

  /* live collaboration */
  private readonly client = Math.random().toString(36).slice(2) + Date.now().toString(36);
  /** the project as last seen on the server (for three-way merges) */
  private base = '';
  /** page id → last local edit (ms) */
  private edited = new Map<string, number>();
  private live: LiveState = { version: 0, users: [], locks: {}, mine: [] };
  private liveTimer = 0;
  private local = false;
  /** fingers on the drawing (touch screens) and the two-finger zoom going on */
  private touches = new Map<number, [number, number]>();
  private pinch: { d: number; cx: number; cy: number } | null = null;
  /** a finger held still on something: picks it up to move it */
  private holdTimer = 0;
  private livePage = '';
  private liveBusy = false;

  private el: HTMLElement;
  private svg!: SVGSVGElement;
  private ro: ResizeObserver;
  private offs: (() => void)[] = [];

  constructor(private root: HTMLElement, doc: ProjectDoc) {
    this.pr = normaliseProject(doc.data);
    this.id = doc.id;
    this.version = doc.version;
    this.base = JSON.stringify(this.pr);
    this.role = doc.role ?? 'owner';
    // a local project: saved to the user's file, nothing goes to the server to be kept, no live collaboration
    this.local = !!doc.local;
    if (this.local) this.liveTimer = -1;
    this.el = document.createElement('div');
    this.el.className = 'app';
    this.el.innerHTML = this.layout();
    root.appendChild(this.el);
    this.svg = qs<SVGSVGElement>(this.el, '.cv');
    this.A = analyze(this.pr, partOf);
    const self = this;
    this.navView = new NavView(qs(this.el, '#nav'), {
      get pr() { return self.pr; },
      get A() { return self.A; },
      get readOnly() { return self.readOnly; },
      mutate: (fn) => this.mutate(fn),
      focusOn: (pi, sid) => this.focusOn(pi, sid),
      focusPage: (pi) => {
        this.mode = 'sch';
        this.cur = pi;
        this.sel.clear();
        this.renderAll();
        this.fit();
      },
    });
    this.bind();
    this.applyPanels();
    this.ro = new ResizeObserver(() => this.applyView());
    this.ro.observe(this.svg);
    this.renderAll();
    // set the viewBox right away: requestAnimationFrame does not run in background tabs,
    // and pointer maths must match what is on screen from the first event on
    this.fit();
    void this.loadMacros();
    void api.aiStatus().then((s) => {
      this.aiOn = s.available;
      this.aiPdf = s.pdf !== false;
      if (this.mode === 'rep') this.renderAll();
    }, () => {});
    void ensureParts(this.pr.pages.flatMap((p) => p.syms.map((s) => s.part))).then(() => {
      this.renderStatus();
      this.renderProps();
      if (this.mode === 'rep') this.renderReports();
    });
    requestAnimationFrame(() => {
      this.fit();
      this.renderOverlay();
    });
  }

  destroy() {
    clearTimeout(this.liveTimer);
    this.liveTimer = -1;
    if (!this.local) void api.live(this.id, { client: this.client, leave: true }).catch(() => {});
    this.ro.disconnect();
    this.offs.forEach((f) => f());
    if (this.rev !== this.savedRev) void this.flush();
    this.el.remove();
  }

  /* ================= layout ================= */
  private layout() {
    return `
<header>
  <a class="brand" href="#/" title="${$t("Projelere dön")}">${LOGO}EnerCAD</a>
  <div class="grp ptogs">
    <button class="btn ptog" data-a="tglleft" title="${$t('Sol paneli (sayfalar, semboller) gizle / göster  [')}" aria-label="${$t('Sol panel')}"><svg viewBox="0 0 16 12" width="16" height="12"><rect x=".5" y=".5" width="15" height="11" rx="1.5" fill="none" stroke="currentColor"/><rect x="1.5" y="1.5" width="4" height="9" fill="currentColor"/></svg></button>
    <button class="btn ptog" data-a="tglright" title="${$t('Sağ paneli (özellikler) gizle / göster  ]')}" aria-label="${$t('Sağ panel')}"><svg viewBox="0 0 16 12" width="16" height="12"><rect x=".5" y=".5" width="15" height="11" rx="1.5" fill="none" stroke="currentColor"/><rect x="10.5" y="1.5" width="4" height="9" fill="currentColor"/></svg></button>
  </div>
  <details class="menu"><summary class="btn">${$t('Kütüphane')} <span class="caret">▾</span></summary><div class="menu-pop">
    <a class="btn" href="#/parts" title="${$t("Parça veritabanı")}">${$t("Parçalar")}<small>${$t("Parça veritabanı")}</small></a>
    <a class="btn" href="#/symbols" title="${$t("Kendi sembollerinizi çizin")}">${$t("Semboller")}<small>${$t("Kendi sembollerinizi çizin")}</small></a>
  </div></details>
  <details class="menu"><summary class="btn" title="${$t('PDF, DXF, Excel, TIA, JSON ve çeviri')}">${$t('Dışa aktar')} <span class="caret">▾</span></summary><div class="menu-pop">
    <button class="btn" data-a="saveas" title="${$t('Projenin bir kopyasını bilgisayarınızda yeni bir dosyaya ya da sunucuda yeni bir projeye kaydeder; çalışma kopyada sürer')}">${$t('Farklı kaydet…')}<small>${$t('Bilgisayarda yeni dosyaya ya da sunucuda yeni projeye')}</small></button>
    <button class="btn" data-a="pdf" title=""${$t("Kapak, içindekiler, şema ve rapor sayfalarıyla PDF")}">${$t("PDF")}<small>${$t("Kapak, içindekiler, şema ve rapor sayfalarıyla PDF")}</small></button>
    <button class="btn" data-a="dxf" title="${$t("AutoCAD için DXF")}">${$t("DXF")}<small>${$t("AutoCAD için DXF")}</small></button>
    <button class="btn" data-a="xlsx" title="${$t("Tüm raporlar tek Excel dosyasında")}">${$t("Excel")}<small>${$t("Tüm raporlar tek Excel dosyasında")}</small></button>
    <button class="btn" data-a="translate" title="${$t('Proje metinlerini sözlükle başka dile çevir; çeviriyle PDF, Excel ya da kopya al')}">${$t('Çeviri')}<small>${$t('Proje metinlerini sözlükle başka dile çevir; çeviriyle PDF, Excel ya da kopya al')}</small></button>
    <button class="btn" data-a="tia" title="${$t("PLC etiket tablosu, TIA Portal'a içe aktarılabilir (.xlsx)")}">${$t("TIA")}<small>${$t("PLC etiket tablosu, TIA Portal'a içe aktarılabilir (.xlsx)")}</small></button>
    <button class="btn" data-a="export" title="${$t("Projeyi .json dosyası olarak indir")}">${$t("JSON")}<small>${$t("Projeyi .json dosyası olarak indir")}</small></button>
    <button class="btn" data-a="import" title="${$t(".json dosyasından sayfaları bu projeye aktar")}">${$t("İçe aktar")}<small>${$t(".json dosyasından sayfaları bu projeye aktar")}</small></button>
  </div></details>
  <div class="grp">
    <button class="btn" data-a="undo" title="${$t("Geri al (Ctrl+Z)")}">${$t("↶ Geri")}</button>
    <button class="btn" data-a="redo" title="${$t("Yinele (Ctrl+Y)")}">${$t("↷ İleri")}</button>
  </div>
  <div class="grp">
    <button class="btn" data-a="tsel" title="${$t("Seç ve taşı (V)")}">${$t("Seç <kbd>V</kbd>")}</button>
    <button class="btn" data-a="twire" title="${$t("Bağlantı çiz (W)")}">${$t("Bağlantı <kbd>W</kbd>")}</button>
    <button class="btn" data-a="rot" title="${$t("Döndür (R)")}">${$t("Döndür <kbd>R</kbd>")}</button>
    <button class="btn" data-a="del" title="${$t("Sil (Del)")}">${$t("Sil")}</button>
    <button class="btn" data-a="find" title="${$t("Bul ve değiştir (Ctrl+F)")}">${$t("Bul")}</button>
  </div>
  <div class="grp">
    <button class="btn" data-a="zout" title="${$t("Uzaklaş")}">−</button>
    <button class="btn" data-a="fit" title="${$t("Sayfaya sığdır (F)")}">${$t("Sığdır")}</button>
    <button class="btn" data-a="zin" title="${$t("Yakınlaş")}">+</button>
    <details class="menu"><summary class="btn">${$t('Görünüm')} <span class="caret">▾</span></summary><div class="menu-pop">
    <button class="btn" data-a="grid" title="${$t("Izgarayı göster/gizle")}">${$t("Izgara")}</button>
    <button class="btn" data-a="net" title="${$t("Tel numaralarını göster")}">${$t("Tel no")}</button>
  <button class="btn" data-a="layers" title="${$t('Katmanları göster/gizle, çıktıya dahil et')}">${$t('Katmanlar')}</button>
    </div></details>
  </div>
  <span class="spacer"></span>
  ${this.readOnly ? `<span class="ro-banner" title="${$t("Bu projeyi yalnızca görüntüleyebilirsiniz")}">${$t("Salt okunur")}</span>` : ''}
  ${langPicker()}
  ${this.local
    ? `<span class="pill ok" title="${$t('Bu proje bilgisayarınızdaki dosyada; sunucuda saklanmaz. Paylaşım, canlı ortak çalışma ve yapay zeka ile gözden geçirme yerel projede yoktur.')}">${$t('Yerel proje')}</span><button class="btn" data-a="allowf" id="allowf" hidden>${$t('Dosyaya kaydetmeye izin ver')}</button>
  <button class="btn" data-a="revs" title="${$t('Adlı revizyonlar, karşılaştırma ve geri yükleme; proje dosyasının içinde saklanır')}">${$t("Revizyonlar")}</button>`
    : `<button class="btn" data-a="revs" title="${$t("Revizyonlar, geçmiş ve karşılaştırma")}">${$t("Revizyonlar")}</button>
  ${this.role === 'owner' ? `<button class="btn" data-a="share" title="${$t("Projeyi başka kullanıcılarla paylaş")}">${$t("Paylaş")}</button>` : ''}`}
  <span class="live" id="live" aria-live="polite"></span>
  <span class="savest" id="savest" aria-live="polite"></span>
  <div class="grp">
    <button class="btn" data-a="msch">${$t("Şema")}</button>
    <button class="btn" data-a="mrep">${$t("Raporlar")}</button>
    <button class="btn" data-a="mnav" title="${$t("Klemens şeritleri, bağlantılar, kablolar ve cihazlar; tabloda düzenleme")}">${$t("Navigatör")}</button>
  </div>
</header>
<aside class="left">
  <div class="sec">
    <div class="navtabs" role="tablist"><button class="btn" data-nav="pages">${$t("Sayfalar")}</button><button class="btn" data-nav="dev">${$t("Cihazlar")}</button><button class="btn" data-nav="pot">${$t("Potansiyeller")}</button></div>
    <div id="navDev" hidden><p class="note navhelp">${$t('Projedeki cihazlar, kurulum yeri ve konuma (= +) göre gruplu. Her cihazın altında şemadaki bütün parçaları (bobin, kontaklar, klemensler) ve yerleri var; tıklayınca o parçaya gider.')}</p><input class="search" id="devq" placeholder="${$t("Cihaz ara… (ör. K1, motor)")}" autocomplete="off"><div id="devtree" class="navtree"></div></div>
    <div id="navPot" hidden><p class="note navhelp">${$t('Adı olan potansiyeller (L1, N, PE, 24V…) ve geçtikleri sayfalar. Ada tıklayınca ilk teline, sayfa numarasına tıklayınca o sayfadaki teline gider.')}</p><div id="potlist" class="navtree"></div></div>
    <div id="navPages">
    <h3>${$t("Sayfalar")}</h3>
    <div class="pgadd"><button class="btn" data-a="padd" title="${$t("Geçerli sayfadan sonra boş şema sayfası ekle")}">${$t("+ Boş sayfa")}</button><button class="btn" data-a="bgen" title="${$t('Dağıtım ve ana dağıtım panoları: bir ya da birden fazla giriş (trafo, jeneratör, hat), bara bölümleri, kuplaj, çıkışlar, ölçüm, senkron kumanda')}">${$t('+ Güç panosu')}</button><button class="btn" data-a="pgen" title="${$t("Motor listesinden güç, kumanda ve PLC sayfaları")}">${$t("+ MCC panosu")}</button><button class="btn" data-a="cgen" title="${$t('Sensör, lamba, vana ve transmitter listesinden PLC ve kumanda sayfaları, acil stop')}">${$t('+ Kumanda panosu')}</button><button class="btn" data-a="kgen" title="${$t('Kademeli kompanzasyon: giriş şalteri, kademe sigortaları, kontaktörler, kondansatörler ve reaktif güç rölesi')}">${$t('+ Kompanzasyon panosu')}</button><button class="btn" data-a="paddsl" title="${$t("Tek hat şeması: projedeki motor beslemelerinden otomatik ya da boş")}">${$t("+ Tek hat")}</button><details class="menu"><summary class="btn" title="${$t('Elle pano yerleşimi ya da kapak sayfası')}">⋯</summary><div class="menu-pop"><button class="btn" data-a="paddlayout">${$t("Pano yerleşimi")}<small>${$t("Boş montaj plakası")}</small></button><button class="btn" data-a="padddoor">${$t("Pano kapağı")}<small>${$t("Buton ve lambaların yerleşimi ve delik ölçüleri")}</small></button><button class="btn" data-a="paddrep">${$t('Rapor sayfası')}<small>${$t('Klemens planı, kablo listesi, malzeme listesi, PLC genel bakış… antetli sayfa olarak')}</small></button></div></details></div>
    <div class="pages" id="pages"></div>
    <div class="pgtools">
      <button class="btn" data-a="pup" title="${$t("Sayfayı yukarı taşı")}">▲</button>
      <button class="btn" data-a="pdn" title="${$t("Sayfayı aşağı taşı")}">▼</button>
      <button class="btn danger" data-a="pdel">${$t("Sayfayı sil")}</button>
      <button class="btn" data-a="pmacro" title="${$t('Seçtiğiniz sayfaları, değer kümeleriyle birlikte sayfa makrosu olarak kaydedin')}">${$t('Sayfa makrosu')}</button>
    </div>
    </div>
  </div>
  <div class="sec">
    <h3>${$t("Sembol kütüphanesi")}<button class="btn" data-a="libopen" title="${$t('Tüm kategorileri aç')}">${$t('Tümünü aç')}</button><button class="btn" data-a="libclose" title="${$t('Tüm kategorileri kapat')}" style="margin-left:0">${$t('Kapat')}</button></h3>
    <input class="search" id="libq" placeholder="${$t("Sembol ara… (ör. kontak, lamba)")}" autocomplete="off">
    <div id="lib"></div>
  </div>
  <div class="sec">
    <h3>${$t("Makrolar")}</h3>
    <div id="macros" class="macros"><p class="note">${$t("Yükleniyor…")}</p></div>
  </div>
  <div class="sec">
    <details class="keys"><summary>${$t("Kısayollar")}</summary>
      <dl>
        <dt>V / W</dt><dd>${$t("Seç / bağlantı çiz")}</dd>
        <dt>R</dt><dd>${$t("Seçileni ya da yerleştirileni döndür")}</dd>
        <dt>${$t("Del")}</dt><dd>${$t("Seçileni sil")}</dd>
        <dt>${$t("Ctrl+C / V")}</dt><dd>${$t("Kopyala / imlecin olduğu yere yapıştır")}</dd>
        <dt>${$t("Ctrl+D")}</dt><dd>${$t("Çoğalt")}</dd>
        <dt>${$t("Ctrl+Z / Y")}</dt><dd>${$t("Geri al / yinele")}</dd>
        <dt>${$t("Esc, sağ tık")}</dt><dd>${$t("İşlemi bitir")}</dd>
        <dt>${$t("Tekerlek")}</dt><dd>${$t("Yakınlaş / uzaklaş")}</dd>
        <dt>${$t("Sağ sürükle")}</dt><dd>${$t("Kaydır (Boşluk+sürükle de olur)")}</dd>
        <dt>${$t("PgUp / PgDn")}</dt><dd>${$t("Önceki / sonraki sayfa")}</dd>
        <dt>${$t("Shift")}</dt><dd>${$t("Bağlantıda köşe yönünü çevir")}</dd>
        <dt>[ / ]</dt><dd>${$t("Sol / sağ paneli gizle, göster")}</dd>
      </dl>
    </details>
  </div>
</aside>
<main class="stage">
  <svg class="cv" xmlns="http://www.w3.org/2000/svg">
    <defs><pattern id="gd" x="-2" y="-2" width="4" height="4" patternUnits="userSpaceOnUse"><circle class="gdot" cx="2" cy="2" r=".22"/></pattern></defs>
    <g id="gPaper"></g><g id="gWire"></g><g id="gSym"></g><g id="gLbl"></g><g id="gSel"></g><g id="gOv"></g>
  </svg>
  <div class="rep" id="rep" hidden></div>
  <div class="rep" id="nav" hidden></div>
  <div class="status">
    <span id="stPos">X — Y —</span>
    <span id="stHint"></span>
    <button class="chk" id="stChk" title="${$t("Kontrol raporunu aç")}"></button>
  </div>
</main>
<aside class="right" id="props"></aside>
<input type="file" id="impFile" accept=".json,application/json" hidden>`;
  }

  /** per-browser editor layout (side panels, symbol categories, recent symbols); storage may be unavailable */
  private ui: { noLeft?: boolean; noRight?: boolean; closed?: string[]; recent?: string[] } = (() => {
    try {
      return JSON.parse(localStorage.getItem('enercad.editorUi') || '{}');
    } catch {
      return {};
    }
  })();
  private saveUi() {
    try {
      localStorage.setItem('enercad.editorUi', JSON.stringify(this.ui));
    } catch {
      /* private window: the layout is not remembered */
    }
  }
  /** shows / hides the left (pages, symbols) or right (properties) panel; the drawing takes the room */
  private togglePanel(side: 'left' | 'right', show?: boolean) {
    const key = side === 'left' ? 'noLeft' : 'noRight';
    this.ui[key] = show == null ? !this.ui[key] : !show;
    this.applyPanels();
    this.saveUi();
  }
  private applyPanels() {
    this.el.classList.toggle('noleft', !!this.ui.noLeft);
    this.el.classList.toggle('noright', !!this.ui.noRight);
    this.el.querySelector('[data-a="tglleft"]')?.classList.toggle('on', !this.ui.noLeft);
    this.el.querySelector('[data-a="tglright"]')?.classList.toggle('on', !this.ui.noRight);
  }

  private $(s: string) {
    return qs(this.el, s);
  }
  private get page() {
    return this.pr.pages[this.cur];
  }

  /* ================= history & saving ================= */
  private snap() {
    return JSON.stringify(this.pr);
  }
  private pushUndo(s: string) {
    this.undoS.push(s);
    if (this.undoS.length > 200) this.undoS.shift();
    this.redoS = [];
  }
  private changed() {
    this.rev++;
    this.setSave('dirty');
    this.scheduleSave(1000);
    this.A = analyze(this.pr, partOf);
    this.renderAll();
  }
  private mutate(fn: () => void) {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const lockedBy = this.live.locks[this.page.id];
    if (lockedBy) return void toast($t('Bu sayfayı şu anda {0} düzenliyor; bitirince siz de düzenleyebilirsiniz.', lockedBy));
    const snap = this.snap();
    const before = new Map(this.pr.pages.map((p) => [p.id, JSON.stringify(p)]));
    fn();
    // a change must not reach pages someone else is editing
    const hit = this.pr.pages.find((p) => this.live.locks[p.id] && before.get(p.id) !== JSON.stringify(p));
    if (hit) {
      this.pr = JSON.parse(snap);
      this.A = analyze(this.pr, partOf);
      this.renderAll();
      return void toast($t('"{0}" sayfasını şu anda {1} düzenliyor; bu işlem o sayfayı da değiştireceği için yapılmadı.', hit.desc, this.live.locks[hit.id]));
    }
    this.pushUndo(snap);
    const now = Date.now();
    let fresh = false;
    for (const p of this.pr.pages)
      if (before.get(p.id) !== JSON.stringify(p)) {
        if (!this.live.mine.includes(p.id)) fresh = true;
        this.edited.set(p.id, now);
      }
    this.changed();
    // take the lock of a newly edited page right away
    if (fresh) this.scheduleLive(150);
  }

  /* ================= live collaboration ================= */
  private scheduleLive(ms: number) {
    if (this.liveTimer === -1) return;
    clearTimeout(this.liveTimer);
    this.liveTimer = window.setTimeout(() => void this.heartbeat(), ms);
  }
  /** Pages this editor is working on: edited in the last two minutes or not saved yet. */
  private editingPages(): string[] {
    const now = Date.now(), ids = new Set(this.pr.pages.map((p) => p.id)), out: string[] = [];
    for (const [id, t] of this.edited) {
      if (!ids.has(id) || (now - t > 120_000 && this.rev === this.savedRev)) this.edited.delete(id);
      else out.push(id);
    }
    return out;
  }
  private async heartbeat() {
    if (this.liveBusy || this.liveTimer === -1) return;
    this.liveBusy = true;
    try {
      const r = await api.live(this.id, { client: this.client, page: this.page?.id ?? '', edit: this.readOnly ? [] : this.editingPages() });
      const lockChange = JSON.stringify(r.locks) !== JSON.stringify(this.live.locks) || JSON.stringify(r.users) !== JSON.stringify(this.live.users);
      this.live = r;
      if (r.version > this.version) {
        // someone saved: take it over (or merge through the save when there are local edits)
        if (this.rev === this.savedRev && !this.saving) await this.reloadFromServer(r.by);
        else if (!this.saving) void this.flush();
      } else if (lockChange) {
        this.renderPages();
        this.renderLive();
      }
    } catch {
      /* offline for a moment: try again later */
    } finally {
      this.liveBusy = false;
      this.scheduleLive(this.live.users.length ? 3000 : 12000);
    }
  }
  private renderLive() {
    const el = this.el.querySelector('#live');
    if (!el) return;
    const u = this.live.users;
    const lockedBy = this.page ? this.live.locks[this.page.id] : '';
    const ini = (n: string) => esc(n.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase());
    const where = (pg: string) => {
      const i = this.pr.pages.findIndex((p) => p.id === pg);
      return i < 0 ? '' : $t('sayfa {0}', i + 1);
    };
    el.innerHTML =
      (lockedBy ? `<span class="ro-banner">${$t('Kilitli: {0} düzenliyor', esc(lockedBy))}</span> ` : '') +
      u.map((x) => `<span class="av" title="${esc(x.name)}${x.me ? ' · ' + esc($t('başka bir sekmede')) : ''} · ${esc(where(x.page))}">${ini(x.name)}</span>`).join('');
  }
  private restore(json: string) {
    this.pr = JSON.parse(json);
    // the project's own symbol definitions as they were (e.g. a library match undone)
    if (this.pr.symbols?.length) registerCustomSymbols(this.pr.symbols);
    this.cur = Math.min(this.cur, this.pr.pages.length - 1);
    this.sel.clear();
    this.changed();
  }
  private undo() {
    if (!this.undoS.length) return;
    this.redoS.push(this.snap());
    this.restore(this.undoS.pop()!);
  }
  private redo() {
    if (!this.redoS.length) return;
    this.undoS.push(this.snap());
    this.restore(this.redoS.pop()!);
  }

  private setSave(s: SaveState) {
    this.saveState = s;
    const el = this.el.querySelector('#savest');
    if (el) {
      el.textContent = $t(SAVE_TEXT[s]);
      el.classList.toggle('bad', s === 'error' || s === 'conflict' || s === 'offline' || s === 'perm');
      const allow = this.el.querySelector<HTMLElement>('#allowf');
      if (allow) allow.hidden = s !== 'perm';
    }
  }
  private scheduleSave(ms: number) {
    clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => void this.flush(), ms);
  }
  private async flush() {
    if (this.saving || this.rev === this.savedRev || this.saveState === 'conflict' || this.saveState === 'offline') return;
    this.saving = true;
    const rev = this.rev;
    this.setSave('saving');
    try {
      if (this.local) {
        // always into this browser's own store; into the file where that is allowed
        const where = await saveLocal(this.id, JSON.parse(JSON.stringify(embedCustomSymbols(this.pr))));
        this.savedRev = rev;
        this.setSave(this.rev !== rev ? 'dirty' : where === 'file' ? 'file' : where === 'browser' ? 'browser' : 'perm');
        if (this.rev !== rev) this.scheduleSave(800);
        return;
      }
      const sent = embedCustomSymbols(this.pr);
      const r = await api.save(this.id, sent, this.version, this.client);
      this.version = r.version;
      this.base = JSON.stringify(normaliseProject(JSON.parse(JSON.stringify(sent))));
      this.savedRev = rev;
      this.setSave(this.rev === rev ? 'saved' : 'dirty');
      if (this.rev !== rev) this.scheduleSave(800);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 409 || e.status === 423)) {
        if (e.status === 423) toast(e.message);
        this.saving = false;
        if (!(await this.mergeWithServer(e.status === 423 ? String(e.body.page ?? '') : ''))) {
          this.setSave('conflict');
          this.conflictDialog(e.body.version, e.body.by);
        }
      } else if (e instanceof ApiError && e.status === 401) {
        this.setSave('offline');
        openModal(
          `<h2>${$t("Oturum sona erdi")}</h2><p>${$t("Değişiklikleriniz kaydedilmedi. Kaybetmemek için projeyi indirin, sonra yeniden giriş yapın.")}</p>
          <div class="acts"><button class="btn" data-m="dl">${$t("Projeyi indir (.json)")}</button><a class="btn pri" href="#/">${$t("Giriş sayfası")}</a></div>`,
          (a) => a === 'dl' && this.exportJson(),
        );
      } else {
        this.setSave('error');
        this.scheduleSave(5000);
      }
    } finally {
      this.saving = false;
    }
  }
  private conflictDialog(serverVersion: number, by?: string) {
    const close = openModal(
      `<h2>${$t("Proje başka bir yerde değiştirilmiş")}</h2>
      <p>${$t("Bu proje siz düzenlerken {0} kaydedildi (sunucudaki sürüm {1}). Hangisini tutmak istediğinizi seçin. Kendi sürümünüzü kaydederseniz onun değişiklikleri kaybolmaz, Revizyonlar'da otomatik kopya olarak kalır.", by ? `${$t("<b>{0}</b> tarafından", esc(by))}` : $t("başka bir sekmede ya da bilgisayarda"), serverVersion)}</p>
      <div class="acts"><button class="btn" data-m="reload">${$t("Sunucudakini yükle")}</button><button class="btn pri" data-m="mine">${$t("Benim sürümümü kaydet")}</button></div>`,
      async (a) => {
        close();
        if (a === 'mine') {
          this.version = serverVersion;
          this.setSave('dirty');
          void this.flush();
        } else if (a === 'reload') {
          await this.reloadFromServer();
        }
      },
    );
  }

  /**
   * Someone else saved while we have local edits: merge page by page. Pages
   * locked by others (and `lostPage`, refused by the server) come from the
   * server. Returns false when both sides changed the same page.
   */
  private async mergeWithServer(lostPage: string): Promise<boolean> {
    let doc: Awaited<ReturnType<typeof api.get>>;
    try {
      doc = await api.get(this.id);
    } catch {
      return false;
    }
    const theirs = normaliseProject(doc.data);
    const keep = new Set([...Object.keys(this.live.locks), ...(lostPage ? [lostPage] : [])]);
    const m = mergeProjects(JSON.parse(this.base), JSON.parse(JSON.stringify(this.pr)), theirs, { keepTheirs: keep });
    if (m.conflicts.length && !m.conflicts.every((id) => keep.has(id))) return false;
    const curId = this.page.id;
    this.pr = m.project;
    this.version = doc.version;
    this.base = JSON.stringify(theirs);
    // undo steps hold whole-project snapshots from before the merge
    this.undoS = [];
    this.redoS = [];
    this.sel.clear();
    this.cur = Math.max(0, this.pr.pages.findIndex((p) => p.id === curId));
    if (lostPage) this.edited.delete(lostPage);
    this.rev++;
    this.A = analyze(this.pr, partOf);
    this.renderAll();
    if (m.fromTheirs.length) toast($t('Diğer kullanıcıların değişiklikleri sizinkilerle birleştirildi.'));
    this.setSave('dirty');
    this.scheduleSave(50);
    return true;
  }

  /** Replaces the local copy with the server's current version. */
  private async reloadFromServer(by?: string) {
    const doc = await api.get(this.id);
    const curId = this.page?.id;
    this.pr = normaliseProject(doc.data);
    this.base = JSON.stringify(this.pr);
    this.version = doc.version;
    this.role = doc.role ?? this.role;
    this.undoS = [];
    this.redoS = [];
    this.sel.clear();
    this.highlight = null;
    const at = this.pr.pages.findIndex((p) => p.id === curId);
    this.cur = at >= 0 ? at : Math.min(this.cur, this.pr.pages.length - 1);
    this.savedRev = this.rev;
    this.setSave('saved');
    this.A = analyze(this.pr, partOf);
    this.renderAll();
    if (by) toast($t('{0} projeyi güncelledi; en son hâli yüklendi.', by));
  }

  /* ================= view ================= */
  private applyView() {
    const r = this.svg.getBoundingClientRect();
    if (!r.width) return;
    this.svg.setAttribute('viewBox', `${this.view.x} ${this.view.y} ${r.width / this.view.s} ${r.height / this.view.s}`);
  }
  private fit() {
    const r = this.svg.getBoundingClientRect();
    if (!r.width) return;
    const v = this.view;
    v.s = Math.min(r.width / (SHEET.W + 12), r.height / (SHEET.H + 12));
    v.x = -(r.width / v.s - SHEET.W) / 2;
    v.y = -(r.height / v.s - SHEET.H) / 2;
    this.applyView();
  }
  private zoomAt(f: number, cx?: number, cy?: number) {
    const r = this.svg.getBoundingClientRect(), v = this.view;
    if (cx == null || cy == null) {
      cx = r.left + r.width / 2;
      cy = r.top + r.height / 2;
    }
    const wx = v.x + (cx - r.left) / v.s, wy = v.y + (cy - r.top) / v.s;
    v.s = Math.max(0.6, Math.min(40, v.s * f));
    v.x = wx - (cx - r.left) / v.s;
    v.y = wy - (cy - r.top) / v.s;
    this.applyView();
  }
  /** Screen → drawing mm, using the transform the browser actually rendered with. */
  private toWorld(e: MouseEvent): Pt {
    const m = this.svg.getScreenCTM();
    if (m) {
      const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
      return [p.x, p.y];
    }
    const r = this.svg.getBoundingClientRect();
    return [this.view.x + (e.clientX - r.left) / this.view.s, this.view.y + (e.clientY - r.top) / this.view.s];
  }

  /* ================= rendering ================= */
  private renderAll() {
    // a tool from the other page kind (e.g. "place symbol" on a panel layout) is dropped
    const layoutTools: Tool[] = ['ldev', 'lrail', 'lduct'];
    const isLayout = this.page.kind === 'layout';
    if (this.page.kind === 'report' ? this.tool !== 'select' : isLayout ? !['select', ...layoutTools].includes(this.tool) : layoutTools.includes(this.tool)) {
      this.tool = 'select';
      this.placeType = this.placeDev = null;
      this.placeMacro = null;
      this.draft = [];
    }
    this.renderPages();
    this.renderNav();
    this.renderPage();
    this.renderProps();
    this.renderTools();
    this.renderStatus();
    this.$('#rep').hidden = this.mode !== 'rep';
    if (this.mode === 'rep') this.renderReports();
    this.$('#nav').hidden = this.mode !== 'nav';
    if (this.mode === 'nav') this.navView.render();
    this.renderLib();
    this.setSave(this.saveState);
  }
  private renderPage() {
    if (this.page.kind === 'layout') return this.renderLayoutPage();
    if (this.page.kind === 'report') {
      // a report page: drawn from the project as it is now, nothing on it to edit
      this.$('#gPaper').innerHTML = `<rect class="paper" x="0" y="0" width="${SHEET.W}" height="${SHEET.H}"/>` + itemsToSvg(pageItems(this.pr, this.A, this.cur, { parts: partOf }));
      for (const id of ['#gWire', '#gSym', '#gLbl', '#gSel', '#gOv']) {
        const el = this.$(id);
        if (el) el.innerHTML = '';
      }
      return;
    }
    this.applyLayers();
    this.$('#gPaper').innerHTML = paperSVG(this.pr, this.cur, this.opts.grid);
    const L = pageLayers(this.pr, this.A, this.cur, this.sel, this.opts.net);
    this.$('#gWire').innerHTML = L.wires;
    this.$('#gSym').innerHTML = L.syms;
    this.$('#gLbl').innerHTML = L.labels;
    this.$('#gSel').innerHTML = L.selection + this.highlightSvg();
    this.renderOverlay();
  }
  /** Hides the layers switched off for the screen (CSS classes on the drawing). */
  private applyLayers() {
    const hidden = hiddenLayers(this.pr);
    for (const [l] of LAYERS) this.svg.classList.toggle('hide-' + l, hidden.has(l));
  }

  /** Layer manager: per layer, shown on screen and included in PDF / DXF. */
  private layersDialog() {
    const cur = this.pr.settings?.layers ?? {};
    const close = openModal(
      `<h2>${$t('Katmanlar')}</h2><p>${$t('Ekranda görünmeyen katmanlar çizimde saklanır; çıktıya dahil edilmeyen katmanlar PDF ve DXF dosyalarına girmez. Ayarlar projeyle kaydedilir.')}</p>
      <div class="tblw"><table class="t"><thead><tr><th>${$t('Katman')}</th><th>${$t('Ekranda')}</th><th>${$t('Çıktıda')}</th></tr></thead><tbody>
      ${LAYERS.map(([l, name]) => `<tr><td>${esc($t(name))}</td><td><input type="checkbox" data-lh="${l}"${cur[l]?.hide ? '' : ' checked'}></td><td><input type="checkbox" data-lp="${l}"${cur[l]?.noprint ? '' : ' checked'}></td></tr>`).join('')}
      </tbody></table></div>
      <div class="acts"><button class="btn" data-m="all">${$t('Tümünü aç')}</button><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('Uygula')}</button></div>`,
      (a, dlg) => {
        if (a === 'all') return void dlg.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach((c) => (c.checked = true));
        if (a !== 'ok') return;
        const layers: Record<string, { hide?: boolean; noprint?: boolean }> = {};
        for (const [l] of LAYERS) {
          const hide = !qs<HTMLInputElement>(dlg, `[data-lh="${l}"]`).checked, noprint = !qs<HTMLInputElement>(dlg, `[data-lp="${l}"]`).checked;
          if (hide || noprint) layers[l] = { ...(hide ? { hide } : {}), ...(noprint ? { noprint } : {}) };
        }
        close();
        this.mutate(() => {
          const st = { ...DEFAULT_SETTINGS, ...this.pr.settings };
          if (Object.keys(layers).length) st.layers = layers;
          else delete st.layers;
          this.pr.settings = st;
        });
      },
    );
  }

  private renderOverlay() {
    if (this.page.kind === 'layout') return this.renderLayoutOverlay();
    let o = '';
    const w = this.mouse;
    if (this.tool === 'macro' && this.macroGhost)
      o += `<g class="ghostm" transform="translate(${snapV(w[0])} ${snapV(w[1])})">${this.macroGhost}</g>`;
    if (this.tool === 'place' && this.placeType) {
      const L = LIB[this.placeType];
      o += `<g class="sym ghost" transform="translate(${snapV(w[0])} ${snapV(w[1])}) rotate(${this.placeRot * 90})">${L.role === 'text' ? `<text x="0" y="0" font-size="3.5">${$t("Metin")}</text>` : svgFor(this.placeType, this.std)}</g>`;
    }
    if (this.tool === 'wire') {
      o += openPinMarkers(this.A, this.page.syms);
      const t = this.wireSnap(w);
      if (this.draft.length) {
        const last = this.draft[this.draft.length - 1];
        o += `<polyline class="draft" points="${[...this.draft, ...route(last, t.p)].map((p) => p.join(',')).join(' ')}"/>`;
      }
      o +=
        t.kind === 'grid'
          ? `<path class="snap" d="M${t.p[0] - 1.2} ${t.p[1]}h2.4M${t.p[0]} ${t.p[1] - 1.2}v2.4"/>`
          : `<circle class="snap" cx="${t.p[0]}" cy="${t.p[1]}" r="1.4"/>`;
    }
    const d = this.drag;
    if (d?.k === 'box')
      o += `<rect class="band" x="${Math.min(d.a[0], d.b[0])}" y="${Math.min(d.a[1], d.b[1])}" width="${Math.abs(d.a[0] - d.b[0])}" height="${Math.abs(d.a[1] - d.b[1])}"/>`;
    this.$('#gOv').innerHTML = o;
  }
  private renderTools() {
    const on = (a: string, v: boolean) => this.el.querySelector(`[data-a="${a}"]`)?.classList.toggle('on', v);
    on('tsel', this.tool === 'select');
    on('twire', this.tool === 'wire');
    on('grid', this.opts.grid);
    on('net', this.opts.net);
    on('msch', this.mode === 'sch');
    on('mrep', this.mode === 'rep');
    on('mnav', this.mode === 'nav');
    (this.$('[data-a="undo"]') as HTMLButtonElement).disabled = !this.undoS.length;
    (this.$('[data-a="redo"]') as HTMLButtonElement).disabled = !this.redoS.length;
    this.svg.style.cursor = this.tool === 'select' ? 'default' : 'crosshair';
  }
  private renderStatus() {
    // a touch screen: one finger on empty drawing pans, two fingers zoom
    const touch = this.tool === 'select' && typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    this.$('#stHint').textContent = touch ? $t('Dokun: seç · Sürükle: kaydır · Basılı tutup sürükle: taşı · İki parmak: yakınlaştır') : $t(HINT[this.tool]);
    const cs = runChecks(this.A, partOf), e = cs.filter((c) => c.sev === 'err').length, w = cs.length - e;
    const hl = this.highlight ? `<span class="pill warn">${$t("{0} ile farklar işaretli", esc(this.highlight.label))}</span> <button class="btn" data-a="hloff" style="padding:1px 6px">${$t("Kapat")}</button> · ` : '';
    this.$('#stChk').innerHTML = hl + $t("Kontrol: {0} {1}{2}", e ? `<span class="pill err">${$t("{0} hata", e)}</span>` : '', w ? `<span class="pill warn">${$t("{0} uyarı", w)}</span>` : '', cs.length ? '' : `<span class="pill ok">${$t("temiz")}</span>`);
  }
  private renderPages() {
    this.$('#pages').innerHTML = this.pr.pages
      .map((p, i) => `<div class="pg${i === this.cur ? ' on' : ''}${this.live.locks[p.id] ? ' locked' : ''}" data-pg="${i}" tabindex="0"${this.live.locks[p.id] ? ` title="${esc($t('Kilitli: {0} düzenliyor', this.live.locks[p.id]))}"` : ''}><span class="no">${i + 1}</span>${this.live.locks[p.id] ? '<span class="lk" aria-hidden="true">🔒</span>' : ''}${this.live.users.filter((x) => x.page === p.id).map((x) => `<span class="av sm" title="${esc(x.name)}">${esc(x.name.slice(0, 1).toUpperCase())}</span>`).join('')}<span class="ds">${p.kind === 'layout' ? `<span class="pkind">${p.layout?.door ? 'KAPAK' : 'PANO'}</span> ` : p.kind === 'single' ? `<span class="pkind">${$t("TEK HAT")}</span> ` : p.kind === 'report' ? `<span class="pkind">${$t('RAPOR')}</span> ` : ''}${esc(p.desc || $t("Adsız sayfa"))}</span></div>`)
      .join('');
    if (this.page && this.page.id !== this.livePage) {
      this.livePage = this.page.id;
      this.scheduleLive(this.livePage ? 250 : 0);
    }
    this.renderLive();
    (this.$('[data-a="pdel"]') as HTMLButtonElement).disabled = this.pr.pages.length < 2;
    (this.$('[data-a="pup"]') as HTMLButtonElement).disabled = this.cur === 0;
    (this.$('[data-a="pdn"]') as HTMLButtonElement).disabled = this.cur === this.pr.pages.length - 1;
  }
  private renderLib() {
    if (this.page.kind === 'layout') return this.renderLayoutLib();
    if (this.page.kind === 'report') return void (this.$('#lib').innerHTML = `<p class="note" style="padding:10px">${$t('Rapor sayfası: içeriği projeden üretilir, düzenlenmez.')}</p>`);
    const q = (this.$('#libq') as HTMLInputElement).value.trim().toLocaleLowerCase('tr');
    // symbols made by importing a drawing belong to their project: only this project's own, named by what they are
    const imported = new Set((this.pr.symbols ?? []).map((c) => c.key));
    const IMPORTED = '@imported';
    const item = (k: string) => {
      const L = LIB[k], own = L.cat === 'EPLAN';
      return `<button class="li${this.tool === 'place' && this.placeType === k ? ' on' : ''}" data-lib="${k}" title="${esc(symbolName(k))}">${symbolPreview(k, 46, 34, this.std)}<span>${esc(own ? symbolName(k) : $t(L.name))}</span>${L.prefix && !own ? `<span class="pf">-${prefixFor(k, this.std)}</span>` : ''}</button>`;
    };
    const groups = new Map<string, string[]>();
    for (const k of LIB_ORDER) {
      const L = LIB[k], own = L.cat === 'EPLAN';
      if (own && !imported.has(k)) continue;
      if (q && !`${own ? symbolName(k) : `${$t(L.name)} ${L.name} ${$t(L.cat)} ${L.cat}`} ${L.prefix}`.toLocaleLowerCase('tr').includes(q)) continue;
      const cat = own ? IMPORTED : L.cat;
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(k);
    }
    // they come last, closed until opened
    const mine = groups.get(IMPORTED);
    if (mine) {
      groups.delete(IMPORTED);
      groups.set(IMPORTED, mine);
    }
    const closed = new Set(this.ui.closed ?? []);
    const head = (cat: string, label: string, n: number, open: boolean) =>
      `<button class="cat cath" data-libcat="${esc(cat)}" aria-expanded="${open}"><span class="chev">${open ? '▾' : '▸'}</span>${esc(label)}<span class="cnt">${n}</span></button>`;
    let o = '';
    const recent = (this.ui.recent ?? []).filter((k) => LIB[k] && LIB_ORDER.includes(k));
    if (!q && recent.length) {
      const open = !closed.has('@recent');
      o += head('@recent', $t('Son kullanılanlar'), recent.length, open) + (open ? `<div class="lib">${recent.map(item).join('')}</div>` : '');
    }
    for (const [cat, keys] of groups) {
      const open = !!q || (cat === IMPORTED ? closed.has(IMPORTED + ':open') : !closed.has(cat));
      o += head(cat, cat === IMPORTED ? $t('Bu projeye içe aktarılan semboller') : $t(cat), keys.length, open) + (open ? `<div class="lib">${keys.map(item).join('')}</div>` : '');
    }
    this.$('#lib').innerHTML = `<button class="btn" data-a="bbox" style="width:100%;justify-content:center;margin:0 0 8px" title="${$t("Kenarlarında bağlantı noktaları olan dikdörtgen cihaz sembolü")}">${$t("+ Kara kutu oluştur…")}</button>` + (groups.size ? o : `<p class="note">${$t("Eşleşen sembol yok.")}</p>`);
  }

  private selItems() {
    const S: Sym[] = [], W: Wire[] = [];
    for (const k of this.sel) {
      const id = k.slice(2);
      if (k[0] === 's') {
        const s = this.page.syms.find((x) => x.id === id);
        if (s) S.push(s);
      } else {
        const w = this.page.wires.find((x) => x.id === id);
        if (w) W.push(w);
      }
    }
    return { S, W };
  }

  /** drawing standard of the open project */
  private get std() {
    return standardOf(this.pr);
  }

  private renderProps() {
    if (this.page.kind === 'layout') return this.renderLayoutProps();
    if (this.page.kind === 'report') {
      const tab = this.page.report?.tab ?? '';
      const n = this.pr.pages.filter((p) => p.kind === 'report' && p.report?.tab === tab).length;
      this.$('#props').innerHTML = `<div class="sec"><h3>${$t('Rapor sayfası')}</h3>
        <dl class="kv"><dt>${$t('Rapor')}</dt><dd>${esc(reportPageName(tab))}</dd><dt>${$t('Sayfa')}</dt><dd>${(this.page.report?.part ?? 0) + 1} / ${n}</dd></dl>
        <p class="note">${$t('Tablo her açılışta ve çıktıda projeden yeniden üretilir. Satırlar sayfalara sığmazsa ya da azalırsa sayfa sayısını güncelleyin.')}</p>
        <button class="btn" data-act="repsync" style="width:100%;justify-content:center">${$t('Sayfa sayısını güncelle')}</button></div>`;
      return;
    }
    const { S, W } = this.selItems(), A = this.A, pr = this.pr, st = { ...DEFAULT_SETTINGS, ...pr.settings };
    let o = '';
    if (S.length + W.length === 0) {
      if (this.page.kind === 'single')
        o += `<div class="sec"><h3>${$t('Tek hat şeması')}</h3><button class="btn pri" data-act="slboard" style="width:100%;justify-content:center">${$t('Bu tek hattan pano üret…')}</button>
          <p class="note">${$t('Baranın üstündeki kol ana şalter (iki kol: jeneratör transferi), alttaki kollar çıkışlardır. Şalter ve kaçak akım sembollerine akım, kutup ve kablo boyu girin.')}</p></div>`;
      o += `<div class="sec"><h3>${$t("Proje")}</h3>${fld($t("Proje adı"), inp('pr.name', pr.name))}${fld($t("Müşteri"), inp('pr.customer', pr.customer))}
        <div class="row2">${fld('Fonksiyon (=)', inp('pr.fn', pr.fn))}${fld('Konum (+)', inp('pr.loc', pr.loc))}</div>
        <div class="row2">${fld($t('Kurulum yeri (++)'), inp('pr.site', pr.site ?? '', 'tag', $t('ör. BİNA2')))}${fld($t('Kullanıcı tanımlı (#)'), inp('pr.ud', pr.ud ?? '', 'tag'))}</div>
        <div class="row2">${fld($t("Çizen"), inp('pr.author', pr.author))}${fld('Tarih', inp('pr.date', pr.date))}</div></div>
        <div class="sec"><h3>${$t('Antet')}</h3>
        <div class="frlogo">${pr.frame?.logo ? `<img src="${pr.frame.logo}" alt="">` : `<span class="note">${$t('Logo yok')}</span>`}
          <div class="btnrow"><button class="btn" data-act="frlogo">${$t('Logo seç…')}</button>${pr.frame?.logo ? `<button class="btn" data-act="frlogodel">${$t('Kaldır')}</button>` : ''}</div></div>
        ${fld($t('Firma adı'), inp('fr.company', pr.frame?.company ?? '', '', 'EnerCAD'))}${fld($t('Adres / telefon'), inp('fr.address', pr.frame?.address ?? ''))}
        ${fld($t('Çizim no'), inp('fr.drawingNo', pr.frame?.drawingNo ?? '', 'tag'))}
        ${[0, 1].map((i) => `<div class="row2">${fld($t('Ek alan {0}', i + 1), inp('fr.label' + i, pr.frame?.fields?.[i]?.label ?? '', '', i ? $t('ör. Onay') : $t('ör. Kontrol')))}${fld($t('Değer'), inp('fr.value' + i, pr.frame?.fields?.[i]?.value ?? ''))}</div>`).join('')}
        <div class="btnrow"><button class="btn" data-act="frload" title="${$t('Firmanın antet şablonunu bu projeye uygula')}">${$t('Firma şablonunu uygula')}</button><button class="btn" data-act="frsave" title="${$t('Yeni projeler bu antetle açılır (firma yöneticisi)')}">${$t('Firma şablonu yap')}</button></div>
        <input type="file" id="frfile" accept="image/png,image/jpeg" hidden></div>
        <div class="sec"><h3>${$t("Çizim standardı")}</h3>${fld($t("Semboller ve cihaz etiketleri"), sel('st.standard', st.standard ?? 'iec', [['iec', $t("IEC 60617 / IEC 81346 (Avrupa)")], ['nfpa', $t("NFPA 79 / JIC (Kuzey Amerika)")]]))}
        <p class="note">${$t("NFPA seçildiğinde kontak, bobin, buton, lamba, şalter ve termik sembolleri NFPA çizimiyle gösterilir; yeni cihazlar CR, M, PB, LT, OL, CB gibi NFPA etiketleri alır. Mevcut etiketler değişmez, bağlantılar aynı kalır.")}</p></div>
        <div class="sec"><h3>${$t("Tel numaralandırma")}</h3>${fld($t("Otomatik numara biçimi"), sel('st.wireNo', st.wireNo, [['seq', $t("Sıralı (1, 2, 3…)")], ['page', $t("Sayfaya göre (101, 102, 201…)")], ['pagecol', $t("Sayfa + sütun (204.1, 204.2…)")]]))}
        <label class="chkline"><input type="checkbox" data-f="st.wireNoPot" id="f_st_wireNoPot"${st.wireNoPot ? ' checked' : ''}> ${$t("Potansiyel adlarını numara olarak kullan (L1, N, PE, +24V)")}</label>
        <p class="note">${$t("Elle verilen ya da sabitlenen numaralar otomatik numaranın önüne geçer. Sabitlemek, sonradan yapılan değişikliklerde numaraların kaymasını önler.")}</p>
        <div class="btnrow"><button class="btn" data-act="freeze">${$t("Numaraları sabitle")}</button><button class="btn" data-act="unfreeze">${$t("Sabit numaraları kaldır")}</button></div></div>
        <div class="sec"><h3>${$t("Sayfa {0}", this.cur + 1)}</h3>${fld($t("Sayfa açıklaması"), inp('pg.desc', this.page.desc))}
        <div class="row2">${fld('Fonksiyon (=)', inp('pg.fn', this.page.fn ?? '', 'tag', pr.fn))}${fld('Konum (+)', inp('pg.loc', this.page.loc ?? '', 'tag', pr.loc))}</div>
        <div class="row2">${fld($t('Kurulum yeri (++)'), inp('pg.site', this.page.site ?? '', 'tag', pr.site ?? ''))}${fld($t('Kullanıcı tanımlı (#)'), inp('pg.ud', this.page.ud ?? '', 'tag', pr.ud ?? ''))}</div>
        <dl class="kv"><dt>${$t("Semboller")}</dt><dd>${this.page.syms.length}</dd><dt>${$t("Bağlantılar")}</dt><dd>${this.page.wires.length}</dd></dl>
        <p class="note">${$t("Kütüphaneden bir sembol seçip sayfaya tıklayarak yerleştirin. Bir nesneye tıklayınca özellikleri burada açılır.")}</p></div>`;
    } else if (S.length + W.length > 1) {
      o += `<div class="sec"><h3>${$t("Çoklu seçim")}</h3><button class="btn" data-act="savemacro" style="margin:0 0 10px">${$t("Makro olarak kaydet")}</button><dl class="kv"><dt>${$t("Sembol")}</dt><dd>${S.length}</dd><dt>${$t("Bağlantı")}</dt><dd>${W.length}</dd></dl>
        ${W.length ? `<div class="row2" style="margin-top:10px">${fld($t("Kesit mm²"), sel('w.cs', '', [['', $t("— değiştirme —")], ...WIRE_SECTIONS.map((c): [string, string] => [c, c])]))}${fld('Renk', sel('w.col', '', [['', $t("— değiştirme —")], ...WIRE_COLOURS.slice(1)]))}</div>` : ''}
        <div class="btnrow" style="margin-top:10px"><button class="btn" data-act="rot">${$t("Döndür")}</button><button class="btn" data-act="dup">${$t("Çoğalt")}</button><button class="btn danger" data-act="del">${$t("Sil")}</button></div></div>`;
    } else if (W.length) {
      const w = W[0], r = A.uf.find(wireNode(w.id)), p = potentialOf(A, wireNode(w.id));
      const ends: string[] = [];
      for (const it of A.all)
        LIB[it.s.type].pins.forEach((_, i) => {
          if (A.uf.find(pinNode(it.s, i)) === r && !['brk', 'pot'].includes(LIB[it.s.type].role)) ends.push(`${pinName(A, it.s, i)}  /${loc(it)}`);
        });
      o += `<div class="sec"><h3>${$t("Bağlantı")}</h3><div class="row2">${fld($t("Kesit mm²"), sel('w.cs', w.cs, [['', '—'], ...WIRE_SECTIONS.map((c): [string, string] => [c, c])]))}${fld('Renk', sel('w.col', w.col, WIRE_COLOURS))}</div>
        ${fld($t("Tel no (elle)"), inp('w.no', w.no ?? '', 'tag', 'otomatik: ' + (A.netLabel.get(r) ?? '')))}
        <dl class="kv"><dt>${$t("Tel no")}</dt><dd>${esc(A.netLabel.get(r))}</dd>${A.wireCore.get(w.id) ? `<dt>${$t("Kablo")}</dt><dd>${$t("-{0} damar {1}", esc(A.wireCore.get(w.id)!.cable.tag), esc(A.wireCore.get(w.id)!.core))}</dd>` : ''}<dt>${$t("Potansiyel")}</dt><dd>${p ? esc(`${p.name} ${p.ptype ? `(${p.ptype})` : ''}`) : '—'}</dd><dt>${$t("Köşe")}</dt><dd>${w.pts.length}</dd></dl>
        <h3 style="margin-top:12px">${$t("Bu telde bağlı uçlar")}</h3><ul class="conn">${ends.map((e) => `<li>${esc(e)}</li>`).join('') || `<li>${$t("Uç yok")}</li>`}</ul>
        <div class="btnrow" style="margin-top:10px"><button class="btn danger" data-act="del">${$t("Bağlantıyı sil")}</button></div></div>`;
    } else {
      const s = S[0], L = LIB[s.type], it: Placed = { s, pi: this.cur };
      const tags = [...new Set(pr.pages.flatMap((p) => p.syms.map((x) => x.tag).filter(Boolean)))].sort();
      const dl = `<datalist id="tags">${tags.map((t) => `<option value="${esc(t)}">`).join('')}</datalist>`;
      const tagInp = (label: string, ph = '') => fld(label, inp('s.tag', s.tag, 'tag', ph).replace('<input', '<input list="tags"') + dl);
      o += `<div class="sec"><div class="symhead">${symbolPreview(s.type, 52, 40, this.std)}<div><b>${esc(symbolName(s.type))}</b><span>${$t("Konum /{0} · X {1} Y {2}", loc(it), s.x, s.y)}</span></div></div>`;
      if (isCustomKey(s.type) && customSymbol(s.type)) {
        // a user or imported symbol: the library symbol it stands for (its drawing stays)
        const same = Object.values(LIB).filter((d) => d.role === L.role && !isCustomKey(d.key) && !d.fluid && !d.key.startsWith('sl_'));
        const opts = [['', $t('— eşleşmemiş —')], ...same.map((d) => [d.key, `${$t(d.name)} (${d.key})`])];
        o += fld($t('Kütüphane karşılığı'), `<select data-f="symfn">${opts.map(([k, n]) => `<option value="${esc(k)}"${(L.fn ?? '') === k ? ' selected' : ''}>${esc(n)}</option>`).join('')}</select>`) +
          `<p class="note" style="margin-top:-6px">${$t('Çizim aynı kalır; parça listesi, kontroller, pano yerleşimi ve 3D bu sembole göre davranır. Aynı çizimin bütün kopyalarına uygulanır.')}</p>`;
      }
      if (L.role === 'main' || L.role === 'plc') {
        o += tagInp($t("Cihaz etiketi"), $t("ör. ") + L.prefix + '1') + fld($t("Açıklama"), inp('s.desc', s.desc, '', $t("ör. Pompa motoru")));
        if (L.role === 'plc') o += fld('Adres', inp('p.addr', s.props.addr, 'tag', $t("ör. I0.0, Q1.3, IW64"))) + fld($t('Sembol'), inp('p.sym', s.props.sym, 'tag', $t('PLC programındaki adı'))) + `<button class="btn" data-act="readdrcard" style="margin:-3px 0 10px">${$t("Bu kartın adreslerini yeniden sırala")}</button>`;
        if (s.type === 'pid_inst') o += fld($t('ISA harfleri'), inp('p.isa', s.props.isa, 'tag', $t('ör. PT, TIC, LSH')));
        const nSol = SOLENOID_VALVES[s.type] ?? 0;
        if (nSol) {
          const solFld = (f: 'sol' | 'sol2', label: string) => fld(label, inp('p.' + f, s.props[f], 'tag', $t('ör. {0}1', prefixFor('sol', this.std))).replace('<input', '<input list="tags"'));
          o += nSol > 1 ? `<div class="row2">${solFld('sol', $t('Bobin 1 (sol)'))}${solFld('sol2', $t('Bobin 2 (sağ)'))}</div>` : solFld('sol', $t('Valf bobini'));
          const keys = A.valveSols.get(s.id) ?? [];
          o += `<dl class="kv">${keys.map((k) => { const m = A.mains.get(k)?.[0]; return `<dt>${esc(A.devName.get(k) ?? k)}</dt><dd>${m ? `/${loc(m)}` : `<span class="pill warn">${$t('elektrik şemasında yok')}</span>`}</dd>`; }).join('')}</dl>`;
        }
        if (s.type === 'sol') {
          const v = A.solValves.get(A.devKey.get(s.id) ?? '') ?? [];
          o += `<dl class="kv"><dt>${$t('Valf')}</dt><dd>${v.length ? v.map((x) => `${esc(deviceName(A, x.s))} /${loc(x)}`).join(', ') : '—'}</dd></dl>`;
        }
        o += this.partField(s) + fld($t("Üretici"), inp('s.maker', s.maker));
        if (L.mirror) {
          const list = A.auxs.get(s.tag) || [];
          o += `<h3 style="margin-top:12px">${$t("Kontak görüntüsü")}</h3><ul class="conn">${list.map((x) => `<li>${esc(symbolName(x.s.type))} · ${esc((A.auxLabels.get(x.s.id) || []).join('/'))} · /${loc(x)}</li>`).join('') || `<li>${$t("Bu cihaza bağlı kontak yok")}</li>`}</ul>`;
        }
      } else if (L.role === 'aux') {
        const m = A.mains.get(s.tag)?.[0];
        o += tagInp($t("Cihaz etiketi (ana fonksiyon)")) + fld($t("Kontak numaraları"), inp('p.pins', s.props.pins, 'tag', 'otomatik: ' + (A.auxLabels.get(s.id) || []).join('/')));
        o += `<dl class="kv"><dt>${$t("Ana fonksiyon")}</dt><dd>${m ? `${esc(symbolName(m.s.type))} /${loc(m)}` : `<span class="pill err">${$t("bulunamadı")}</span>`}</dd>${m?.s.desc ? `<dt>${$t("Açıklama")}</dt><dd>${esc(m.s.desc)}</dd>` : ''}</dl>`;
      } else if (L.role === 'term') {
        o += `<div class="row2">${tagInp($t("Klemens şeridi"))}${fld($t("Klemens no"), inp('p.pinNo', s.props.pinNo, 'tag'))}</div>`;
        if (PLUG_TYPES.has(s.type)) o += fld($t('Karşı fiş (etiket)'), inp('p.mate', s.props.mate ?? '', 'tag', $t('ör. X11 — fiş planında ve kontrolde eşleşir')));
        else o += fld($t("Köprü"), inp('p.bridge', s.props.bridge, 'tag', $t("ör. a — aynı harfli klemensler köprülüdür")));
        o += this.partField(s);
      } else if (L.role === 'pot' || L.role === 'brk') {
        o += `<div class="row2">${fld(L.role === 'pot' ? $t("Potansiyel adı") : 'Ad', inp('p.name', s.props.name, 'tag'))}${fld('Tip', sel('p.ptype', s.props.ptype || '', PTYPES))}</div>`;
        if (L.role === 'brk') {
          const g = (A.brk.get(s.props.name || '') || []).filter((x) => x.s.id !== s.id);
          o += `<p class="note">${$t("Aynı adlı kesinti noktaları: {0}", g.length ? g.map((x) => '/' + loc(x)).join(', ') : 'yok')}</p>`;
        } else o += `<p class="note">${$t("Bağlı olduğu tellere adını ve rengini verir. Renk kontak ve klemenslerden geçerek devam eder.")}</p>`;
      } else if (s.type === 'cdp') {
        const w = this.page.wires.find((x) => x.pts.slice(1).some((q, k) => onSeg(s.x, s.y, x.pts[k], q)));
        o += `<div class="row2">${fld($t("Kesit mm²"), sel('p.cs', s.props.cs || '', [['', '—'], ...WIRE_SECTIONS.map((c): [string, string] => [c, c])]))}${fld($t('Renk'), sel('p.col', s.props.col || '', [['', '—'], ...WIRE_COLOURS.slice(1)]))}</div>
          ${fld($t('Tel numarası'), inp('p.wno', s.props.wno ?? '', 'tag', $t('boş: otomatik')))}
          <p class="note">${w ? $t('Bu değerler üzerinde durduğu tele verilir ve telin kendi özelliğinden önceliklidir.') : `<span class="pill warn">${$t('Bağlantı tanım noktası bir telin üzerinde değil')}</span>`}</p>`;
      } else if (L.role === 'cable') {
        const info = A.cables.get(s.id);
        o += `<div class="row2">${tagInp($t("Kablo etiketi"))}${fld('Tip', inp('p.cableType', s.props.cableType, '', $t("ör. NYY-J, LiYCY")))}</div>
          ${fld($t("Damarlar (virgülle, sırayla)"), inp('p.cores', s.props.cores ?? '1,2,3,PE', 'tag', $t("ör. 1,2,3,PE ya da BN,BK,GY,GNYE")))}
          <div class="row2">${fld($t("Kesit mm²"), sel('p.cs', s.props.cs || '', [['', '—'], ...WIRE_SECTIONS.map((c): [string, string] => [c, c])]))}${fld('Uzunluk (m)', inp('p.length', s.props.length, 'tag'))}</div>
          <label class="chkline"><input type="checkbox" data-f="p.shield"${s.props.shield === '1' ? ' checked' : ''}> ${$t('Ekranlı kablo (SH)')}</label>
          ${this.partField(s)}
          ${fld($t("Çizgi uzunluğu (mm)"), sel('p.len', String(s.props.len || 24), ['16', '24', '32', '40', '48', '64', '80', '120', '160'].map((v): [string, string] => [v, v])))}
          <h3 style="margin-top:12px">${$t("Damar ataması")}</h3><ul class="conn">${(info?.cross || []).map((c) => `<li>${$t("{0} → tel {1}", c.core ? esc(c.core) : `<span class="pill err">${$t("damar yok")}</span>`, esc(A.netLabel.get(A.uf.find(wireNode(c.wireId)))))}</li>`).join('') || `<li>${$t("Kablo çizgisini tellerin üzerinden geçirin")}</li>`}</ul>`;
      } else if (L.role === 'sl') {
        const m = A.mains.get(A.devKey.get(s.id) ?? '')?.[0];
        if (L.prefix) o += tagInp($t("Cihaz etiketi (çok hatlı şemadaki)"), $t("ör. ") + L.prefix + '1') + fld($t("Açıklama (boşsa cihazınki)"), inp('s.desc', s.desc));
        if (s.type === 'sl_cb' || s.type === 'sl_rcd')
          o += `<div class="row2">${fld($t('Akım (A)'), inp('p.amps', s.props.amps ?? '', 'tag', $t('ör. 63')))}${s.type === 'sl_cb' ? fld($t('Kutup'), sel('p.poles', s.props.poles === '1' ? '1' : '3', [['3', '3P'], ['1', '1P']])) : ''}</div>
            ${s.type === 'sl_cb' ? `<div class="row2">${fld($t('Kablo boyu (m)'), inp('p.length', s.props.length ?? '', 'tag'))}${fld($t('Ana şalter ise tipi'), sel('p.swkind', s.props.swkind ?? '', [['', '—'], ...(Object.keys(INCOMER_NAMES) as IncomerKind[]).map((k): [string, string] => [k, $t(INCOMER_NAMES[k])])]))}</div>` : ''}
            <p class="note">${$t('Bu değerlerle tek hat sayfasından kuvvet şeması, pano yerleşimi ve 3D üretilebilir (sayfada hiçbir şey seçili değilken sağdaki düğme).')}</p>`;
        o += `<dl class="kv"><dt>${$t("Çok hatlı karşılığı")}</dt><dd>${m ? `${esc(symbolName(m.s.type))} /${loc(m)}` : L.prefix ? `<span class="pill warn">${$t("bulunamadı")}</span>` : '—'}</dd></dl>
          <p class="note">${$t("Tek hat sembolleri malzeme listesine girmez; cihazın asıl sembolüne çapraz referans verir.")}</p>`;
      } else if (L.role === 'box') {
        o += fld($t("Açıklama"), inp('s.desc', s.desc, '', $t("ör. Saha, Motor 1 buat")));
        o += `<div class="row2">${fld($t("Genişlik (mm)"), inp('p.w', String(s.props.w ?? 120), 'tag'))}${fld($t("Yükseklik (mm)"), inp('p.h', String(s.props.h ?? 80), 'tag'))}</div>`;
        o += `<div class="row2">${fld('Fonksiyon (=)', inp('s.fn', s.fn ?? '', 'tag', this.page.fn || pr.fn))}${fld('Konum (+)', inp('s.loc', s.loc ?? '', 'tag', this.page.loc || pr.loc))}</div><div class="row2">${fld($t('Kurulum yeri (++)'), inp('s.site', s.site ?? '', 'tag', this.page.site || pr.site || ''))}${fld($t('Kullanıcı tanımlı (#)'), inp('s.ud', s.ud ?? '', 'tag', this.page.ud || pr.ud || ''))}</div>
          <p class="note">${$t("Kutunun içindeki cihazlar bu fonksiyon ve konumu alır (ör. +SAHA: sahadaki butonlar ve motor). Kendi tanıtıcısı olan cihazlar etkilenmez.")}</p>`;
      } else if (L.role === 'text') {
        o += fld($t('Metin'), inp('p.text', s.props.text)) + fld($t("Yazı yüksekliği (mm)"), sel('p.size', String(s.props.size || 3.5), [['2.5', '2,5'], ['3.5', '3,5'], ['5', '5'], ['7', '7']]));
      }
      if (!['pot', 'brk', 'text', 'box'].includes(L.role))
        o += `<details class="struct"${s.fn || s.loc || s.site || s.ud ? ' open' : ''}><summary>${$t("Yapı tanıtıcısı · {0}", esc(A.devKey.get(s.id) ?? ''))}</summary><div class="row2">${fld('Fonksiyon (=)', inp('s.fn', s.fn ?? '', 'tag', this.page.fn || pr.fn))}${fld('Konum (+)', inp('s.loc', s.loc ?? '', 'tag', this.page.loc || pr.loc))}</div><div class="row2">${fld($t('Kurulum yeri (++)'), inp('s.site', s.site ?? '', 'tag', this.page.site || pr.site || ''))}${fld($t('Kullanıcı tanımlı (#)'), inp('s.ud', s.ud ?? '', 'tag', this.page.ud || pr.ud || ''))}</div></details>`;
      o += `<div class="btnrow" style="margin-top:6px"><button class="btn" data-act="rot">${$t("Döndür")}</button><button class="btn" data-act="dup">${$t("Çoğalt")}</button><button class="btn danger" data-act="del">${$t("Sil")}</button></div></div>`;
    }
    this.$('#props').innerHTML = o;
  }

  private renderReports() {
    const R = buildReport(this.pr, this.A, this.repTab, partOf);
    let o = `<div class="reptabs">${REPORT_TABS.map(([k, n]) => `<button class="btn${this.repTab === k ? ' on' : ''}" data-rt="${k}">${$t(n)}</button>`).join('')}
      <span class="spacer"></span>${this.repTab === 'chk' && this.aiOn && !this.local ? `<button class="btn" data-act="aireview" title="${$t('Proje yapay zekaya gönderilir, bir mühendis gözüyle değerlendirilir; hiçbir şey değiştirilmez')}">${$t('Yapay zeka ile gözden geçir')}</button>` : ''}${this.repTab === 'plc' ? `<button class="btn" data-act="readdr">${$t("Adresleri yeniden sırala")}</button><button class="btn" data-act="plcaml" title="${$t('PLC donanımı ve G/Ç: CPU, kartlar, kanallar ve etiketler; TIA Portal ve EPLAN okur (AutomationML AR APC)')}">${$t('AutomationML')}</button><button class="btn" data-act="plcimp" title="${$t('TIA Portal etiket tablosu, CODESYS değişken listesi ya da adres ve sembol sütunlu Excel/CSV')}">${$t('Atama listesi içe aktar')}</button><button class="btn" data-a="tia">${$t("TIA Portal tablosu")}</button>` : ''}<button class="btn" data-act="marks" title="${$t('Tel, klemens, cihaz ve kablo etiketleri; etiket yazıcısı programına aktarmak için')}">${$t('Etiket listesi')}</button><button class="btn" data-act="csv">${$t("CSV indir")}</button></div>
      <h2>${esc(R.title)}</h2><p class="sub">${esc(R.sub)} · ${esc(this.pr.name)}</p>`;
    if (!R.rows.length) o += `<div class="tblw"><div class="empty">${this.repTab === 'chk' ? $t("Temiz: kontrol edilecek bir sorun yok.") : $t("Bu rapor için henüz veri yok.")}</div></div>`;
    else {
      // check messages: a button to ignore each one (or take an ignored one back)
      const chk = this.repTab === 'chk' && !this.readOnly;
      o += `<div class="tblw"><table class="t"><thead><tr>${R.cols.map((c) => `<th>${c}</th>`).join('')}${chk ? '<th></th>' : ''}</tr></thead><tbody>`;
      for (const r of R.rows) {
        if (r.group) {
          o += `<tr class="grp"><td colspan="${R.cols.length + (chk ? 1 : 0)}">${esc(r.cells[0])}</td></tr>`;
          continue;
        }
        const go = r.target ? ` data-go="${r.target.pi}|${r.target.s.id}"` : '';
        const btn = chk && r.key ? `<td><button class="btn" data-ign="${esc(r.key)}"${r.ignored ? ' data-back="1"' : ''}>${r.ignored ? $t('Geri al') : $t('Yok say')}</button></td>` : '';
        o += `<tr${go}${r.ignored ? ' class="dim"' : ''}>${r.cells.map((c, i) => `<td class="${R.mono.includes(i) ? 'm' : ''}">${r.sev && i === 0 ? `<span class="pill ${r.sev}">${esc(c)}</span>` : esc(c)}</td>`).join('')}${btn}</tr>`;
      }
      o += '</tbody></table></div>';
    }
    this.$('#rep').innerHTML = o;
  }

  /* ================= panel layout ================= */
  private get lay(): PanelLayout {
    return this.page.layout!;
  }

  /** Object behind a selection key "L:i|r|d:id" with its plate rectangle. */
  private lobj(key: string, devs = layoutDevices(this.A, partOf)) {
    const [, t, id] = key.split(':'), l = this.lay;
    if (t === 'i') {
      const o = l.items.find((x) => x.id === id);
      const d = o && devs.get(o.dev);
      return o ? { t, o, r: [o.x, o.y, d?.w ?? 20, d?.h ?? 20] as const, d } : null;
    }
    if (t === 'r') {
      const o = l.rails.find((x) => x.id === id);
      return o ? { t, o, r: [o.x, o.y - RAIL_H / 2, o.len, RAIL_H] as const, d: undefined } : null;
    }
    const o = l.ducts.find((x) => x.id === id);
    return o ? { t, o, r: [o.x, o.y, o.w, o.h] as const, d: undefined } : null;
  }

  private sheetRect(r: readonly [number, number, number, number], cls: string, extra = '') {
    const [a, b] = toSheet(this.lay, r[0], r[1]), k = 1 / this.lay.scale;
    return `<rect class="${cls}" ${extra} x="${a}" y="${b}" width="${r[2] * k}" height="${r[3] * k}"/>`;
  }

  private renderLayoutPage() {
    const pg = this.page, l = this.lay, devs = layoutDevices(this.A, partOf);
    this.$('#gPaper').innerHTML = `<rect class="paper" x="0" y="0" width="${SHEET.W}" height="${SHEET.H}"/>` + itemsToSvg(frameItems(this.pr, { no: String(this.cur + 1), desc: pg.desc, total: String(this.pr.pages.length), pi: this.cur }));
    this.$('#gWire').innerHTML = '';
    let hits = '';
    for (const d of l.ducts) hits += this.sheetRect([d.x, d.y, d.w, d.h], 'lhit', `data-lid="L:d:${d.id}"`);
    for (const r of l.rails) hits += this.sheetRect([r.x, r.y - RAIL_H / 2, r.len, RAIL_H], 'lhit', `data-lid="L:r:${r.id}"`);
    for (const i of l.items) {
      const d = devs.get(i.dev);
      hits += this.sheetRect([i.x, i.y, d?.w ?? 20, d?.h ?? 20], 'lhit', `data-lid="L:i:${i.id}"`);
    }
    this.$('#gSym').innerHTML = itemsToSvg(layoutItems(this.pr, this.A, this.cur, partOf, frontOf)) + hits;
    // fetch front views of the placed parts once; redraw when new ones arrive
    const pns = l.items.map((i) => devs.get(i.dev)?.pn ?? '');
    void ensureFronts(pns).then((got) => got && this.page.kind === 'layout' && this.renderPage());
    this.$('#gLbl').innerHTML = '';
    let sel = '';
    for (const k of this.sel) {
      const o = this.lobj(k, devs);
      if (o) sel += this.sheetRect([o.r[0] - 2 * l.scale, o.r[1] - 2 * l.scale, o.r[2] + 4 * l.scale, o.r[3] + 4 * l.scale], 'selbox');
    }
    this.$('#gSel').innerHTML = sel;
    this.renderOverlay();
  }

  private renderLayoutOverlay() {
    const l = this.lay, p = toPlate(l, this.mouse[0], this.mouse[1]);
    let o = '';
    if (this.tool === 'ldev' && this.placeDev) {
      const d = layoutDevices(this.A, partOf).get(this.placeDev);
      if (d) {
        const [x, y] = snapToRail(l, p[0] - d.w / 2, p[1] - d.h / 2, d.h);
        o += this.sheetRect([x, y, d.w, d.h], 'band');
        const [cx, cy] = toSheet(l, x + d.w / 2, y + d.h / 2);
        o += `<text x="${cx}" y="${cy + 1}" font-size="2.6" text-anchor="middle" fill="var(--accent)" font-family="var(--mono)">${esc(d.name)}</text>`;
      }
    }
    const dr = this.drag;
    if (dr?.k === 'ldraw') {
      const [a, b] = [dr.a, dr.b];
      if (dr.kind === 'rail') o += this.sheetRect([Math.min(a[0], b[0]), a[1] - RAIL_H / 2, Math.abs(b[0] - a[0]), RAIL_H], 'band');
      else o += this.sheetRect([Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1])], 'band');
      const len = dr.kind === 'rail' ? `${Math.abs(b[0] - a[0])} mm` : `${Math.abs(b[0] - a[0])} × ${Math.abs(b[1] - a[1])} mm`;
      const [tx, ty] = toSheet(l, b[0], b[1]);
      o += `<text x="${tx + 2}" y="${ty - 2}" font-size="2.6" fill="var(--accent)" font-family="var(--mono)">${len}</text>`;
    }
    if (this.tool !== 'select') o += `<path class="snap" d="M${this.mouse[0] - 1.2} ${this.mouse[1]}h2.4M${this.mouse[0]} ${this.mouse[1] - 1.2}v2.4"/>`;
    this.$('#gOv').innerHTML = o;
  }

  private renderLayoutLib() {
    if (this.lay.inside) {
      // the inside view follows the front view; nothing is placed on it
      this.$('#lib').innerHTML = `<button class="btn pri" data-act="view3d" style="width:100%;justify-content:center;margin:0 0 8px">${$t('3D görünüm')}</button><p class="note">${$t("Kapak iç görünüşü: ön görünüşteki cihazlar arkadan, aynalanmış olarak çizilir. Cihazları ön görünüşte yerleştirin.")}</p>`;
      return;
    }
    const q = (this.$('#libq') as HTMLInputElement).value.trim().toLocaleLowerCase('tr');
    const door = !!this.lay.door;
    const devs = [...layoutDevices(this.A, partOf).values()]
      .filter((d) => !door || isDoorDevice(this.A, d.key))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr', { numeric: true }));
    const where = new Map<string, [number, string]>();
    this.pr.pages.forEach((p, pi) => p.layout?.items.forEach((i) => where.set(i.dev, [pi, i.id])));
    const match = (d: (typeof devs)[number]) => !q || `${d.name} ${d.desc} ${d.pn}`.toLocaleLowerCase('tr').includes(q);
    const size = (d: (typeof devs)[number]) => `${d.w}×${d.h}${d.sized ? '' : ' ~'}`;
    const open = devs.filter((d) => !where.has(d.key) && match(d)), done = devs.filter((d) => where.has(d.key) && match(d));
    this.$('#lib').innerHTML = `<button class="btn pri" data-act="view3d" style="width:100%;justify-content:center;margin:0 0 8px">${$t('3D görünüm')}</button>${door ? `<p class="note" style="margin:0 0 8px">${$t("Kapak: buton, lamba, seçici, acil stop ve ölçü aletleri, kesim ölçüleriyle (Ø22,5 delik, DIN kare kesim ya da üretici verisi) ve delik merkezlerinin koordinatlarıyla çizilir.")}</p>` : `<div class="btnrow" style="margin-bottom:8px"><button class="btn${this.tool === 'lrail' ? ' on' : ''}" data-a="lrail">${$t("DIN rayı çiz")}</button><button class="btn${this.tool === 'lduct' ? ' on' : ''}" data-a="lduct">${$t("Kablo kanalı çiz")}</button></div>`}
      <div class="cat">${$t("Yerleştirilecek cihazlar · {0}", open.length)}</div>
      <div class="navtree">${open.map((d) => `<button class="navrow${this.placeDev === d.key ? ' on' : ''}" data-ldev="${esc(d.key)}" title="${esc(d.pn || $t("parça yok"))}"><span><b class="m" style="color:var(--fg)">${esc(d.name)}</b> <span class="note">${esc(d.desc)}</span></span><span class="m">${size(d)}</span></button>`).join('') || `<p class="note">${$t("Tüm cihazlar yerleştirildi.")}</p>`}</div>
      <div class="cat">${$t("Yerleştirilmiş · {0}", done.length)}</div>
      <div class="navtree">${done.map((d) => `<button class="navrow" data-lgo="${where.get(d.key)![0]}|${where.get(d.key)![1]}"><span><b class="m" style="color:var(--fg)">${esc(d.name)}</b></span><span class="m">s. ${where.get(d.key)![0] + 1}</span></button>`).join('')}</div>
      ${door ? '' : `<p class="note">${$t("\"~\" ölçüsü parça veritabanında olmayan cihazlar; varsayılan ölçüyle çizilir. Ölçüleri Parçalar sayfasından girebilirsiniz.")}</p>`}`;
  }

  private renderLayoutProps() {
    const l = this.lay, devs = layoutDevices(this.A, partOf);
    const objs = [...this.sel].map((k) => this.lobj(k, devs)).filter((x): x is NonNullable<typeof x> => !!x);
    let o = '';
    if (!objs.length) {
      const issues = runChecks(this.A, partOf).filter((c) => c.pi === this.cur);
      const pl = l.door ? $t('Kapak') : $t('Plaka');
      o = `<div class="sec"><h3>${$t("{0} · sayfa {1}", l.door ? $t("Pano kapağı") : $t("Pano yerleşimi"), this.cur + 1)}</h3>${fld($t("Sayfa açıklaması"), inp('pg.desc', this.page.desc))}
        <div class="row2">${fld(pl + $t(" genişliği (mm)"), inp('ly.w', l.w, 'tag'))}${fld(pl + $t(" yüksekliği (mm)"), inp('ly.h', l.h, 'tag'))}</div>
        ${l.door ? '' : `<div class="row2">${fld($t('Pano derinliği (mm)'), inp('ly.depth', cabinetDepth(l), 'tag'))}<div class="btnrow" style="align-self:end;margin-bottom:9px"><button class="btn" data-act="view3d">${$t('3D görünüm')}</button></div></div>`}
        <div class="row2">${fld($t("Ölçek"), sel('ly.scale', String(l.scale), LAYOUT_SCALES.map((s): [string, string] => [String(s), '1:' + String(s).replace('.', ',')])))}<div class="btnrow" style="align-self:end;margin-bottom:9px"><button class="btn" data-act="lfit">${$t("Sığdır")}</button></div></div>
        <dl class="kv"><dt>${$t("Cihaz")}</dt><dd>${l.items.length}</dd>${l.door ? `<dt>${$t("Kesim")}</dt><dd>${$t("Ø22,5 mm")}</dd>` : `<dt>${$t("DIN rayı")}</dt><dd>${$t("{0} · {1} mm", l.rails.length, l.rails.reduce((a, r) => a + r.len, 0))}</dd><dt>${$t("Kablo kanalı")}</dt><dd>${l.ducts.length}</dd>`}</dl>
        <button class="btn pri" data-act="autolayout" style="width:100%;justify-content:center;margin:10px 0 4px">${$t("Otomatik yerleştir…")}</button>
        <p class="note">${l.door ? $t("Ya da soldaki listeden bir cihaza tıklayıp kapakta yerini seçin. Delik ölçüleri Raporlar → Kapak delik listesinde.") : $t("Ya da soldan DIN raylarını ve kablo kanallarını çizip listeden bir cihaza tıklayarak elle yerleştirin. Cihazlar yakınındaki raya oturur.")}</p>
        ${issues.length ? `<h3 style="margin-top:12px">${$t("Bu sayfadaki sorunlar")}</h3><ul class="conn">${issues.map((c) => `<li><span class="pill ${c.sev}">${c.sev === 'err' ? 'Hata' : $t("Uyarı")}</span> ${esc(c.msg)}</li>`).join('')}</ul>` : ''}</div>`;
    } else if (objs.length > 1) {
      o = `<div class="sec"><h3>${$t("Çoklu seçim")}</h3><dl class="kv"><dt>${$t("Nesne")}</dt><dd>${objs.length}</dd></dl><div class="btnrow" style="margin-top:10px"><button class="btn danger" data-act="del">${$t("Sil")}</button></div></div>`;
    } else {
      const x = objs[0];
      if (x.t === 'i') {
        const d = x.d;
        o = `<div class="sec"><h3>${$t("Cihaz")}</h3><dl class="kv"><dt>${$t("Cihaz")}</dt><dd>${esc(d?.name ?? (x.o as any).dev)}</dd><dt>${$t("Açıklama")}</dt><dd>${esc(d?.desc ?? '—')}</dd><dt>${$t("Parça no")}</dt><dd>${esc(d?.pn || '—')}</dd><dt>${$t("Ölçü")}</dt><dd>${d ? `${d.w} × ${d.h} mm${d.sized ? '' : $t(" (varsayılan)")}` : '—'}</dd></dl>
          <div class="row2" style="margin-top:10px">${fld('X (mm)', inp('lo.x', x.o.x, 'tag'))}${fld('Y (mm)', inp('lo.y', x.o.y, 'tag'))}</div>`;
      } else if (x.t === 'r') {
        const r = x.o as any;
        o = `<div class="sec"><h3>${$t("DIN rayı 35 mm")}</h3><div class="row2">${fld('X (mm)', inp('lo.x', r.x, 'tag'))}${fld($t("Y ekseni (mm)"), inp('lo.y', r.y, 'tag'))}</div>${fld('Uzunluk (mm)', inp('lo.len', r.len, 'tag'))}`;
      } else {
        const d = x.o as any;
        o = `<div class="sec"><h3>${$t("Kablo kanalı")}</h3><div class="row2">${fld('X (mm)', inp('lo.x', d.x, 'tag'))}${fld('Y (mm)', inp('lo.y', d.y, 'tag'))}</div><div class="row2">${fld($t("Genişlik (mm)"), inp('lo.w', d.w, 'tag'))}${fld($t("Yükseklik (mm)"), inp('lo.h', d.h, 'tag'))}</div>`;
      }
      o += `<div class="btnrow" style="margin-top:6px"><button class="btn danger" data-act="del">${$t("Sil")}</button></div></div>`;
    }
    this.$('#props').innerHTML = o;
  }

  /** Called from onField, i.e. already inside mutate(). */
  private layoutField(f: string, v: string) {
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n)) return;
    const l = this.lay;
    if (f === 'ly.w' || f === 'ly.h') {
      const k = f.slice(3) as 'w' | 'h';
      l[k] = Math.max(50, Math.min(5000, Math.round(n)));
      // a door and its inside view keep the same size
      for (const p of this.pr.pages) if (p.layout && (p.layout.inside === this.page.id || p.id === l.inside)) p.layout[k] = l[k];
    }
    else if (f === 'ly.scale') l.scale = Math.max(0.5, Math.min(100, n));
    else if (f === 'ly.depth') l.depth = Math.max(50, Math.min(2000, Math.round(n)));
    else {
      const k = [...this.sel][0], x = k && this.lobj(k);
      if (!x) return;
      const key = f.slice(3);
      const val = key === 'len' || key === 'w' || key === 'h' ? Math.max(5, Math.round(n)) : Math.round(n * 10) / 10;
      (x.o as any)[key] = val;
    }
  }

  private layoutDown(e: PointerEvent, w: Pt) {
    const l = this.lay, p = toPlate(l, w[0], w[1]);
    if (this.tool === 'ldev' && this.placeDev && !l.inside) {
      const d = layoutDevices(this.A, partOf).get(this.placeDev);
      if (!d) return;
      this.mutate(() => {
        const it = addLayoutItem(l, d, p[0] - d.w / 2, p[1] - d.h / 2);
        this.sel.clear();
        this.sel.add('L:i:' + it.id);
      });
      this.placeDev = null;
      return this.setTool('select');
    }
    if (this.tool === 'lrail' || this.tool === 'lduct') {
      const a: Pt = [Math.round(p[0] / 5) * 5, Math.round(p[1] / 5) * 5];
      this.drag = { k: 'ldraw', kind: this.tool === 'lrail' ? 'rail' : 'duct', a, b: a };
      this.svg.setPointerCapture(e.pointerId);
      return;
    }
    const key = (e.target as Element).closest<SVGElement>('[data-lid]')?.dataset.lid;
    if (e.pointerType === 'touch' && this.tool === 'select') {
      // held still: picks it up (selected by then), so the plate-move below starts
      return this.touchPan(e, key, key ? () => this.layoutDown({ pointerType: 'hold', pointerId: e.pointerId, target: e.target, shiftKey: false, ctrlKey: false } as unknown as PointerEvent, w) : undefined);
    }
    if (key) {
      if (e.shiftKey || e.ctrlKey) {
        if (this.sel.has(key)) this.sel.delete(key);
        else this.sel.add(key);
      } else if (!this.sel.has(key)) {
        this.sel.clear();
        this.sel.add(key);
      }
      const orig = new Map<string, { x: number; y: number }>();
      for (const k of this.sel) {
        const o = this.lobj(k);
        if (o) orig.set(k, { x: o.o.x, y: o.o.y });
      }
      if (!this.readOnly) this.drag = { k: 'lmove', a: p, snap: this.snap(), orig, moved: false };
      this.svg.setPointerCapture(e.pointerId);
    } else this.sel.clear();
    this.renderPage();
    this.renderProps();
  }

  private layoutDrag(d: Extract<Drag, { k: 'lmove' | 'ldraw' }>, w: Pt) {
    const l = this.lay, p = toPlate(l, w[0], w[1]);
    if (d.k === 'ldraw') {
      d.b = [Math.round(p[0] / 5) * 5, Math.round(p[1] / 5) * 5];
      return this.renderOverlay();
    }
    const step = l.scale >= 5 ? 5 : 1;
    const dx = Math.round((p[0] - d.a[0]) / step) * step, dy = Math.round((p[1] - d.a[1]) / step) * step;
    if (!dx && !dy && !d.moved) return;
    d.moved = true;
    const devs = layoutDevices(this.A, partOf);
    for (const [k, o0] of d.orig) {
      const x = this.lobj(k, devs);
      if (!x) continue;
      x.o.x = o0.x + dx;
      x.o.y = o0.y + dy;
      if (x.t === 'i' && d.orig.size === 1 && x.d) [x.o.x, x.o.y] = snapToRail(l, x.o.x, x.o.y, x.d.h);
    }
    this.renderPage();
  }

  private layoutUp(d: Extract<Drag, { k: 'lmove' | 'ldraw' }>) {
    const l = this.lay;
    if (d.k === 'lmove') {
      if (d.moved) {
        this.pushUndo(d.snap);
        this.changed();
      }
      return;
    }
    const [a, b] = [d.a, d.b];
    if (d.kind === 'rail') {
      const len = Math.abs(b[0] - a[0]);
      if (len >= 20) this.mutate(() => l.rails.push({ id: newId(), x: Math.min(a[0], b[0]), y: a[1], len }));
    } else {
      const w = Math.abs(b[0] - a[0]), h = Math.abs(b[1] - a[1]);
      if (w >= 10 && h >= 10) this.mutate(() => l.ducts.push({ id: newId(), x: Math.min(a[0], b[0]), y: Math.min(a[1], b[1]), w, h }));
    }
    this.renderOverlay();
  }

  private autoLayoutDialog() {
    const l = this.lay, has = l.items.length + l.rails.length + l.ducts.length > 0;
    const close = openModal(
      `<h2>${$t("Otomatik yerleştir")}</h2>
      ${this.lay.door
        ? `<p>${$t("Kapak cihazları (buton, lamba, acil stop) şemadaki sırayla, 60 mm aralıkla ortalanmış satırlara dizilir; bir motorun buton ve lambaları yan yana kalır. Delik ölçüleri <b>Raporlar → Kapak delik listesi</b>nde.")}</p>`
        : `<p>${$t("Cihazlar pano sırasıyla dizilir: besleme ve sigortalar, motor koruma, kontaktör ve röleler, güç kaynağı ve PLC. Klemens şeritleri en alt satıra gelir. Yanlara dikey, satırların arasına yatay kablo kanalı konur; her satır bir DIN rayıdır.")}</p>`}
      <label class="chkline"><input type="radio" name="am" value="fresh"${has ? '' : ' checked'}> ${$t("Sıfırdan yerleştir{0}", has ? ` <span class="note">${$t("(bu sayfadaki ray, kanal ve cihazlar kaldırılır)")}</span>` : '')}</label>
      <label class="chkline"><input type="radio" name="am" value="fill"${has ? ' checked' : ''}> ${$t("Yalnızca yerleştirilmemiş cihazları ekle")} <span class="note">${$t("(mevcut raylardaki boşluklar, sonra yeni satırlar)")}</span></label>
      <div class="row2" style="margin-top:8px${this.lay.door ? ';display:none' : ''}"><label class="fld"><span>${$t("Kablo kanalı (mm)")}</span><select id="aduct"><option>25</option><option selected>40</option><option>60</option><option>80</option></select></label>
      <label class="fld"><span>${$t("Kenar boşluğu (mm)")}</span><select id="amarg"><option>10</option><option selected>20</option><option>30</option></select></label></div>
      <label class="chkline"${this.lay.door ? ' style="display:none"' : ''}><input type="checkbox" id="adoor"> ${$t("Kapak cihazlarını da plakaya koy (buton, lamba, acil stop)")}</label>
      <label class="chkline"><input type="checkbox" id="agrow" checked> ${$t("Sığmazsa plaka yüksekliğini büyüt")}</label>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Yerleştir")}</button></div>`,
      (a, dlg) => {
        if (a !== 'ok') return;
        const mode = (dlg.querySelector<HTMLInputElement>('input[name="am"]:checked')?.value ?? 'fill') as 'fresh' | 'fill';
        let r: ReturnType<typeof autoLayout> | null = null;
        this.mutate(() => {
          r = autoLayout(this.pr, this.A, this.cur, {
            mode,
            duct: Number(qs<HTMLSelectElement>(dlg, '#aduct').value),
            margin: Number(qs<HTMLSelectElement>(dlg, '#amarg').value),
            includeDoor: qs<HTMLInputElement>(dlg, '#adoor').checked,
            growPlate: qs<HTMLInputElement>(dlg, '#agrow').checked,
          }, partOf);
          this.sel.clear();
        });
        close();
        const res = r as ReturnType<typeof autoLayout> | null;
        if (!res) return;
        if (!res.placed.length && !res.skipped.length) return toast($t("Yerleştirilecek cihaz kalmadı"));
        openModal(
          `<h2>${$t("Otomatik yerleşim tamamlandı")}</h2>
          <dl class="kv"><dt>${$t("Yerleşen")}</dt><dd>${$t("{0} cihaz · {1} yeni satır", res.placed.length, res.rows)}</dd>${res.grewTo ? `<dt>${$t("Plaka")}</dt><dd>${$t("yükseklik {0} mm'ye çıkarıldı", res.grewTo)}</dd>` : ''}
          ${res.skipped.length ? `<dt>${$t("Sığmayan")}</dt><dd>${res.skipped.map(esc).join(', ')}</dd>` : ''}</dl>
          <p class="note" style="margin-top:10px">${$t("Sonucu sürükleyerek düzeltebilirsiniz; geri almak için Ctrl+Z.{0}", res.skipped.length ? $t(" Sığmayanlar için plakayı büyütün ya da \"Sığmazsa plaka yüksekliğini büyüt\" seçeneğini açın.") : '')}</p>
          <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
          () => {},
        );
      },
    );
  }

  /* ================= collaboration ================= */
  private highlightSvg() {
    const t = this.highlight?.diff.touched.get(this.page.id);
    if (!t) return '';
    let o = '';
    for (const s of this.page.syms)
      if (t.has(s.id)) {
        const B = bbWorld(s);
        o += `<rect class="chgbox" x="${B[0] - 2}" y="${B[1] - 2}" width="${B[2] - B[0] + 4}" height="${B[3] - B[1] + 4}" rx="1"/>`;
      }
    for (const w of this.page.wires) if (t.has(w.id)) o += `<polyline class="chgwire" points="${w.pts.map((p) => p.join(',')).join(' ')}"/>`;
    return o;
  }

  private async shareDialog() {
    let info: Awaited<ReturnType<typeof api.members>>;
    try {
      info = await api.members(this.id);
    } catch (e) {
      return toast((e as Error).message);
    }
    const body = () => `<h2>${$t("Projeyi paylaş")}</h2>
      <p>${$t("Sahibi: <b>{0}</b> ({1}). Paylaştığınız kişiler projeyi kendi listelerinde \"Benimle paylaşılanlar\" altında görür.", esc(info.owner.name), esc(info.owner.email))}</p>
      <div class="vlist">${info.members.map((m) => `<div class="vrow"><span class="vt">${esc(m.name)}</span><span class="note">${esc(m.email)}</span>
        <span style="white-space:nowrap"><select data-mrole="${m.id}" aria-label="${$t("Yetki")}"><option value="read"${m.role === 'read' ? ' selected' : ''}>${$t("Okuyabilir")}</option><option value="write"${m.role === 'write' ? ' selected' : ''}>${$t("Düzenleyebilir")}</option></select>
        <button class="btn danger" data-munshare="${m.id}">${$t("Kaldır")}</button></span></div>`).join('') || `<p class="note">${$t("Henüz kimseyle paylaşılmadı.")}</p>`}</div>
      ${info.role !== 'owner' ? '' : info.candidates.length
        ? `<div class="row2"><label class="fld"><span>${$t("Kullanıcı")}</span><select id="se">${info.candidates.map((u) => `<option value="${esc(u.email)}">${esc(u.name)} — ${esc(u.email)}</option>`).join('')}</select></label>
          <label class="fld"><span>${$t("Yetki")}</span><select id="sr"><option value="write">${$t("Düzenleyebilir")}</option><option value="read">${$t("Okuyabilir")}</option></select></label></div>`
        : `<p class="note">${$t("Firmanızda bu projeyi paylaşabileceğiniz başka kullanıcı yok. Yeni kullanıcıyı firma yöneticisi \"Firma ve kullanıcılar\" sayfasından ekler.")}</p>`}
      <div class="err" id="serr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Kapat")}</button>${info.role === 'owner' && info.candidates.length ? `<button class="btn pri" data-m="add">${$t("Paylaş")}</button>` : ''}</div>`;
    const refresh = async (dlg: HTMLElement) => {
      info = await api.members(this.id);
      dlg.innerHTML = body();
    };
    openModal(body(), async (a, dlg) => {
      if (a !== 'add') return;
      try {
        await api.share(this.id, qs<HTMLSelectElement>(dlg, '#se').value, qs<HTMLSelectElement>(dlg, '#sr').value as 'read' | 'write');
        await refresh(dlg);
        toast($t("Paylaşıldı"));
      } catch (e) {
        qs(dlg, '#serr').textContent = (e as Error).message;
      }
    });
    const dlg = document.querySelector<HTMLElement>('.modal .dlg')!;
    dlg.addEventListener('change', async (e) => {
      const t = e.target as HTMLSelectElement;
      if (!t.dataset.mrole) return;
      const m = info.members.find((x) => x.id === +t.dataset.mrole!);
      if (m) await api.share(this.id, m.email, t.value as 'read' | 'write').catch((x) => toast((x as Error).message));
      toast($t("Yetki güncellendi"));
    });
    dlg.addEventListener('click', async (e) => {
      const id = (e.target as HTMLElement).closest<HTMLElement>('[data-munshare]')?.dataset.munshare;
      if (!id) return;
      await api.unshare(this.id, +id).catch((x) => toast((x as Error).message));
      await refresh(dlg);
    });
  }

  private async revisionsDialog() {
    let list: Awaited<ReturnType<typeof api.versions>>;
    try {
      list = await api.versions(this.id);
    } catch (e) {
      return toast((e as Error).message);
    }
    const canWrite = !this.readOnly;
    const rev = this.pr.revision;
    const row = (v: (typeof list.versions)[number]) => `<div class="vrow">
      <span>${v.kind === 'rev' ? `<span class="pill ok">${$t("Rev {0}", esc(v.label))}</span>` : `<span class="pill warn">${$t("otomatik")}</span>`}</span>
      <span><span class="vt">${esc(fmtDate(v.created_at))}</span> <span class="note">${$t("· sürüm {0}{1}", v.version, v.author ? ` · ${esc(v.author)}` : '')}</span>${v.note ? `<br><span class="note">${esc(v.note)}</span>` : ''}</span>
      <span style="white-space:nowrap"><button class="btn" data-vcmp="${v.id}">${$t("Karşılaştır")}</button>${canWrite ? ` <button class="btn" data-vrestore="${v.id}">${$t("Geri yükle")}</button>` : ''}</span></div>`;
    const close = openModal(
      `<h2>${$t("Revizyonlar")}</h2>
      <p>${$t("Geçerli revizyon: <b>{0}</b>. Kaydederken en fazla 10 dakikada bir otomatik kopya alınır; adlı revizyonlar hiç silinmez.", rev ? $t("Rev {0} · {1}{2}", esc(rev.label), esc(rev.date), rev.by ? ` · ${esc(rev.by)}` : '') : $t("henüz yok"))}</p>
      ${canWrite ? `<div class="row2"><label class="fld"><span>${$t("Yeni revizyon adı")}</span><input id="rl" class="tag" maxlength="20" placeholder="${esc(nextRev(rev?.label))}"></label>
      <label class="fld"><span>${$t("Açıklama")}</span><input id="rn" maxlength="200" placeholder="${$t("ör. Müşteri onayı için")}"></label></div>
      <div class="btnrow"><button class="btn pri" data-m="newrev">${$t("Revizyon oluştur")}</button></div><div class="err" id="rerr"></div>` : ''}
      <div class="vlist">${list.versions.map(row).join('') || `<p class="note">${$t("Henüz kayıtlı sürüm yok.")}</p>`}</div>
      <div class="acts"><button class="btn" data-m="close">${$t("Kapat")}</button></div>`,
      async (a, dlg) => {
        if (a !== 'newrev') return;
        const label = qs<HTMLInputElement>(dlg, '#rl').value.trim() || nextRev(rev?.label);
        try {
          // the revision stamps what is on the server: save pending edits first
          clearTimeout(this.saveTimer);
          await this.flush();
          if (this.rev !== this.savedRev) throw new Error($t("Önce değişiklikler kaydedilmeli; kayıt başarısız oldu."));
          const r = await api.createRevision(this.id, label, qs<HTMLInputElement>(dlg, '#rn').value.trim(), this.version);
          this.version = r.version;
          this.pr.revision = r.revision;
          close();
          this.renderPage();
          toast($t("Rev {0} oluşturuldu", label));
        } catch (e) {
          qs(dlg, '#rerr').textContent = (e as Error).message;
        }
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .dlg')!;
    dlg.addEventListener('click', async (e) => {
      const t = e.target as HTMLElement;
      const cmp = t.closest<HTMLElement>('[data-vcmp]')?.dataset.vcmp;
      const rst = t.closest<HTMLElement>('[data-vrestore]')?.dataset.vrestore;
      const v = list.versions.find((x) => x.id === Number(cmp ?? rst));
      if (!v) return;
      if (cmp) {
        close();
        return this.compareWith(v.id, v.kind === 'rev' ? $t("Rev {0}", v.label) : $t("{0} kopyası", fmtDate(v.created_at)));
      }
      const c2 = openModal(
        `<h2>${$t("Bu sürüm geri yüklensin mi?")}</h2><p>${$t("{0} · {1} geçerli sürüm olur. Şu anki hâl otomatik kopya olarak saklanır; istediğinizde ona geri dönebilirsiniz.", v.kind === 'rev' ? $t("Rev {0}", esc(v.label)) : $t("Otomatik kopya"), esc(fmtDate(v.created_at)))}</p>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Geri yükle")}</button></div>`,
        async (b) => {
          if (b !== 'ok') return;
          try {
            clearTimeout(this.saveTimer);
            await this.flush();
            await api.restoreVersion(this.id, v.id);
            c2();
            close();
            await this.reloadFromServer();
            toast($t("Sürüm geri yüklendi"));
          } catch (x) {
            toast((x as Error).message);
          }
        },
      );
    });
  }

  /**
   * A local project's revisions: named ones only, each a copy kept inside the project file (no automatic copies).
   * Making one stamps the changed pages like on the server; restoring is one step back with Undo.
   */
  private async localRevisionsDialog() {
    const list = await localHistory(this.id);
    const rev = this.pr.revision;
    const row = (h: (typeof list)[number], i: number) => `<div class="vrow">
      <span><span class="pill ok">${$t("Rev {0}", esc(h.label))}</span></span>
      <span><span class="vt">${esc(fmtDate(h.created || h.date))}</span>${h.note ? `<br><span class="note">${esc(h.note)}</span>` : ''}</span>
      <span style="white-space:nowrap"><button class="btn" data-lcmp="${i}">${$t("Karşılaştır")}</button> <button class="btn" data-lrestore="${i}">${$t("Geri yükle")}</button></span></div>`;
    const close = openModal(
      `<h2>${$t("Revizyonlar")}</h2>
      <p>${$t('Geçerli revizyon: <b>{0}</b>. Adlı revizyonlar proje dosyasının içinde saklanır; yerel projede otomatik kopya alınmaz.', rev ? $t("Rev {0} · {1}{2}", esc(rev.label), esc(rev.date), rev.by ? ` · ${esc(rev.by)}` : '') : $t("henüz yok"))}</p>
      <div class="row2"><label class="fld"><span>${$t("Yeni revizyon adı")}</span><input id="rl" class="tag" maxlength="20" placeholder="${esc(nextRev(rev?.label))}"></label>
      <label class="fld"><span>${$t("Açıklama")}</span><input id="rn" maxlength="200" placeholder="${$t("ör. Müşteri onayı için")}"></label></div>
      <div class="btnrow"><button class="btn pri" data-m="newrev">${$t("Revizyon oluştur")}</button></div><div class="err" id="rerr"></div>
      <div class="vlist">${list.map(row).reverse().join('') || `<p class="note">${$t("Henüz kayıtlı sürüm yok.")}</p>`}</div>
      <div class="acts"><button class="btn" data-m="close">${$t("Kapat")}</button></div>`,
      async (a, dlg) => {
        if (a !== 'newrev') return;
        const label = qs<HTMLInputElement>(dlg, '#rl').value.trim() || nextRev(rev?.label), note = qs<HTMLInputElement>(dlg, '#rn').value.trim();
        try {
          // the pages changed since the last named revision carry this one in their title block
          const prev = list.length ? list[list.length - 1].data : null;
          this.mutate(() => stampRevision(this.pr, prev, { label, date: today(), ...(note ? { note } : {}) }));
          clearTimeout(this.saveTimer);
          await this.flush();
          await addLocalRevision(this.id, { label, date: today(), ...(note ? { note } : {}), created: new Date().toISOString(), data: JSON.parse(JSON.stringify(embedCustomSymbols(this.pr))) });
          close();
          this.renderPage();
          toast($t("Rev {0} oluşturuldu", label));
        } catch (e) {
          qs(dlg, '#rerr').textContent = (e as Error).message;
        }
      },
    );
    document.querySelector<HTMLElement>('.modal .dlg')!.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const cmp = t.closest<HTMLElement>('[data-lcmp]')?.dataset.lcmp, rst = t.closest<HTMLElement>('[data-lrestore]')?.dataset.lrestore;
      const h = list[Number(cmp ?? rst)];
      if (!h) return;
      const copy = () => normaliseProject(JSON.parse(JSON.stringify(h.data)));
      if (cmp) {
        close();
        return this.compareProject(copy(), $t("Rev {0}", h.label));
      }
      const c2 = openModal(
        `<h2>${$t("Bu sürüm geri yüklensin mi?")}</h2><p>${$t('Rev {0} geçerli hâl olur. Şu anki hâle Geri (Ctrl+Z) ile dönebilirsiniz; istiyorsanız önce bir revizyon oluşturun.', esc(h.label))}</p>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Geri yükle")}</button></div>`,
        (b) => {
          if (b !== 'ok') return;
          c2();
          close();
          this.mutate(() => {
            this.pr = copy();
            this.cur = Math.min(this.cur, this.pr.pages.length - 1);
            this.sel.clear();
          });
          this.renderAll();
          toast($t("Sürüm geri yüklendi"));
        },
      );
    });
  }

  private async compareWith(vid: number, label: string) {
    let old;
    try {
      old = normaliseProject((await api.versionData(this.id, vid)).data);
    } catch (e) {
      return toast((e as Error).message);
    }
    this.compareProject(old, label);
  }

  /** Marks what changed since an earlier state of the project and lists it. */
  private compareProject(old: Project, label: string) {
    const diff = diffProjects(old, this.pr);
    this.highlight = { diff, label };
    this.renderPage();
    this.renderStatus();
    const KIND = { added: 'eklendi', removed: 'silindi', changed: $t("değişti"), moved: $t("taşındı") };
    const s = diff.summary;
    const close = openModal(
      `<h2>${$t("{0} → şimdiki hâl", esc(label))}</h2>
      <p>${$t("{0} eklenen, {1} silinen, {2} değişen, {3} taşınan. Değişen semboller ve bağlantılar şemada turuncu işaretlendi; işaretleri durum çubuğundan kapatabilirsiniz.", s.added, s.removed, s.changed, s.moved)}</p>
      <div class="vlist">${diff.changes.map((c, i) => `<button class="navrow" data-chg="${i}"><span><b class="chg-${c.kind}">${KIND[c.kind]}</b> ${esc(c.what)} <span class="note">${esc(c.detail)}</span></span><span class="m">${esc(c.page.split(' · ')[0])}</span></button>`).join('') || `<p class="note">${$t("Fark yok.")}</p>`}</div>
      <div class="acts"><button class="btn" data-m="close">${$t("Kapat")}</button></div>`,
      () => {},
    );
    document.querySelector<HTMLElement>('.modal .dlg')!.addEventListener('click', (e) => {
      const i = (e.target as HTMLElement).closest<HTMLElement>('[data-chg]')?.dataset.chg;
      if (i == null) return;
      const c = diff.changes[+i];
      close();
      if (c.symId && this.pr.pages[c.pi]?.syms.some((x) => x.id === c.symId)) this.focusOn(c.pi, c.symId);
      else if (this.pr.pages[c.pi]) this.gotoPage(c.pi);
    });
  }

  /* ================= PLC ================= */
  /** AutomationML export: the CPU is not drawn, so its order number and firmware are asked (and remembered in this browser). */
  private automationMLDialog() {
    const KEY = 'enercad.aml';
    let saved: Partial<AmlOptions> = {};
    try {
      saved = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    } catch {
      /* private window: defaults */
    }
    const f = (id: string, label: string, v: string, ph = '') => `<label class="fld"><span>${label}</span><input id="${id}" value="${esc(v)}" placeholder="${esc(ph)}"></label>`;
    const close = openModal(
      `<h2>${$t('AutomationML dışa aktar')}</h2>
      <p>${$t('PLC kartları, kanalları ve etiketleri AutomationML (AR APC 1.4) dosyası olarak; TIA Portal "CAx verilerini içe aktar" ve EPLAN okur. Kartlar parça numarasıyla (sipariş numarası) tanınır.')}</p>
      <label class="fld"><span>${$t('Sistem')}</span><select id="asys"><option value="s71500"${saved.system !== 'generic' ? ' selected' : ''}>S7-1500 (TIA Portal)</option><option value="generic"${saved.system === 'generic' ? ' selected' : ''}>${$t('Genel (raf olmadan)')}</option></select></label>
      <div class="row2">${f('asta', $t('İstasyon adı'), saved.station ?? 'S7-1500_station_1')}${f('acpu', $t('CPU adı'), saved.cpuName ?? 'PLC_1')}</div>
      <div class="row2">${f('aord', $t('CPU sipariş numarası'), saved.cpuOrder ?? '', '6ES7 511-1AK02-0AB0')}${f('afw', $t('Firmware'), saved.cpuFirmware ?? '', 'V2.8')}</div>
      <p class="note">${$t('Kartlar 2. yuvadan başlayarak etiket sırasıyla yerleşir; TIA Portal\'da yuvaları kontrol edin.')}</p>
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('İndir')}</button></div>`,
      (a, dlg) => {
        if (a !== 'ok') return;
        const v = (id: string) => qs<HTMLInputElement>(dlg, '#' + id).value.trim();
        const o: AmlOptions = { system: v('asys') === 'generic' ? 'generic' : 's71500', station: v('asta') || 'S7-1500_station_1', cpuName: v('acpu') || 'PLC_1', cpuOrder: v('aord'), cpuFirmware: v('afw'), now: new Date().toISOString() };
        try {
          localStorage.setItem(KEY, JSON.stringify({ ...o, now: undefined }));
        } catch {
          /* not remembered */
        }
        const r = plcAutomationML(this.pr, this.A, o, partOf);
        if (!r.cards) return void toast($t('Projede adresli PLC kanalı yok.'));
        close();
        downloadFile(`${safeFileName(this.pr.name)}.aml`, r.xml, 'application/xml;charset=utf-8');
        if (r.warnings.length)
          openModal(
            `<h2>${$t('AutomationML dışa aktarıldı')}</h2><p>${$t('{0} kart, {1} kanal. Dikkat:', r.cards, r.channels)}</p><ul>${r.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}</ul><div class="acts"><button class="btn pri" data-m="close">${$t('Tamam')}</button></div>`,
            () => {},
          );
        else toast($t('{0} kart, {1} kanal dışa aktarıldı', r.cards, r.channels));
      },
    );
  }

  /** PLC assignment list: addresses and symbol names from a sheet, matched to the channels, shown before they are applied. */
  private assignmentImport() {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.csv,.txt,.st,.exp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        // a CODESYS declaration list is read here line by line; sheets on the server
        const text = /\.(st|exp|txt)$/i.test(file.name) ? await readFileText(file) : '';
        const table = / AT\s+%[IQ]/i.test(text) ? text.split(/\r?\n/).filter((l) => l.trim()).map((l) => [l]) : (await api.sheetRows(file)).rows;
        const list = readAssignmentList(table);
        if (!list.rows.length) return void toast($t('Dosyada PLC adresi bulunamadı (ör. I0.0, %IX0.0, IW64).'));
        this.assignmentPreview(file.name, list);
      } catch (e) {
        if (!reloadIfStale(e)) toast((e as Error).message);
      }
    };
    input.click();
  }

  private assignmentPreview(name: string, list: ReturnType<typeof readAssignmentList>) {
    const r = matchAssignmentList(this.A, list);
    const cols = Object.entries(list.columns).map(([k, v]) => `${({ addr: $t('Adres'), name: $t('Sembol'), comment: $t('Açıklama'), card: $t('Kart') } as Record<string, string>)[k]}: <b>${esc(v)}</b>`).join(' · ');
    const arrow = (a: string, b: string) => (a === b ? esc(b) : `${esc(a || '—')} → <b>${esc(b || '—')}</b>`);
    const close = openModal(
      `<h2>${$t('Atama listesi içe aktar')}</h2>
      <p>${esc(name)}: ${$t('{0} satır; {1} kanal değişecek, {2} kanal zaten aynı, {3} satır eşleşmedi.', list.rows.length, r.changes.length, r.same, r.unmatched.length)}${list.skipped ? ' ' + $t('{0} satır giriş/çıkış adresi değil (bellek, veri bloğu…), atlandı.', list.skipped) : ''}</p>
      <p class="note">${cols}<br>${$t('Sembol adı bir kanalın sembolü ya da açıklamasıyla eşleşen satır o kanala adresini verir; adresi bir kanalda olan satır o kanala sembol adını verir.')}</p>
      ${r.problems.length ? `<div class="err">${r.problems.map(esc).join('<br>')}</div>` : ''}
      ${r.changes.length ? `<div class="tblw" style="max-height:38vh"><table class="t"><thead><tr><th>${$t('Kart')}</th><th>${$t('Konum')}</th><th>${$t('Adres')}</th><th>${$t('Sembol')}</th><th>${$t('Açıklama')}</th><th>${$t('Satır')}</th></tr></thead><tbody>${r.changes
        .map((c) => `<tr><td class="mono">${esc(c.card)}</td><td class="mono">/${esc(loc(c.it))}</td><td class="mono">${arrow(c.old.addr, c.addr)}</td><td class="mono">${arrow(c.old.sym, c.sym)}</td><td>${arrow(c.old.desc, c.desc)}</td><td class="mono">${c.line}${c.by === 'name' ? ' · ' + $t('sembol adıyla') : ''}</td></tr>`)
        .join('')}</tbody></table></div>` : ''}
      ${r.unmatched.length ? `<details style="margin-top:8px"><summary>${$t('Eşleşmeyen satırlar ({0})', r.unmatched.length)}</summary><div class="tblw" style="max-height:20vh"><table class="t"><tbody>${r.unmatched.map((u) => `<tr><td class="mono">${u.line}</td><td class="mono">${esc(u.addr)}</td><td class="mono">${esc(u.name)}</td><td>${esc(u.comment)}</td></tr>`).join('')}</tbody></table></div></details>` : ''}
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button>${r.changes.length ? `<button class="btn pri" data-m="ok">${$t('Uygula')}</button>` : ''}</div>`,
      (a) => {
        if (a !== 'ok') return;
        close();
        this.mutate(() => applyAssignment(matchAssignmentList(this.A, list).changes));
        toast($t('{0} PLC kanalı güncellendi', r.changes.length));
      },
    );
  }

  private readdressDialog(key: string) {
    const title = key ? keyName(this.A, key) : $t("Tüm PLC kartları");
    const n = this.A.all.filter((it) => PLC_KIND[it.s.type] && (!key || this.A.devKey.get(it.s.id) === key)).length;
    const f = (id: string, label: string, v: string) => `<label class="fld"><span>${label}</span><input id="${id}" class="tag" value="${v}"></label>`;
    const close = openModal(
      `<h2>${$t("PLC adreslerini yeniden sırala")}</h2><p>${$t("{0}: {1} kanal, sayfa ve sütun sırasıyla yeniden numaralanır. Dijital adresler bit bit (0–7), analog adresler 2 bayt artar.", esc(title), n)}</p>
      <div class="row2">${f('sDI', $t("Dijital giriş başlangıcı"), DEFAULT_START.DI)}${f('sDQ', $t("Dijital çıkış başlangıcı"), DEFAULT_START.DQ)}</div>
      <div class="row2">${f('sAI', $t("Analog giriş başlangıcı"), DEFAULT_START.AI)}${f('sAQ', $t("Analog çıkış başlangıcı"), DEFAULT_START.AQ)}</div>
      <div class="err" id="rerr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Yeniden sırala")}</button></div>`,
      (a, dlg) => {
        if (a !== 'ok') return;
        const v = (id: string) => qs<HTMLInputElement>(dlg, '#' + id).value.trim().toUpperCase();
        const start = { DI: v('sDI'), DQ: v('sDQ'), AI: v('sAI'), AQ: v('sAQ') };
        const bad = Object.values(start).find((x) => !parsePlcAddress(x));
        if (bad) return void (qs(dlg, '#rerr').textContent = $t("\"{0}\" geçerli bir adres değil (ör. I0.0, Q4.0, IW64).", bad));
        let changed = 0;
        this.mutate(() => void (changed = readdressPlc(this.A, key, start)));
        close();
        toast(changed ? $t("{0} adres güncellendi", changed) : $t("Adresler zaten sıralı"));
      },
    );
  }

  /* ================= navigators ================= */
  private renderNav() {
    for (const [k, id] of [['pages', 'navPages'], ['dev', 'navDev'], ['pot', 'navPot']] as const) {
      this.$('#' + id).hidden = this.nav !== k;
      this.el.querySelector(`[data-nav="${k}"]`)?.classList.toggle('on', this.nav === k);
    }
    if (this.nav === 'dev') this.renderDevTree();
    if (this.nav === 'pot') this.renderPotList();
  }

  private renderDevTree() {
    const A = this.A, q = (this.$('#devq') as HTMLInputElement).value.trim().toLocaleLowerCase('tr');
    const groups = new Map<string, Map<string, Placed[]>>();
    for (const it of A.all) {
      const key = A.devKey.get(it.s.id);
      if (!key) continue;
      const struct = key.slice(0, key.lastIndexOf('-'));
      const g = groups.get(struct) ?? groups.set(struct, new Map()).get(struct)!;
      (g.get(key) ?? g.set(key, []).get(key)!).push(it);
    }
    let o = '';
    for (const [struct, devs] of [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'tr'))) {
      const rows = [...devs.entries()]
        .sort((a, b) => keyName(A, a[0]).localeCompare(keyName(A, b[0]), 'tr', { numeric: true }))
        .filter(([key, list]) => !q || `${key} ${list.map((x) => `${x.s.desc} ${symbolName(x.s.type)}`).join(' ')}`.toLocaleLowerCase('tr').includes(q));
      if (!rows.length) continue;
      o += `<details open><summary>${esc(struct)} <span class="note">${rows.length}</span></summary>`;
      for (const [key, list] of rows) {
        const main = list.find((x) => LIB[x.s.type].role !== 'aux') ?? list[0];
        o += `<div class="dv"><b>${esc(key.slice(key.lastIndexOf('-')))}</b> <span class="note">${esc(main.s.desc || symbolName(main.s.type))}</span></div>`;
        for (const x of list.sort((a, b) => a.pi - b.pi || a.s.x - b.s.x))
          o += `<button class="navrow" data-goto="${x.pi}|${x.s.id}"><span>${esc(symbolName(x.s.type))}${LIB[x.s.type].role === 'term' ? ' ' + esc(x.s.props.pinNo ?? '') : ''}</span><span class="m">/${loc(x)}</span></button>`;
      }
      o += '</details>';
    }
    this.$('#devtree').innerHTML = o || `<p class="note">${$t("Cihaz bulunamadı.")}</p>`;
  }

  private renderPotList() {
    // each potential with the first of its wires on every page it runs over
    const A = this.A, pots = new Map<string, { name: string; ptype: string; pages: Map<number, string> }>();
    this.pr.pages.forEach((p, pi) =>
      p.wires.forEach((w) => {
        const r = A.pu.find(wireNode(w.id)), pt = A.pot.get(r);
        if (!pt) return;
        const e = pots.get(r) ?? pots.set(r, { name: pt.name, ptype: pt.ptype, pages: new Map() }).get(r)!;
        if (!e.pages.has(pi)) e.pages.set(pi, w.id);
      }),
    );
    const COL: Record<string, string> = { L: 'pl', N: 'pn', PE: 'ppe', 'DC+': 'pdp', 'DC-': 'pdn' };
    this.$('#potlist').innerHTML =
      [...pots.values()]
        .sort((a, b) => (a.name || a.ptype).localeCompare(b.name || b.ptype, 'tr', { numeric: true }))
        .map((p) => {
          const [pi, wid] = [...p.pages][0];
          // the type only when the name does not say it already (L1 · L, but not N · N)
          const type = p.name && p.name !== p.ptype ? ` <span class="note">${esc(p.ptype)}</span>` : '';
          return `<div class="potrow"><button class="navrow" data-gowire="${pi}|${wid}" title="${$t('İlk teline git')}"><span><i class="pdot" style="background:var(--${COL[p.ptype] ?? 'ink'})"></i>${esc(p.name || p.ptype)}${type}</span></button><span class="pgs" title="${$t('Geçtiği sayfalar')}">${[...p.pages].map(([q, w]) => `<button class="pglink" data-gowire="${q}|${w}">${q + 1}</button>`).join('')}</span></div>`;
        })
        .join('') || `<p class="note">${$t("Adlandırılmış potansiyel yok. Potansiyel noktası ya da tipli kesinti noktası ekleyin.")}</p>`;
  }

  private focusWire(pi: number, wid: string) {
    this.mode = 'sch';
    this.cur = pi;
    this.sel.clear();
    this.sel.add('w:' + wid);
    this.renderAll();
    const w = this.page.wires.find((x) => x.id === wid);
    if (w) {
      const r = this.svg.getBoundingClientRect();
      this.view.s = Math.max(this.view.s, Math.min(6, r.width / 160));
      this.view.x = w.pts[0][0] - r.width / this.view.s / 2;
      this.view.y = w.pts[0][1] - r.height / this.view.s / 2;
    }
    this.applyView();
  }

  /* ================= macros ================= */
  private async loadMacros() {
    try {
      this.macros = (await api.macros()).macros;
    } catch {
      this.macros = [];
    }
    this.renderMacros();
  }

  private renderMacros() {
    const el = this.el.querySelector('#macros');
    if (!el) return;
    const item = (m: Editor['macros'][number]) =>
      `<div class="macro${this.tool === 'macro' && this.placeMacro?.name === m.data.name ? ' on' : ''}"><button class="mname" data-macro="${esc(m.id)}" title="${esc(m.desc)}">${m.data.pages?.length ? `<span class="pill">${$t('{0} sayfa', m.data.pages.length)}</span> ` : ''}${esc(m.name)}${m.data.sets?.length ? ` <span class="note">· ${$t('{0} varyant', m.data.sets.length)}</span>` : ''}</button>${m.mine ? `<button class="mdel" data-delmacro="${esc(m.id)}" title="Makroyu sil" aria-label="Makroyu sil">×</button>` : ''}</div>`;
    const shared = this.macros.filter((m) => !m.builtin && m.shared), own = this.macros.filter((m) => !m.builtin && !m.shared);
    el.innerHTML =
      `<div class="cat">${$t("Hazır")}</div>${this.macros.filter((m) => m.builtin).map(item).join('')}` +
      (shared.length ? `<div class="cat">${$t("Ortak kütüphane")}</div>${shared.map(item).join('')}` : '') +
      `<div class="cat">${$t("Firmamızın makroları")}</div>${own.map(item).join('') || `<p class="note">${$t("Birden fazla nesne seçip sağ panelden \"Makro olarak kaydet\" deyin.")}</p>`}`;
  }

  private startMacro(id: string) {
    const m = this.macros.find((x) => x.id === id);
    if (!m) return;
    const names = macroPlaceholders(m.data);
    if (names.length) return this.macroValuesDialog(m.data, names, (values) => this.useMacro(m, values));
    this.useMacro(m, {});
  }

  /** Asks for the placeholder values of a macro: pick a value set or type them. */
  private macroValuesDialog(m: Macro, names: string[], done: (values: Record<string, string>) => void) {
    const sets = m.sets ?? [];
    const close = openModal(
      `<h2>${esc(m.name)}</h2><p>${$t('Bu makroda değişken metinler var. Bir varyant seçin ya da değerleri yazın.')}</p>
      ${sets.length ? `<label class="fld"><span>${$t('Varyant')}</span><select id="vset"><option value="">${$t('— elle —')}</option>${sets.map((v, i) => `<option value="${i}">${esc(v.name)}</option>`).join('')}</select></label>` : ''}
      ${names.map((n, i) => `<label class="fld"><span>${esc(n)}</span><input data-ph="${i}" value="${esc(sets[0]?.values[n] ?? '')}"></label>`).join('')}
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${m.pages?.length ? $t('Sayfaları ekle') : $t('Yerleştir')}</button></div>`,
      (a, dlg) => {
        if (a !== 'ok') return;
        const values: Record<string, string> = {};
        dlg.querySelectorAll<HTMLInputElement>('input[data-ph]').forEach((inp) => (values[names[+inp.dataset.ph!]] = inp.value));
        close();
        done(values);
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal')!;
    const sel = dlg.querySelector<HTMLSelectElement>('#vset');
    if (sel) {
      sel.value = sets.length ? '0' : '';
      sel.onchange = () => {
        const v = sets[+sel.value];
        if (v) dlg.querySelectorAll<HTMLInputElement>('input[data-ph]').forEach((inp) => (inp.value = v.values[names[+inp.dataset.ph!]] ?? ''));
      };
    }
  }

  private useMacro(m: Editor['macros'][number], values: Record<string, string>) {
    if (m.data.pages?.length) {
      let at: number[] = [];
      this.mutate(() => (at = insertPageMacro(this.pr, m.data, this.cur, values)));
      if (at.length) {
        this.mode = 'sch';
        this.cur = at[0];
        this.sel.clear();
        this.renderAll();
        this.fit();
        void ensureParts(at.flatMap((i) => this.pr.pages[i].syms.map((s) => s.part)));
        toast($t('"{0}": {1} sayfa eklendi', m.name, at.length));
      }
      return;
    }
    if (this.mode !== 'sch') {
      this.mode = 'sch';
      this.renderAll();
    }
    this.placeMacro = m.data;
    this.macroValues = values;
    const items = [
      ...m.data.syms.filter((s) => LIB[s.type]).flatMap((s) => symbolItems(s, this.std)),
      ...m.data.wires.map((w) => ({ k: 'poly' as const, pts: w.pts, stroke: 'accent' as const, width: 0.35, layer: 'wire' as const })),
    ];
    this.macroGhost = itemsToSvg(items);
    this.setTool('macro');
    toast(m.desc ? m.desc.split('. ')[0] : $t("Yerleştirmek için sayfaya tıklayın"));
  }

  private saveMacroDialog() {
    const { S, W } = this.selItems();
    const close = openModal(
      `<h2>${$t("Makro olarak kaydet")}</h2><p>${$t("{0} sembol ve {1} bağlantı kaydedilecek. Bu sunucudaki herkes makroyu kullanabilir.", S.length, W.length)}</p>
      <label class="fld"><span>${$t("Ad")}</span><input id="mn" placeholder="${$t("ör. Pompa kumandası")}"></label>
      <label class="fld"><span>${$t("Açıklama")}</span><textarea id="md" rows="3" style="height:auto" placeholder="${$t("Nasıl bağlanacağını kısaca yazın")}"></textarea></label>
      ${this.platformAdmin ? `<label class="chkline"><input type="checkbox" id="mshared"> ${$t("Ortak kütüphaneye kaydet (tüm firmalar görür)")}</label>` : ''}
      <div class="err" id="merr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Kaydet")}</button></div>`,
      async (a, dlg) => {
        if (a !== 'ok') return;
        const name = qs<HTMLInputElement>(dlg, '#mn').value.trim();
        if (!name) return void (qs(dlg, '#merr').textContent = $t("Makroya bir ad verin."));
        try {
          await api.saveMacro({ ...makeMacro(name, qs<HTMLTextAreaElement>(dlg, '#md').value.trim(), S, W), ...(dlg.querySelector<HTMLInputElement>('#mshared')?.checked ? { shared: true } : {}) });
          close();
          toast($t("Makro kaydedildi"));
          void this.loadMacros();
        } catch (e) {
          qs(dlg, '#merr').textContent = (e as Error).message;
        }
      },
    );
  }

  /** Saves chosen pages as a page macro with value sets (variants) for its {{PLACEHOLDERS}}. */
  private savePageMacroDialog() {
    let sets: ValueSet[] = [];
    const close = openModal(
      `<div class="gen"><h2>${$t('Sayfa makrosu olarak kaydet')}</h2>
      <p>${$t('Seçtiğiniz sayfalar makroya girer. Metinlere {{GÜÇ}} gibi yer tutucular yazarsanız, her varyant için değerlerini aşağıda verebilirsiniz (ör. 4 kW, 7,5 kW, 11 kW). Makro eklenirken cihazlar yeni etiket alır.')}</p>
      <div class="tblw" style="max-height:24vh"><table class="t"><tbody>${this.pr.pages.map((p, i) => `<tr><td><label class="chkline" style="margin:0"><input type="checkbox" data-pp="${i}"${i === this.cur ? ' checked' : ''}> ${i + 1} · ${esc(p.desc || $t('Adsız sayfa'))}</label></td></tr>`).join('')}</tbody></table></div>
      <label class="fld"><span>${$t('Ad')}</span><input id="pmn" placeholder="${$t('ör. Pompa motoru komple')}"></label>
      <label class="fld"><span>${$t('Açıklama')}</span><input id="pmd"></label>
      <h3 style="margin:10px 0 4px">${$t('Varyantlar (değer kümeleri)')}</h3><div id="pmsets"></div>
      ${this.platformAdmin ? `<label class="chkline"><input type="checkbox" id="mshared"> ${$t("Ortak kütüphaneye kaydet (tüm firmalar görür)")}</label>` : ''}
      <div class="err" id="pmerr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn" data-m="addset">${$t('+ Varyant')}</button><button class="btn pri" data-m="ok">${$t('Kaydet')}</button></div></div>`,
      async (a, dlg) => {
        read();
        if (a === 'addset') {
          sets.push({ name: '', values: {} });
          return draw();
        }
        if (a !== 'ok') return;
        const pages = chosen();
        const name = qs<HTMLInputElement>(dlg, '#pmn').value.trim();
        if (!pages.length) return void (qs(dlg, '#pmerr').textContent = $t('En az bir sayfa seçin.'));
        if (!name) return void (qs(dlg, '#pmerr').textContent = $t("Makroya bir ad verin."));
        try {
          const m = makePageMacro(name, qs<HTMLInputElement>(dlg, '#pmd').value.trim(), pages, sets.filter((v) => v.name.trim()));
          await api.saveMacro({ ...m, ...(dlg.querySelector<HTMLInputElement>('#mshared')?.checked ? { shared: true } : {}) });
          close();
          toast($t("Makro kaydedildi"));
          void this.loadMacros();
        } catch (e) {
          qs(dlg, '#pmerr').textContent = (e as Error).message;
        }
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    const chosen = () => [...dlg.querySelectorAll<HTMLInputElement>('input[data-pp]')].filter((c) => c.checked).map((c) => this.pr.pages[+c.dataset.pp!]);
    const names = () => macroPlaceholders({ syms: [], wires: [], pages: chosen() });
    const read = () =>
      dlg.querySelectorAll<HTMLElement>('tr[data-set]').forEach((tr) => {
        const v = sets[+tr.dataset.set!];
        v.name = tr.querySelector<HTMLInputElement>('input[data-sn]')!.value;
        tr.querySelectorAll<HTMLInputElement>('input[data-sv]').forEach((inp) => (v.values[inp.dataset.sv!] = inp.value));
      });
    const draw = () => {
      const ns = names();
      qs(dlg, '#pmsets').innerHTML = ns.length
        ? `<div class="tblw"><table class="t"><thead><tr><th>${$t('Varyant adı')}</th>${ns.map((n) => `<th>${esc(n)}</th>`).join('')}</tr></thead><tbody>${sets
            .map((v, i) => `<tr data-set="${i}"><td><input data-sn value="${esc(v.name)}" placeholder="${$t('ör. 4 kW')}"></td>${ns.map((n) => `<td><input data-sv="${esc(n)}" value="${esc(v.values[n] ?? '')}"></td>`).join('')}</tr>`)
            .join('') || `<tr><td colspan="${ns.length + 1}" class="note">${$t('"+ Varyant" ile değer kümesi ekleyin.')}</td></tr>`}</tbody></table></div>`
        : `<p class="note">${$t('Seçili sayfalarda {{…}} yer tutucusu yok; makro olduğu gibi kaydedilir.')}</p>`;
    };
    dlg.addEventListener('change', (e) => {
      if ((e.target as HTMLElement).matches('input[data-pp]')) {
        read();
        draw();
      }
    });
    draw();
  }

  private deleteMacro(id: string) {
    const m = this.macros.find((x) => x.id === id);
    if (!m) return;
    const close = openModal(
      `<h2>${$t("\"{0}\" silinsin mi?", esc(m.name))}</h2><p>${$t("Makro listeden kaldırılır. Daha önce yerleştirilmiş kopyalar etkilenmez.")}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Makroyu sil")}</button></div>`,
      async (a) => {
        if (a !== 'ok') return;
        try {
          await api.deleteMacro(id);
          close();
          void this.loadMacros();
        } catch (e) {
          toast((e as Error).message);
        }
      },
    );
  }

  /* ================= find & replace ================= */
  private findDialog() {
    type Hit = { pi: number; s: Sym; field: string; label: string; value: string };
    const FIELDS: [string, string, (s: Sym) => string | undefined, (s: Sym, v: string) => void][] = [
      ['tag', 'Etiket', (s) => s.tag, (s, v) => (s.tag = normaliseTag(v))],
      ['desc', $t("Açıklama"), (s) => s.desc, (s, v) => (s.desc = v)],
      ['part', $t("Parça no"), (s) => s.part, (s, v) => (s.part = v)],
      ['text', 'Metin', (s) => s.props.text, (s, v) => (s.props.text = v)],
      ['name', $t("Potansiyel / kesinti adı"), (s) => s.props.name, (s, v) => (s.props.name = v)],
      ['addr', 'PLC adresi', (s) => s.props.addr, (s, v) => (s.props.addr = v)],
      ['sym', $t('PLC sembolü'), (s) => s.props.sym, (s, v) => (s.props.sym = v)],
    ];
    let hits: Hit[] = [];
    const run = (dlg: HTMLElement) => {
      const q = qs<HTMLInputElement>(dlg, '#fq').value.trim().toLocaleLowerCase('tr');
      const on = new Set([...dlg.querySelectorAll<HTMLInputElement>('[data-ff]:checked')].map((c) => c.dataset.ff));
      hits = [];
      if (q)
        this.pr.pages.forEach((p, pi) =>
          p.syms.forEach((s) => {
            for (const [k, label, get] of FIELDS) {
              const v = get(s);
              if (on.has(k) && v && v.toLocaleLowerCase('tr').includes(q)) hits.push({ pi, s, field: k, label, value: v });
            }
          }),
        );
      qs(dlg, '#fres').innerHTML = q
        ? hits.length
          ? hits.slice(0, 300).map((h, i) => `<button class="navrow" data-hit="${i}"><span><b>${esc(deviceName(this.A, h.s))}</b> ${esc(h.label)}: ${esc(h.value)}</span><span class="m">/${loc({ s: h.s, pi: h.pi })}</span></button>`).join('')
          : `<p class="note">${$t("Eşleşme yok.")}</p>`
        : '';
      qs(dlg, '#fcount').textContent = q ? $t("{0} eşleşme", hits.length) : '';
    };
    const close = openModal(
      `<h2>${$t("Bul ve değiştir")}</h2>
      <label class="fld"><span>${$t("Aranan")}</span><input id="fq" autocomplete="off" placeholder="${$t("ör. K1, Pompa, KT-09")}"></label>
      <div class="chkrow">${FIELDS.map(([k, label]) => `<label class="chkline"><input type="checkbox" data-ff="${k}"${k === 'tag' || k === 'desc' || k === 'text' ? ' checked' : ''}> ${label}</label>`).join('')}</div>
      <div class="navtree" id="fres" style="max-height:260px;overflow:auto;margin:8px 0"></div>
      <p class="note" id="fcount"></p>
      <label class="fld"><span>${$t("Yerine")}</span><input id="fr" autocomplete="off"></label>
      <div class="acts"><button class="btn" data-m="close">${$t("Kapat")}</button><button class="btn pri" data-m="replace">${$t("Tümünü değiştir")}</button></div>`,
      (a, dlg) => {
        if (a !== 'replace' || !hits.length) return;
        const q = qs<HTMLInputElement>(dlg, '#fq').value.trim(), rep = qs<HTMLInputElement>(dlg, '#fr').value;
        const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        const n = hits.length;
        this.mutate(() => {
          for (const h of hits) {
            const f = FIELDS.find((x) => x[0] === h.field)!;
            f[3](h.s, (f[2](h.s) ?? '').replace(re, rep));
          }
        });
        toast($t("{0} alan değiştirildi", n));
        run(dlg);
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .dlg')!;
    dlg.addEventListener('input', () => run(dlg));
    dlg.addEventListener('change', () => run(dlg));
    dlg.addEventListener('click', (e) => {
      const i = (e.target as HTMLElement).closest<HTMLElement>('[data-hit]')?.dataset.hit;
      if (i == null) return;
      const h = hits[+i];
      close();
      this.focusOn(h.pi, h.s.id);
    });
  }

  /** Part picker fed by the server parts database (cached per symbol type). */
  private partField(s: Sym) {
    // an imported symbol matched to a library one lists that one's parts
    const type = fnOf(s.type), list = partsForType(type);
    if (!list) {
      void loadType(type).then(() => this.renderProps());
      return fld($t("Parça no"), `<select disabled><option>${$t("Parçalar yükleniyor…")}</option></select>`);
    }
    const opts: [string, string][] = [['', $t("— seçilmedi —")], ...list.map((c): [string, string] => [c.pn, `${c.pn} · ${partText(c)}`])];
    if (s.part && !list.some((c) => c.pn === s.part)) opts.push([s.part, s.part]);
    const p = partOf(s.part);
    const cap = p && (p.no != null || p.nc != null) ? $t(" · kapasite {0} NO + {1} NC", p.no ?? 0, p.nc ?? 0) : '';
    return fld($t("Parça no"), sel('s.part', s.part, opts)) + (p ? `<p class="note" style="margin:-4px 0 9px">${esc(partText(p))}${cap}</p>` : '');
  }

  /* ================= editing operations ================= */
  private setTool(t: Tool, type: string | null = null) {
    this.tool = t;
    this.placeType = t === 'place' ? type : null;
    if (t !== 'place') this.placeRot = 0;
    this.draft = [];
    this.renderTools();
    this.renderLib();
    this.renderMacros();
    this.renderOverlay();
    this.renderStatus();
  }
  private wireSnap(w: Pt): { p: Pt; kind: 'pin' | 'wire' | 'grid' } {
    const tol = Math.max(1.2, 9 / this.view.s);
    let best: { p: Pt; kind: 'pin' | 'wire' } | null = null, bd = tol;
    for (const s of this.page.syms)
      for (const p of pinsW(s)) {
        const d = Math.hypot(p[0] - w[0], p[1] - w[1]);
        if (d < bd) {
          bd = d;
          best = { p, kind: 'pin' };
        }
      }
    if (best) return best;
    for (const x of this.page.wires) {
      for (const p of x.pts) {
        const d = Math.hypot(p[0] - w[0], p[1] - w[1]);
        if (d < bd) {
          bd = d;
          best = { p: [p[0], p[1]], kind: 'wire' };
        }
      }
      for (let i = 0; i < x.pts.length - 1; i++) {
        const a = x.pts[i], b = x.pts[i + 1];
        if (a[0] === b[0]) {
          const d = Math.abs(w[0] - a[0]), y = snapV(w[1]);
          if (d < bd && y > Math.min(a[1], b[1]) && y < Math.max(a[1], b[1])) {
            bd = d;
            best = { p: [a[0], y], kind: 'wire' };
          }
        } else if (a[1] === b[1]) {
          const d = Math.abs(w[1] - a[1]), xx = snapV(w[0]);
          if (d < bd && xx > Math.min(a[0], b[0]) && xx < Math.max(a[0], b[0])) {
            bd = d;
            best = { p: [xx, a[1]], kind: 'wire' };
          }
        }
      }
    }
    return best || { p: [snapV(w[0]), snapV(w[1])], kind: 'grid' };
  }
  private wireClick(w: Pt, shift: boolean) {
    const t = this.wireSnap(w);
    if (!this.draft.length) {
      this.draft = [t.p];
      return this.renderOverlay();
    }
    const last = this.draft[this.draft.length - 1];
    if (last[0] === t.p[0] && last[1] === t.p[1]) {
      if (this.draft.length > 1) this.finishWire();
      return;
    }
    this.draft.push(...route(last, t.p, shift));
    if (t.kind !== 'grid') this.finishWire();
    else this.renderOverlay();
  }
  private finishWire() {
    const pts = cleanPolyline(this.draft);
    this.draft = [];
    if (pts.length >= 2) this.mutate(() => this.page.wires.push({ id: newId(), pts, cs: '', col: '' }));
    else this.renderOverlay();
  }
  private placeAt(p: Pt) {
    this.mutate(() => {
      const s = createSymbol(this.pr, this.page, this.placeType!, p[0], p[1], this.placeRot, this.mem);
      this.page.syms.push(s);
      this.sel.clear();
      this.sel.add('s:' + s.id);
    });
  }
  private delSel() {
    if (!this.sel.size) return;
    if (this.page.kind === 'layout') {
      const l = this.page.layout!;
      return this.mutate(() => {
        l.items = l.items.filter((x) => !this.sel.has('L:i:' + x.id));
        l.rails = l.rails.filter((x) => !this.sel.has('L:r:' + x.id));
        l.ducts = l.ducts.filter((x) => !this.sel.has('L:d:' + x.id));
        this.sel.clear();
      });
    }
    this.mutate(() => {
      const pg = this.page;
      pg.syms = pg.syms.filter((s) => !this.sel.has('s:' + s.id));
      pg.wires = pg.wires.filter((w) => !this.sel.has('w:' + w.id));
      this.sel.clear();
    });
  }
  private rotSel() {
    if (this.page.kind === 'layout') return;
    if (this.tool === 'place') {
      this.placeRot = (this.placeRot + 1) % 4;
      return this.renderOverlay();
    }
    const { S } = this.selItems();
    if (!S.length) return;
    this.mutate(() => S.forEach((s) => LIB[s.type].role !== 'text' && (s.rot = (s.rot + 1) % 4)));
  }
  private copySel() {
    const { S, W } = this.selItems();
    if (!S.length && !W.length) return false;
    this.clip = JSON.parse(JSON.stringify({ S, W }));
    return true;
  }
  private paste(at: Pt | null) {
    if (this.page.kind === 'report') return;
    const c = this.clip;
    if (!c) return;
    const xs = [...c.S.map((s) => s.x), ...c.W.flatMap((w) => w.pts.map((p) => p[0]))];
    const ys = [...c.S.map((s) => s.y), ...c.W.flatMap((w) => w.pts.map((p) => p[1]))];
    const dx = at ? snapV(at[0]) - Math.min(...xs) : 8, dy = at ? snapV(at[1]) - Math.min(...ys) : 8;
    this.mutate(() => {
      this.sel.clear();
      for (const s0 of c.S) {
        const s: Sym = JSON.parse(JSON.stringify(s0));
        s.id = newId();
        s.x += dx;
        s.y += dy;
        const L = LIB[s.type];
        if (L.role === 'main') s.tag = nextTag(this.pr, prefixFor(s.type, this.std));
        if (L.role === 'term') s.props.pinNo = nextTerminal(this.pr, s.tag);
        this.page.syms.push(s);
        this.sel.add('s:' + s.id);
      }
      for (const w0 of c.W) {
        const w: Wire = { ...w0, id: newId(), pts: w0.pts.map((p): Pt => [p[0] + dx, p[1] + dy]) };
        this.page.wires.push(w);
        this.sel.add('w:' + w.id);
      }
    });
  }
  private gotoPage(i: number) {
    if (i < 0 || i >= this.pr.pages.length) return;
    this.cur = i;
    this.sel.clear();
    this.draft = [];
    this.renderAll();
  }
  /** Label list for label printers: pick what to label, preview, download as CSV or plain text. */
  private marksDialog() {
    const KEY = 'enercad.marks';
    let o: MarkOptions = { wires: true, wireText: 'no', terminals: true, devices: true, cables: true, merge: false };
    try {
      o = { ...o, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
    } catch {
      /* defaults */
    }
    const chk = (k: keyof MarkOptions, label: string) => `<label class="chkline"><input type="checkbox" data-o="${k}"${o[k] ? ' checked' : ''}> ${label}</label>`;
    const close = openModal(
      `<div class="gen marks"><h2>${$t('Etiket listesi')}</h2>
      <p class="note">${$t('Etiket yazıcınızın programına (ör. Phoenix Contact clipPROJECT, Weidmüller M-Print PRO, WAGO smartSCRIPT, Brady, Brother) aktarmak için. CSV Excel\'de açılır; metin dosyasında her satır bir etikettir.')}</p>
      <div class="row2"><div>${chk('wires', $t('Tel etiketleri (her telin iki ucu)'))}
        <label class="fld"><span>${$t('Tel etiketi metni')}</span><select data-o="wireText"><option value="no"${o.wireText === 'no' ? ' selected' : ''}>${$t('Yalnızca tel numarası')}</option><option value="dest"${o.wireText === 'dest' ? ' selected' : ''}>${$t('Tel numarası + karşı uç (ör. 12 -X1:5)')}</option></select></label></div>
      <div>${chk('terminals', $t('Klemens etiketleri (dizi adı ve numaralar)'))}${chk('devices', $t('Cihaz etiketleri'))}${chk('cables', $t('Kablo etiketleri (her kablonun iki ucu)'))}${chk('merge', $t('Aynı metinleri birleştir (adet sütunu)'))}</div></div>
      <div id="mkprev"></div>
      <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button><button class="btn" data-m="txt">${$t('Metin (.txt)')}</button><button class="btn pri" data-m="csv">${$t('CSV indir')}</button></div></div>`,
      (a) => {
        const marks = markingList(this.pr, this.A, o);
        if (!marks.length) return toast($t('Seçilen türlerde etiket yok.'));
        const name = `${safeFileName(this.pr.name)} - ${$t('Etiket listesi')}`;
        if (a === 'csv') downloadFile(name + '.csv', '\ufeff' + marksToCsv(marks), 'text/csv;charset=utf-8');
        if (a === 'txt') downloadFile(name + '.txt', '\ufeff' + marksToText(marks), 'text/plain;charset=utf-8');
        if (a === 'csv' || a === 'txt') close();
      },
    );
    const dlg = document.querySelector<HTMLElement>('.marks')!;
    const preview = () => {
      const marks = markingList(this.pr, this.A, o);
      const count = (k: string) => marks.filter((x) => x.kind === k).reduce((s, x) => s + x.n, 0);
      qs(dlg, '#mkprev').innerHTML = `<p class="note">${(Object.keys(MARK_KINDS) as (keyof typeof MARK_KINDS)[]).filter((k) => count(k)).map((k) => `${$t(MARK_KINDS[k])}: <b>${count(k)}</b>`).join(' · ') || $t('Seçilen türlerde etiket yok.')}</p>
        ${marks.length ? `<div class="tblw" style="max-height:34vh"><table class="t"><thead><tr><th>${$t('Tür')}</th><th>${$t('Metin')}</th><th>${$t('Grup')}</th><th>${$t('Adet')}</th></tr></thead><tbody>${marks.slice(0, 200).map((x) => `<tr><td>${$t(MARK_KINDS[x.kind])}</td><td class="m">${esc(x.text)}</td><td>${esc(x.group)}</td><td class="m">${x.n}</td></tr>`).join('')}</tbody></table></div>${marks.length > 200 ? `<p class="note">${$t('İlk 200 satır gösteriliyor; dosyada hepsi var ({0}).', marks.length)}</p>` : ''}` : ''}`;
    };
    dlg.addEventListener('change', (e) => {
      const t = e.target as HTMLInputElement | HTMLSelectElement;
      const k = t.dataset.o as keyof MarkOptions | undefined;
      if (!k) return;
      o = { ...o, [k]: t instanceof HTMLInputElement && t.type === 'checkbox' ? t.checked : t.value };
      try {
        localStorage.setItem(KEY, JSON.stringify(o));
      } catch {
        /* not remembered */
      }
      preview();
    });
    preview();
  }

  /**
   * Asks the AI how to fix one finding. The answer is a list of checked operations the
   * engineer ticks, plus manual steps; the ticked operations are applied as one undo step.
   */
  private aiFixDialog(f: AiFinding, onDone: (msg: string) => void) {
    let ops: AiOp[] = [];
    const close = openModal(
      `<div class="gen aifix"><h2>${$t('Düzeltme önerisi')}</h2>
      <p><b>${esc(f.title)}</b><br><span class="note">${esc(f.detail)}</span></p>
      <div id="afx"><p class="note">${$t('Yapay zeka düzeltme önerisi hazırlıyor…')}</p></div>
      <div class="err" id="afxerr"></div>
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="apply" disabled>${$t('Seçilenleri uygula')}</button></div></div>`,
      (a, d) => {
        if (a !== 'apply') return;
        const picked = ops.filter((_, i) => d.querySelector<HTMLInputElement>(`input[data-op="${i}"]`)?.checked);
        if (!picked.length) return toast($t('Uygulanacak bir işlem seçilmedi.'));
        let r: { done: string[]; skipped: string[] } = { done: [], skipped: [] };
        this.mutate(() => {
          r = applyAiOps(this.pr, picked);
          this.sel.clear();
          this.cur = Math.min(this.cur, this.pr.pages.length - 1);
        });
        if (!r.done.length && !r.skipped.length) return; // refused by mutate (read-only, locked page)
        close();
        toast($t('{0} değişiklik uygulandı; Geri ile tek adımda geri alınır.', r.done.length) + (r.skipped.length ? ' ' + $t('{0} işlem atlandı: {1}', r.skipped.length, r.skipped.join('; ')) : ''));
        onDone($t('Uygulandı'));
      },
    );
    const dlg = document.querySelector<HTMLElement>('.aifix');
    const run = async () => {
      if (this.rev !== this.savedRev) {
        clearTimeout(this.saveTimer);
        await this.flush();
        if (this.rev !== this.savedRev) throw new Error($t("Önce proje kaydedilmeli; kayıt başarısız oldu."));
      }
      return api.aiFix(this.id, f);
    };
    void run().then(
      (r) => {
        if (!dlg?.isConnected) return;
        ops = r.ops;
        qs(dlg, '#afx').innerHTML = `<p>${esc(r.explanation)}</p>
          ${ops.length ? `<h3>${$t('Otomatik yapılabilecek değişiklikler')}</h3>${ops.map((o, i) => `<label class="chkline"><input type="checkbox" data-op="${i}" checked> ${esc(describeAiOp(o))}</label>`).join('')}` : `<p class="note">${$t('Bu bulgu otomatik değişiklikle düzeltilemiyor.')}</p>`}
          ${r.manual.length ? `<h3>${$t('Elle yapmanız gerekenler')}</h3><ol class="conn">${r.manual.map((x) => `<li>${esc(x)}</li>`).join('')}</ol>` : ''}
          <p class="note">${$t('Yapay zeka hata yapabilir; uygulamadan önce her değişikliği kontrol edin.')}</p>`;
        qs<HTMLButtonElement>(dlg, '[data-m="apply"]').disabled = !ops.length;
      },
      (e) => {
        if (!dlg?.isConnected) return;
        qs(dlg, '#afx').innerHTML = '';
        qs(dlg, '#afxerr').textContent = (e as Error).message;
      },
    );
  }

  /** Sends the saved project to the AI for an engineering review and lists the findings; clicking one jumps to the device or page. */
  private aiReviewDialog() {
    const SEV: Record<string, [string, string]> = { error: ['err', tk('Hata')], warning: ['warn', tk('Uyarı')], suggestion: ['ok', tk('Öneri')] };
    type Target = [number, string];
    let review: AiReview | null = null;
    // per finding: the devices it names (label + where it is) and the page as a fallback
    let links: { tags: [string, Target | null][]; page: Target | null }[] = [];
    const go = (t: Target) => {
      close();
      if (t[1]) return this.focusOn(t[0], t[1]);
      this.mode = 'sch';
      this.cur = t[0];
      this.sel.clear();
      this.renderAll();
    };
    const table = () => {
      const R: ReportTable = {
        title: $t('Yapay zeka ile gözden geçir'), sub: '', mono: [],
        cols: [$t('Durum'), $t('Cihaz'), $t('Sayfa'), $t('Başlık'), $t('Açıklama')],
        rows: (review?.findings ?? []).map((f) => ({ cells: [$t(SEV[f.severity][1]), f.tag, f.page ? String(f.page) : '', f.title, f.detail] })),
      };
      return R;
    };
    const close = openModal(
      `<div class="gen aireview"><h2>${$t('Yapay zeka ile gözden geçir')}</h2>
      <p class="note">${$t('Proje kaydedilir; cihazlar, kablolar, klemensler, PLC listesi ve kontrol sonuçları yapay zekaya gönderilir. Projede hiçbir şey değiştirilmez.')}</p>
      <div id="airv"><p class="note">${$t('Yapay zeka projeyi inceliyor… (bu bir dakika kadar sürebilir)')}</p></div>
      <div class="err" id="airverr"></div>
      <div class="acts"><button class="btn" data-m="copy" hidden>${$t('Kopyala')}</button><button class="btn" data-m="csv" hidden>${$t('CSV indir')}</button><button class="btn" data-m="close">${$t('Kapat')}</button></div></div>`,
      (a) => {
        if (a === 'copy' && review) {
          const R = table();
          const text = [review.summary, '', R.cols.join('\t'), ...R.rows.map((r) => r.cells.join('\t'))].join('\n');
          return void navigator.clipboard.writeText(text).then(() => toast($t('Panoya kopyalandı')), () => toast($t('Kopyalanamadı')));
        }
        if (a === 'csv' && review) return downloadFile(`${safeFileName(this.pr.name)} - ${$t('Yapay zeka ile gözden geçir')}.csv`, '\ufeff' + reportToCsv(table()), 'text/csv;charset=utf-8');
        if (a.startsWith('fix:') && review) return this.aiFixDialog(review.findings[+a.slice(4)], (msg) => {
          const b = dlg?.querySelector<HTMLButtonElement>(`[data-m="${a}"]`);
          if (b) {
            b.textContent = msg;
            b.disabled = true;
          }
        });
        const m = /^go:(\d+)(?::(\d+))?$/.exec(a);
        const l = m ? links[+m[1]] : undefined;
        if (!l) return;
        const t = m![2] !== undefined ? l.tags[+m![2]]?.[1] : (l.tags.find((x) => x[1])?.[1] ?? l.page);
        if (t) go(t);
      },
    );
    const dlg = document.querySelector<HTMLElement>('.aireview');
    const norm = (t: string) => t.replace(/^-/, '').trim().toUpperCase();
    // a finding names devices by their tags as the reports show them; prefer the symbol on the page it names
    const find = (tag: string, page: number): Target | null => {
      const hits = this.A.all.filter((it) => norm(this.A.devLabel.get(it.s.id) ?? it.s.tag ?? '') === norm(tag));
      const hit = hits.find((it) => it.pi === page - 1) ?? hits[0];
      return hit ? [hit.pi, hit.s.id] : null;
    };
    const run = async () => {
      if (this.rev !== this.savedRev) {
        clearTimeout(this.saveTimer);
        await this.flush();
        if (this.rev !== this.savedRev) throw new Error($t("Önce proje kaydedilmeli; kayıt başarısız oldu."));
      }
      return api.aiReview(this.id);
    };
    void run().then(
      (r) => {
        if (!dlg?.isConnected) return;
        review = r;
        links = r.findings.map((f) => ({
          tags: f.tag.split(/[,;\s]+/).filter(Boolean).slice(0, 12).map((t): [string, Target | null] => [t, find(t, f.page)]),
          page: f.page >= 1 && f.page <= this.pr.pages.length ? [f.page - 1, ''] : null,
        }));
        const clickable = (i: number) => links[i].page || links[i].tags.some((x) => x[1]);
        qs(dlg, '#airv').innerHTML = `<p>${esc(r.summary)}</p>
          ${r.findings.length ? `<div class="tblw" style="max-height:52vh"><table class="t"><tbody>${r.findings.map((f, i) => `<tr${clickable(i) ? ` data-m="go:${i}" style="cursor:pointer" title="${$t('Göster')}"` : ''}>
            <td><span class="pill ${SEV[f.severity][0]}">${$t(SEV[f.severity][1])}</span></td>
            <td class="m">${links[i].tags.map(([t, tg], j) => (tg ? `<button class="btn" data-m="go:${i}:${j}" style="padding:1px 6px;margin:1px">${esc(t)}</button>` : esc(t))).join(' ')}${f.page ? `<br><span class="note">${$t('Sayfa {0}', f.page)}</span>` : ''}</td>
            <td><b>${esc(f.title)}</b><br>${esc(f.detail)}</td>
            <td>${this.readOnly ? '' : `<button class="btn" data-m="fix:${i}" title="${$t('Yapay zekadan bu bulgu için düzeltme önerisi iste')}">${$t('Düzelt')}</button>`}</td></tr>`).join('')}</tbody></table></div>` : `<p class="note">${$t('Yapay zeka önemli bir sorun bulmadı.')}</p>`}
          <p class="note">${$t('Yapay zeka hata yapabilir; bulguları kendi mühendislik kararınızla değerlendirin.')}</p>`;
        if (r.findings.length) dlg.querySelectorAll<HTMLElement>('[data-m="copy"],[data-m="csv"]').forEach((b) => (b.hidden = false));
      },
      (e) => {
        if (!dlg?.isConnected) return;
        qs(dlg, '#airv').innerHTML = '';
        qs(dlg, '#airverr').textContent = (e as Error).message;
      },
    );
  }

  private focusOn(pi: number, sid: string) {
    this.mode = 'sch';
    this.cur = pi;
    this.sel.clear();
    this.sel.add('s:' + sid);
    const s = this.page.syms.find((x) => x.id === sid);
    this.renderAll();
    if (s) {
      const r = this.svg.getBoundingClientRect();
      this.view.s = Math.max(this.view.s, Math.min(8, r.width / 110));
      this.view.x = s.x - r.width / this.view.s / 2;
      this.view.y = s.y - r.height / this.view.s / 2;
    }
    this.applyView();
  }

  /* moving selected symbols drags attached wire ends along (rubber-banding) */
  private startMove(w: Pt) {
    const { S, W } = this.selItems(), pinKeys = new Set<string>();
    S.forEach((s) => pinsW(s).forEach((p) => pinKeys.add(kxy(p[0], p[1]))));
    const full = W.map((x) => ({ w: x, o: x.pts.map((p): Pt => [p[0], p[1]]) }));
    const att: MoveDrag['att'] = [];
    for (const x of this.page.wires) {
      if (this.sel.has('w:' + x.id)) continue;
      const n = x.pts.length, e0 = pinKeys.has(kxy(...x.pts[0])), e1 = pinKeys.has(kxy(...x.pts[n - 1]));
      const o = x.pts.map((p): Pt => [p[0], p[1]]);
      if (e0 && e1) full.push({ w: x, o });
      else if (e0 || e1) att.push({ w: x, o, e: e0 ? 0 : n - 1 });
    }
    this.drag = { k: 'move', a: w, snap: this.snap(), S: S.map((s) => ({ s, x: s.x, y: s.y })), full, att, moved: false };
  }
  private applyMove(d: MoveDrag, dx: number, dy: number) {
    for (const m of d.S) {
      m.s.x = m.x + dx;
      m.s.y = m.y + dy;
    }
    for (const f of d.full) f.w.pts = f.o.map((p): Pt => [p[0] + dx, p[1] + dy]);
    for (const a of d.att) {
      const o = a.o, e = a.e, mv: Pt = [o[e][0] + dx, o[e][1] + dy];
      if (o.length === 2) {
        const other = o[1 - e], wasV = o[0][0] === o[1][0];
        if (mv[0] === other[0] || mv[1] === other[1]) {
          a.w.pts = e === 0 ? [mv, other] : [other, mv];
          continue;
        }
        let mid: Pt[];
        if (wasV) {
          const my = snapV((mv[1] + other[1]) / 2);
          mid = [[mv[0], my], [other[0], my]];
        } else {
          const mx = snapV((mv[0] + other[0]) / 2);
          mid = [[mx, mv[1]], [mx, other[1]]];
        }
        a.w.pts = e === 0 ? [mv, ...mid, other] : [other, mid[1], mid[0], mv];
        continue;
      }
      const pts = o.map((p): Pt => [p[0], p[1]]), n = e === 0 ? 1 : pts.length - 2, wasV = o[e][0] === o[n][0];
      pts[e] = mv;
      pts[n] = wasV ? [mv[0], pts[n][1]] : [pts[n][0], mv[1]];
      a.w.pts = pts;
    }
  }

  /* ================= parametric generation ================= */
  /** MCC board from a motor list, or a control board from a signal list (sensors, lamps, valves) without motors. */
  private generateDialog(mode: 'mcc' | 'control' = 'mcc') {
    const ctrl = mode === 'control';
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const example = [
      $t("Etiket\tAçıklama\tGüç (kW)\tYol verme\tKumanda\tKablo (m)"),
      $t("M1\tBesleme pompası\t1,5\tDirekt\tYerel\t25"),
      $t("M2\tKarıştırıcı\t4\tİleri-geri\tPLC\t40"),
      $t("M3\tFan\t15\tYıldız-üçgen\tPLC\t60"),
      $t("M4\tKonveyör\t5,5\tFrekans konvertörü\tPLC\t30"),
      $t("M5\tKompresör\t45\tSoft starter\tEl/Otomatik\t50"),
    ].join('\n');
    const ioExample = [
      $t('Etiket\tAçıklama\tTür'),
      $t('B1\tTank dolu\tŞalter NO'),
      $t('B2\tKuru çalışma\tŞalter NC'),
      $t('H1\tArıza lambası\tLamba'),
      $t('MB1\tDolum vanası\tSelenoid vana'),
      $t('PT1\tHat basıncı 0-10 bar\tTransmitter 4-20 mA'),
      $t('MB2\tDebi vanası\tOransal vana'),
    ].join('\n');
    const close = openModal(
      `<div class="gen"><h2>${ctrl ? $t('Kumanda panosu üret') : $t('MCC panosu üret · motor listesi')}</h2>
      <div${ctrl ? ' hidden' : ''}>
      <p>${$t("Excel'den sütunları kopyalayıp yapıştırın: <b>etiket, açıklama, güç (kW), yol verme, kumanda, kablo boyu (m)</b>. Yol verme: <i>{0}, {1}, {2}, {3}, {4}</i>. Kumanda: <i>{5}</i> (buton ve lambalar), <i>PLC</i> (ara röle, geri bildirimler), <i>{6}</i> (El-0-Otomatik seçici: elde butonlar, otomatikte şamandıra, presostat gibi bir kontak) ya da <i>{7}</i> (otomatikte PLC).", $t(STARTER_NAMES.dol), $t(STARTER_NAMES.rev), $t(STARTER_NAMES.yd), $t(STARTER_NAMES.vfd), $t(STARTER_NAMES.ss), $t(CONTROL_NAMES.local), $t(CONTROL_NAMES.auto), $t(CONTROL_NAMES.autoplc))}</p>
      <textarea id="gl" rows="7" spellcheck="false" style="width:100%;font:12px var(--mono);height:auto" placeholder="${esc(example)}"></textarea>
      <div class="btnrow" style="margin:6px 0 10px"><button class="btn" data-m="ex">${$t("Örnek listeyi yükle")}</button><span class="note" id="gs"></span></div>
      <p class="note">${$t('İsteğe bağlı 7. sütun <b>PLC hız ref.</b>: PLC kumandalı frekans konvertöründe "evet" yazarsanız hızı PLC analog çıkışı belirler. İsteğe bağlı 8. sütun <b>PTC</b>: sargılarında termistör olan motorda "evet" yazarsanız kontaktörün kumanda devresine termistör rölesi eklenir.')}</p>
      <div class="tblw" style="max-height:200px"><table class="t"><thead><tr><th>${$t("Motor")}</th><th>${$t("Açıklama")}</th><th>kW</th><th>In</th><th>${$t("Yol verme")}</th><th>${$t("Kumanda")}</th><th>${$t("Kablo")}</th><th>${$t('PLC hız ref.')}</th><th>PTC</th></tr></thead><tbody id="gp"></tbody></table></div>
      <div class="err" id="ge"></div>
      </div>
      <details class="struct" style="margin-top:8px"${ctrl ? ' open' : ''}><summary>${ctrl ? $t('Sinyal listesi (sensör, lamba, vana, transmitter)') : $t('PLC sinyalleri (sensör, lamba, vana) · isteğe bağlı')}</summary>
        <p class="note">${$t('Sütunlar: <b>etiket, açıklama, tür</b>. Tür: {0}. Etiket boşsa sıradaki numara verilir.', (Object.values(IO_NAMES) as string[]).map((n) => `<i>${esc($t(n))}</i>`).join(', '))}</p>
        <textarea id="gio" rows="4" spellcheck="false" style="width:100%;font:12px var(--mono);height:auto" placeholder="${esc(ioExample)}"></textarea>
        <div class="btnrow" style="margin:6px 0"><button class="btn" data-m="ioex">${$t("Örnek listeyi yükle")}</button><span class="note" id="gios"></span></div>
        <div class="err" id="gioe"></div>
      </details>
      <label class="fld" style="max-width:320px"><span>${$t('Acil stop butonu')}</span><input id="gest" type="number" min="0" max="6" value="0" title="${$t('0: acil stop devresi çizilmez. 1–6: güvenlik rölesi ile şablon acil stop devresi')}"></label>
      <label class="chkline"${ctrl ? ' hidden' : ''}><input type="checkbox" id="gsl"${ctrl ? '' : ' checked'}> ${$t("Tek hat şemasını da ekle")}</label>
      <label class="chkline"><input type="checkbox" id="gly" checked> ${$t('Pano yerleşimi ve kapak: cihazları plakaya ve kapağa otomatik yerleştir (2D ve 3D görünüm)')}</label>
      <p class="note">${$t("Sayfalar projenin sonuna eklenir: besleme (gerekirse), güç, 230 V kumanda ve 24 V PLC sayfaları. Etiketler, klemens numaraları ve PLC adresleri projedeki son değerlerden devam eder. Parçalar parça veritabanından değerlere göre seçilir; uygun parça yoksa uyarı verilir. İşlemi <b>Geri</b> ile geri alabilirsiniz.")}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok" id="gok" disabled>${$t("Sayfaları üret")}</button></div></div>`,
      async (a, dlg) => {
        const ta = qs<HTMLTextAreaElement>(dlg, '#gl');
        if (a === 'ex') {
          ta.value = example;
          return preview();
        }
        if (a === 'ioex') {
          qs<HTMLTextAreaElement>(dlg, '#gio').value = ioExample;
          return preview();
        }
        if (a !== 'ok') return;
        const { motors } = parseMotorList(ta.value);
        const { io } = parseIoList(qs<HTMLTextAreaElement>(dlg, '#gio').value);
        const estop = Math.max(0, Math.min(6, Math.round(Number(qs<HTMLInputElement>(dlg, '#gest').value) || 0)));
        if (!motors.length && !io.length) return;
        qs<HTMLButtonElement>(dlg, '#gok').disabled = true;
        const parts = await this.generatorParts();
        const r = generateMotorCircuits(this.pr, motors, { parts, io, estop });
        const withLayout = qs<HTMLInputElement>(dlg, '#gly').checked;
        if (qs<HTMLInputElement>(dlg, '#gsl').checked) {
          const tmp = { ...this.pr, pages: r.pages };
          r.pages.push(...singleLinePages(tmp, analyze(tmp)));
        }
        close();
        const first = this.pr.pages.length;
        let placed = 0;
        this.mutate(() => {
          this.pr.pages.push(...r.pages);
          if (withLayout) placed = this.addPlates(ctrl ? $t('Kumanda panosu') : $t('MCC panosu'), r.warnings);
          this.cur = first;
          this.sel.clear();
          this.setTool('select');
        });
        void ensureParts(r.pages.flatMap((p) => p.syms.map((s) => s.part)));
        if (r.warnings.length)
          openModal(
            `<h2>${$t("{0} sayfa üretildi", r.pages.length + placed)}</h2><p>${$t("Gözden geçirmeniz gereken noktalar:")}</p><ul class="note">${r.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>
            <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
            () => {},
          );
        else toast($t("{0} sayfa üretildi", r.pages.length + placed));
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    const preview = () => {
      const { motors, errors } = parseMotorList(qs<HTMLTextAreaElement>(dlg, '#gl').value);
      qs(dlg, '#gp').innerHTML = motors
        .map(
          (m) => `<tr><td class="m">${esc(m.tag || '(otomatik)')}</td><td>${esc(m.desc)}</td><td class="m">${String(m.kw).replace('.', ',')}</td>
          <td class="m">${ratedCurrent(m.kw).toFixed(1).replace('.', ',')} A</td><td>${esc($t(STARTER_NAMES[m.kind]))}</td><td>${esc($t(CONTROL_NAMES[m.control]))}</td><td class="m">${m.cableLen ? `${m.cableLen} m` : '—'}</td><td>${m.speedRef ? '✓' : ''}</td><td>${m.ptc ? '✓' : ''}</td></tr>`,
        )
        .join('');
      qs(dlg, '#ge').textContent = errors.join(' · ');
      qs(dlg, '#gs').textContent = motors.length ? $t("{0} motor", motors.length) : '';
      const sig = parseIoList(qs<HTMLTextAreaElement>(dlg, '#gio').value);
      qs(dlg, '#gioe').textContent = sig.errors.join(' · ');
      qs(dlg, '#gios').textContent = sig.io.length ? $t('{0} sinyal', sig.io.length) : '';
      qs<HTMLButtonElement>(dlg, '#gok').disabled = !motors.length && !sig.io.length;
    };
    qs<HTMLTextAreaElement>(dlg, '#gl').addEventListener('input', preview);
    qs<HTMLTextAreaElement>(dlg, '#gio').addEventListener('input', preview);
    qs<HTMLTextAreaElement>(dlg, ctrl ? '#gio' : '#gl').focus();
  }

  /**
   * Mounting plate (and, when the project has buttons, lamps or meters for it, a door) for the devices not on a
   * layout page yet; returns the number of pages added.
   */
  private addPlates(name: string, warnings: string[]): number {
    // one plate, or several cabinets side by side when the devices do not fit in one
    const { pages, skipped } = addPlates(this.pr, partOf);
    pages.forEach((p, i) => (p.desc = pages.length > 1 ? $t('{0} · yerleşim {1}/{2}', name, i + 1, pages.length) : $t('{0} · yerleşim', name)));
    if (skipped.length) warnings.push($t('Pano yerleşimine sığmayan cihazlar: {0}', skipped.join(', ')));
    // the doors: the size of their cabinet, one per cabinet when the devices need it
    const doors = addDoors(this.pr, pages.map((p) => p.layout!), partOf), nd = doors.pages.length;
    doors.pages.forEach(({ front, inside }, i) => {
      front.desc = nd > 1 ? $t('{0} · kapak ön görünüş {1}/{2}', name, i + 1, nd) : $t('{0} · kapak ön görünüş', name);
      inside.desc = nd > 1 ? $t('{0} · kapak iç görünüş {1}/{2}', name, i + 1, nd) : $t('{0} · kapak iç görünüş', name);
    });
    if (doors.skipped.length) warnings.push($t('Kapağa sığmayan cihazlar: {0}', doors.skipped.join(', ')));
    const A = analyze(this.pr, partOf), placed = new Set(this.pr.pages.flatMap((p) => p.layout?.items.map((i) => i.dev) ?? []));
    // devices laid out with a typical size
    const guessed = [...layoutDevices(A, partOf).values()].filter((d) => placed.has(d.key) && !d.sized && !d.strip).map((d) => d.name);
    if (guessed.length)
      warnings.push($t('{0} cihazın ölçüsü parça veritabanında yok; tipik ölçüyle yerleştirildi. Parçaları seçip yerleşimi yeniden yapın: {1}', guessed.length, guessed.slice(0, 15).join(', ') + (guessed.length > 15 ? ' …' : '')));
    return pages.length + 2 * nd;
  }

  /** Power factor correction board: incomer, capacitor steps with fuses and contactors, reactive power relay. */
  private compensationDialog() {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const SIZES = [63, 100, 125, 160, 250, 400, 630, 800, 1000, 1250, 1600];
    const close = openModal(
      `<div class="gen"><h2>${$t('Kompanzasyon panosu üret')}</h2>
      <label class="fld"><span>${$t('Kademeler (kvar)')}</span><input id="ks" value="25, 2×50, 3×100" placeholder="25, 2×50, 3×100"></label>
      <div id="ksum" class="note"></div>
      <div class="btnrow">
        <label class="fld"><span>${$t('Giriş şalteri')}</span><select id="kk"><option value="mccb">${$t(INCOMER_NAMES.mccb)}</option><option value="lbs">${$t(INCOMER_NAMES.lbs)}</option><option value="acb">${$t(INCOMER_NAMES.acb)}</option></select></label>
        <label class="fld"><span>${$t('Akım (A)')}</span><input id="ka" type="number" min="16" max="${MAX_INCOMER}" style="width:100px"></label>
      </div>
      <label class="fld"><span>${$t('Üretici')}</span><select id="kmk"><option value="">${$t('Tümü (en uygun parça)')}</option></select></label>
      <label class="chkline"><input type="checkbox" id="km" checked> ${$t('Ölçüm (akım trafoları ve enerji analizörü)')}</label>
      <label class="chkline"><input type="checkbox" id="kp"> ${$t('Parafudr')}</label>
      <label class="chkline"><input type="checkbox" id="kl" checked> ${$t('Pano yerleşimi: cihazları otomatik yerleştir (2D ve 3D görünüm)')}</label>
      <div class="err" id="ke"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok" id="kok">${$t("Sayfaları üret")}</button></div></div>`,
      async (a, dlg) => {
        if (a !== 'ok') return;
        const { steps, error } = parseSteps(qs<HTMLInputElement>(dlg, '#ks').value);
        if (error || !steps.length) return void (qs(dlg, '#ke').textContent = error || $t('En az bir kademe girin.'));
        const amps = Math.round(Number(qs<HTMLInputElement>(dlg, '#ka').value) || 0);
        if (!(amps >= 16)) return void (qs(dlg, '#ke').textContent = $t('Giriş akımını girin.'));
        qs<HTMLButtonElement>(dlg, '#kok').disabled = true;
        const kind = qs<HTMLSelectElement>(dlg, '#kk').value as IncomerKind;
        const parts = await this.generatorParts(GEN_TYPES, qs<HTMLSelectElement>(dlg, '#kmk').value);
        const r = generateDistribution(this.pr, { kind, amps, meter: qs<HTMLInputElement>(dlg, '#km').checked, spd: qs<HTMLInputElement>(dlg, '#kp').checked, ats: false, comp: steps }, [], { parts, fallback: this.generatorFallback(GEN_TYPES) });
        const withLayout = qs<HTMLInputElement>(dlg, '#kl').checked;
        close();
        const first = this.pr.pages.length;
        let placed = 0;
        this.mutate(() => {
          this.pr.pages.push(...r.pages);
          if (withLayout) {
            const { result } = addBoardLayout(this.pr, amps, partOf);
            placed = 1;
            if (result.skipped.length) r.warnings.push($t('Pano yerleşimine sığmayan cihazlar: {0}', result.skipped.join(', ')));
          }
          this.cur = first;
          this.sel.clear();
          this.setTool('select');
        });
        void ensureParts(r.pages.flatMap((p) => p.syms.map((s) => s.part)));
        openModal(
          `<h2>${$t("{0} sayfa üretildi", r.pages.length + placed)}</h2>${r.warnings.length ? `<p>${$t("Gözden geçirmeniz gereken noktalar:")}</p><ul class="note">${r.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>` : ''}
          <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
          () => {},
        );
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    let touched = false;
    const update = () => {
      const { steps, error } = parseSteps(qs<HTMLInputElement>(dlg, '#ks').value);
      const kvar = steps.reduce((a, b) => a + b, 0), I = capCurrent(kvar);
      qs(dlg, '#ksum').textContent = error || (steps.length ? $t('{0} kademe · {1} kvar · {2} A (400 V)', steps.length, String(Math.round(kvar * 10) / 10).replace('.', ','), Math.round(I)) : '');
      // the incomer: 1.43 × the capacitor current (overvoltage, harmonics, tolerance), next standard size
      if (!touched && steps.length) qs<HTMLInputElement>(dlg, '#ka').value = String(SIZES.find((s) => s >= I * 1.43) ?? MAX_INCOMER);
    };
    qs<HTMLInputElement>(dlg, '#ks').addEventListener('input', update);
    qs<HTMLInputElement>(dlg, '#ka').addEventListener('input', () => (touched = true));
    void this.fillMakers(qs<HTMLSelectElement>(dlg, '#kmk'));
    update();
    qs<HTMLInputElement>(dlg, '#ks').focus();
  }

  /** Distribution board: incomer settings and a feeder list pasted from Excel become incomer and feeder pages. */
  /** Power board with busbar sections, several sources and couplers, described as text (see parseBoardText). */
  private boardDialog(pre?: { text: string; title: string; warnings?: string[] }) {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const exAdp = [
      'Pano: Ana dağıtım panosu',
      'Bölüm A',
      'GİRİŞ TR1; trafo; 2000 A; ACB; 4P; ölçüm; 1000 kVA 22/0,4 kV',
      'GİRİŞ SEN-1; hat; 2000 A; ACB; 4P; ölçüm',
      'Q11; TR1 çıkış busbar; 2000 A; 4P; bara kanalı',
      'Q12; Mevcut pano; 1000 A; 3P; 80 m',
      'Q13; Mevcut pano; 630 A; 3P; 60 m',
      'Q14; Kompanzasyon panosu; 800 A; 3P; 10 m',
      'KUPLAJ 2000 A; ACB; 4P',
      'Bölüm B',
      'GİRİŞ TR2; trafo; 2000 A; ACB; 4P; ölçüm; 1000 kVA 22/0,4 kV',
      'GİRİŞ SEN-2; hat; 2000 A; ACB; 4P; ölçüm',
      'Q21; TR2 çıkış busbar; 2000 A; 4P; bara kanalı',
      'Q22; Çıkış; 630 A; 3P; 60 m',
      'Q23; Kompanzasyon panosu; 800 A; 3P; 10 m',
    ].join('\n');
    const exSync = ['Pano: Senkron panosu', 'Senkron', 'GİRİŞ G1; jeneratör; 1600 A; ACB; 4P; 1100 kVA', 'GİRİŞ G2; jeneratör; 1600 A; ACB; 4P; 1100 kVA', 'GİRİŞ G3; jeneratör; 1600 A; ACB; 4P; 1100 kVA', 'Senkron-1 çıkış; 2000 A; 4P', 'Senkron-2 çıkış; 2000 A; 4P'].join('\n');
    const close = openModal(
      `<div class="gen"><h2>${$t('Güç panosu üret')}</h2>
      <p class="note">${$t('Panoyu satır satır tarif edin: <b>Bölüm A</b> yeni bara bölümü başlatır; <b>GİRİŞ</b> ad; trafo / jeneratör / hat; akım; ACB / MCCB / yük ayırıcı; 4P; ölçüm; <b>KUPLAJ</b> akım; ACB; 4P (bölümü sonrakine bağlar); <b>KOMP</b> 25, 2×50 kvar; <b>PARAFUDR</b>; <b>Senkron</b> (girişler paralel). Diğer satırlar çıkıştır: etiket; açıklama; akım; 1P/3P/4P; kaçak akım; kablo m; bara kanalı. Ayraç: sekme ya da ;')}</p>
      <textarea id="bt" rows="14" spellcheck="false" style="width:100%;font:12px var(--mono);height:auto" placeholder="${esc(exAdp)}"></textarea>
      ${this.aiOn ? `<div class="aibox"><b>${$t('Yapay zekâ ile oku')}</b> <span class="note">${$t('Tek hat şemasını (PDF ya da resim) seçin ya da panoyu kısaca anlatın; sonuç aşağıdaki metne yazılır, üretmeden önce düzenleyebilirsiniz.')}</span>
        <div class="btnrow" style="margin-top:6px"><input type="file" id="baif" accept=".pdf,.png,.jpg,.jpeg,.webp"><input id="bain" placeholder="${$t('Not: ör. senkron panosunu değil ana panoyu oku')}" style="flex:1;min-width:200px"><button class="btn" data-m="ai" id="baib">${$t('Oku')}</button></div>
        <div class="note" id="bair"></div></div>` : ''}
      <div class="btnrow" style="margin:6px 0 10px"><button class="btn" data-m="exadp">${$t('Örnek: 2 trafo + kuplaj')}</button><button class="btn" data-m="exsync">${$t('Örnek: senkron panosu')}</button></div>
      <div id="bsum" class="note"></div>
      <div class="err" id="be"></div>
      <label class="fld"><span>${$t('Üretici')}</span><select id="bmk"><option value="">${$t('Tümü (en uygun parça)')}</option></select></label>
      <label class="chkline"><input type="checkbox" id="bly" checked> ${$t('Pano yerleşimi: cihazları gözlere otomatik yerleştir (2D ve 3D görünüm)')}</label>
      <label class="chkline"><input type="checkbox" id="bnh"> ${$t('Nötr barası faz akımının yarısına göre (seçilmezse 1:1)')}</label>
      <div class="btnrow"><label class="chkline"><input type="checkbox" id="bctl"> ${$t('Kontrolörlü kumanda (senkron / şebeke): her giriş için kontrolör, şalter kumandası ve kapak')}</label><input id="bcm" value="DEIF AGC 150" list="bcml" title="${$t('Kontrolör modeli')}" style="width:180px"><datalist id="bcml"><option value="DEIF AGC 150"><option value="Woodward easYgen-2300"></datalist></div>
      <p class="note">${$t('Her giriş, kuplaj ve bölüm için ayrı sayfalar, motorlu şalterlerin kilitleme kumandası ve tek hat özeti projenin sonuna eklenir. İşlemi <b>Geri</b> ile geri alabilirsiniz.')}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok" id="bok">${$t("Sayfaları üret")}</button></div></div>`,
      async (a, dlg) => {
        const ta = qs<HTMLTextAreaElement>(dlg, '#bt');
        if (a === 'ai') {
          const f = qs<HTMLInputElement>(dlg, '#baif').files?.[0], note = qs<HTMLInputElement>(dlg, '#bain').value.trim();
          if (!f && note.length < 10) return void (qs(dlg, '#bair').textContent = $t('Bir tek hat dosyası seçin ya da panoyu birkaç cümleyle anlatın.'));
          if (f && /\.(dwg|dxf)$/i.test(f.name)) return void (qs(dlg, '#bair').textContent = $t('AutoCAD çizimini PDF olarak kaydedip onu seçin.'));
          const btn = qs<HTMLButtonElement>(dlg, '#baib');
          btn.disabled = true;
          qs(dlg, '#bair').textContent = $t('Okunuyor… (yarım dakika kadar sürebilir)');
          try {
            const up = f ? await aiUpload(f, this.aiPdf) : undefined;
            const r = await api.aiBoard(note, up && { name: up.name, type: up.type, data: up.data });
            ta.value = boardToText(r.spec);
            preview();
            const notes = [...r.assumptions, ...r.questions];
            qs(dlg, '#bair').innerHTML = notes.length ? `<ul>${notes.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : esc($t('Okundu; metni kontrol edip sayfaları üretin.'));
          } catch (x) {
            qs(dlg, '#bair').textContent = (x as Error).message;
          } finally {
            btn.disabled = false;
          }
          return;
        }
        if (a === 'exadp' || a === 'exsync') {
          ta.value = a === 'exadp' ? exAdp : exSync;
          return preview();
        }
        if (a !== 'ok') return;
        const { spec, errors } = parseBoardText(ta.value);
        if (errors.length) return void (qs(dlg, '#be').textContent = errors.join(' · '));
        if (!spec.sections.some((x) => x.sources.length)) return void (qs(dlg, '#be').textContent = $t('En az bir giriş (GİRİŞ satırı) gerekli.'));
        qs<HTMLButtonElement>(dlg, '#bok').disabled = true;
        const parts = await this.generatorParts(GEN_TYPES, qs<HTMLSelectElement>(dlg, '#bmk').value);
        const withLayout = qs<HTMLInputElement>(dlg, '#bly').checked;
        const model = qs<HTMLInputElement>(dlg, '#bcm').value.trim() || 'DEIF AGC 150';
        const control = qs<HTMLInputElement>(dlg, '#bctl').checked ? { model } : undefined;
        if (qs<HTMLInputElement>(dlg, '#bnh').checked) spec.nHalf = true;
        const r = generateBoard(this.pr, spec, { parts, fallback: this.generatorFallback(GEN_TYPES), control });
        close();
        const first = this.pr.pages.length;
        let placed = 0;
        this.mutate(() => {
          this.pr.pages.push(...r.pages);
          if (withLayout) {
            const amps = Math.max(...spec.sections.map((_, i) => sectionAmps(spec, i)));
            const { page, result } = addBoardLayout(this.pr, amps, partOf, r.groups, { nHalf: !!spec.nHalf });
            placed = 1;
            if (control) {
              // the controllers, buttons and lamps on the doors: one per compartment, the size of the compartment
              const doors = addDoors(this.pr, [page.layout!], partOf), nd = doors.pages.length;
              doors.pages.forEach(({ front, inside }, i) => {
                const name = nd > 1 ? $t('Kontrolörler {0}/{1}', i + 1, nd) : $t('Kontrolörler');
                front.desc = $t('{0} · kapak ön görünüş', name);
                inside.desc = $t('{0} · kapak iç görünüş', name);
              });
              placed += 2 * nd;
              if (doors.skipped.length) r.warnings.push($t('Kapağa sığmayan cihazlar: {0}', doors.skipped.join(', ')));
            }
            if (result.skipped.length) r.warnings.push($t('Pano yerleşimine sığmayan cihazlar: {0}', result.skipped.join(', ')));
          }
          this.cur = first;
          this.sel.clear();
          this.setTool('select');
        });
        void ensureParts(r.pages.flatMap((p) => p.syms.map((s) => s.part)));
        openModal(
          `<h2>${$t("{0} sayfa üretildi", r.pages.length + placed)}</h2>${r.warnings.length ? `<p>${$t("Gözden geçirmeniz gereken noktalar:")}</p><ul class="note">${r.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>` : ''}
          <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
          () => {},
        );
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    const preview = () => {
      const text = qs<HTMLTextAreaElement>(dlg, '#bt').value;
      if (!text.trim()) {
        qs(dlg, '#bsum').innerHTML = '';
        qs(dlg, '#be').textContent = '';
        return;
      }
      const { spec, errors } = parseBoardText(text);
      const sum = (sp: BoardSpec) =>
        sp.sections
          .map((x, i) => {
            const c = sp.couplers?.[i];
            return `<div><b>${esc($t('Bölüm {0}', x.name || String.fromCharCode(65 + i)))}</b> · ${esc(x.sources.map((g) => `${g.name} ${g.amps} A`).join(', ') || $t('giriş yok'))} · ${$t('{0} çıkış', x.feeders.length)}${x.comp?.length ? ` · ${$t('kompanzasyon {0} kvar', x.comp.reduce((p, k) => p + k, 0))}` : ''}${c && i < sp.sections.length - 1 ? ` · ${esc($t('kuplaj {0} A', c.amps))}` : ''}</div>`;
          })
          .join('') + (sp.sync ? `<div>${$t('Girişler paralel (senkron)')}</div>` : '');
      qs(dlg, '#bsum').innerHTML = sum(spec);
      qs(dlg, '#be').textContent = errors.join(' · ');
    };
    qs<HTMLTextAreaElement>(dlg, '#bt').addEventListener('input', preview);
    void this.fillMakers(qs<HTMLSelectElement>(dlg, '#bmk'));
    if (pre) {
      qs<HTMLTextAreaElement>(dlg, '#bt').value = pre.text;
      qs(dlg, 'h2').textContent = pre.title;
      preview();
      if (pre.warnings?.length) qs(dlg, '#be').textContent = [qs(dlg, '#be').textContent, ...pre.warnings].filter(Boolean).join(' · ');
    }
  }

  private distributionDialog(pre?: { kind: IncomerKind; amps: number; ats: boolean; manual?: boolean; feeders: FeederSpec[]; warnings: string[]; fromPage: string }) {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const example = [
      $t('Etiket\tAçıklama\tAkım (A)\tKutup\tKaçak akım\tKablo (m)\tKoruma\tSayaç'),
      $t('F1\tAydınlatma zemin kat\t16\t1P\tevet\t30'),
      $t('F2\tPriz grubu\t20\t1P\tevet\t25'),
      $t('F3\tKlima\t32\t3P\thayır\t40'),
      $t('F4\tMutfak panosu\t63\t3P\tevet\t20'),
      $t('Q2\tKompresör panosu\t160\t3P\thayır\t60'),
      $t('Q3\tAtölye panosu\t125\t3P\thayır\t45\tNH\tevet'),
      $t('F5\tDaire 1\t40\t3P\tevet\t15\t\tevet'),
    ].join('\n');
    const close = openModal(
      `<div class="gen"><h2>${$t('Dağıtım panosu üret')}</h2>
      <div class="row2"><label class="fld"><span>${$t('Ana şalter')}</span><select id="dk">${(Object.keys(INCOMER_NAMES) as IncomerKind[]).map((k) => `<option value="${k}"${k === 'mccb' ? ' selected' : ''}>${esc($t(INCOMER_NAMES[k]))}</option>`).join('')}</select></label>
        <label class="fld"><span>${$t('Ana şalter akımı (A)')}</span><input id="da" type="number" min="16" max="${MAX_INCOMER}" value="250"></label></div>
      <label class="chkline"><input type="checkbox" id="dm" checked> ${$t('Akım trafoları ve enerji analizörü')}</label>
      <label class="chkline"><input type="checkbox" id="ds" checked> ${$t('Parafudr (aşırı gerilim koruma)')}</label>
      <label class="fld"><span>${$t('Jeneratör')}</span><select id="dg"><option value="">${$t('Jeneratör yok')}</option><option value="ats">${$t('Otomatik transfer (kilitli kontaktörler)')}</option><option value="manual">${$t('Elle transfer: I-0-II komütatör şalter')}</option></select></label>
      <label class="fld"><span>${$t('Kompanzasyon kademeleri (kvar)')}</span><input id="dc" placeholder="${$t('ör. 5, 10, 2×20 · boş bırakılırsa kompanzasyon yok')}"></label>
      <label class="fld"><span>${$t('Üretici')}</span><select id="dmk"><option value="">${$t('Tümü (en uygun parça)')}</option></select></label>
      <label class="chkline"><input type="checkbox" id="dly" checked> ${$t('Pano yerleşimi: cihazları montaj plakasına otomatik yerleştir (2D ve 3D görünüm)')}</label>
      <p>${$t('Çıkış listesini Excel\'den yapıştırın: <b>etiket, açıklama, akım (A), kutup (1P/3P), kaçak akım (evet/hayır), kablo boyu (m), koruma (boş ya da NH), sayaç (evet/hayır)</b>. 63 A\'ya kadar otomatik sigorta, üstünde kompakt şalter seçilir; NH yazılan 3 fazlı çıkışlara sigortalı yük ayırıcı, sayaçlı çıkışlara 80 A\'ya kadar direkt bağlı sayaç konur. Tek fazlı çıkışlar L1, L2, L3\'e sırayla dağıtılır.')}</p>
      <textarea id="dl" rows="7" spellcheck="false" style="width:100%;font:12px var(--mono);height:auto" placeholder="${esc(example)}"></textarea>
      <div class="btnrow" style="margin:6px 0 10px"><button class="btn" data-m="ex">${$t("Örnek listeyi yükle")}</button><span class="note" id="dst"></span></div>
      <div class="tblw" style="max-height:200px"><table class="t"><thead><tr><th>${$t('Etiket')}</th><th>${$t('Açıklama')}</th><th>A</th><th>${$t('Kutup')}</th><th>${$t('Kaçak akım')}</th><th>${$t('Kablo')}</th><th>${$t('Koruma')}</th><th>${$t('Sayaç')}</th></tr></thead><tbody id="dp"></tbody></table></div>
      <div class="err" id="de"></div>
      <p class="note">${$t('Sayfalar projenin sonuna eklenir: giriş sayfası ve çıkış sayfaları. Parçalar akım değerine göre parça veritabanından seçilir. İşlemi <b>Geri</b> ile geri alabilirsiniz.')}</p>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok" id="dok">${$t("Sayfaları üret")}</button></div></div>`,
      async (a, dlg) => {
        const ta = qs<HTMLTextAreaElement>(dlg, '#dl');
        if (a === 'ex') {
          ta.value = example;
          return preview();
        }
        if (a !== 'ok') return;
        const { feeders } = parseFeederList(ta.value);
        const amps = Number(qs<HTMLInputElement>(dlg, '#da').value);
        if (!(amps >= 16 && amps <= MAX_INCOMER)) return void (qs(dlg, '#de').textContent = $t('Ana şalter akımı 16–{0} A arasında olmalı.', MAX_INCOMER));
        const kind = qs<HTMLSelectElement>(dlg, '#dk').value as IncomerKind;
        const comp = parseSteps(qs<HTMLInputElement>(dlg, '#dc').value);
        if (comp.error) return void (qs(dlg, '#de').textContent = comp.error);
        qs<HTMLButtonElement>(dlg, '#dok').disabled = true;
        const parts = await this.generatorParts(GEN_TYPES, qs<HTMLSelectElement>(dlg, '#dmk').value);
        const withLayout = qs<HTMLInputElement>(dlg, '#dly').checked;
        // from a single-line page: that page is the overview, no second one is drawn
        const r = generateDistribution(this.pr, { kind, amps, meter: qs<HTMLInputElement>(dlg, '#dm').checked, spd: qs<HTMLInputElement>(dlg, '#ds').checked, ats: !!qs<HTMLSelectElement>(dlg, '#dg').value, manual: qs<HTMLSelectElement>(dlg, '#dg').value === 'manual', comp: comp.steps }, feeders, { parts, fallback: this.generatorFallback(GEN_TYPES), singleLine: !pre });
        close();
        const first = this.pr.pages.length;
        let placed = 0;
        this.mutate(() => {
          this.pr.pages.push(...r.pages);
          // mounting plate with every device placed: the 2D layout and the 3D view
          if (withLayout) {
            const { result } = addBoardLayout(this.pr, amps, partOf);
            placed = 1;
            if (result.skipped.length) r.warnings.push($t('Pano yerleşimine sığmayan cihazlar: {0}', result.skipped.join(', ')));
          }
          this.cur = first;
          this.sel.clear();
          this.setTool('select');
        });
        void ensureParts(r.pages.flatMap((p) => p.syms.map((s) => s.part)));
        if (r.warnings.length)
          openModal(
            `<h2>${$t("{0} sayfa üretildi", r.pages.length + placed)}</h2><p>${$t("Gözden geçirmeniz gereken noktalar:")}</p><ul class="note">${r.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}</ul>
            <div class="acts"><button class="btn pri" data-m="close">${$t("Tamam")}</button></div>`,
            () => {},
          );
        else toast($t("{0} sayfa üretildi", r.pages.length + placed));
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    const preview = () => {
      const { feeders, errors } = parseFeederList(qs<HTMLTextAreaElement>(dlg, '#dl').value);
      qs(dlg, '#dp').innerHTML = feeders
        .map((f) => `<tr><td class="m">${esc(f.tag || $t('(otomatik)'))}</td><td>${esc(f.desc)}</td><td class="m">${f.amps}</td><td>${f.poles}P</td><td>${f.rcd ? '✓' : ''}</td><td class="m">${f.cableLen ? `${f.cableLen} m` : '—'}</td><td>${f.fuse && f.poles === 3 ? 'NH' : ''}</td><td>${f.meter ? '✓' : ''}</td></tr>`)
        .join('');
      qs(dlg, '#de').textContent = errors.join(' · ');
      qs(dlg, '#dst').textContent = feeders.length ? $t('{0} çıkış', feeders.length) : '';
    };
    qs<HTMLTextAreaElement>(dlg, '#dl').addEventListener('input', preview);
    void this.fillMakers(qs<HTMLSelectElement>(dlg, '#dmk'));
    if (pre) {
      // filled from the single-line page: incomer and the feeder rows as a pasted list
      qs<HTMLSelectElement>(dlg, '#dk').value = pre.kind;
      qs<HTMLInputElement>(dlg, '#da').value = String(pre.amps);
      qs<HTMLSelectElement>(dlg, '#dg').value = pre.manual ? 'manual' : pre.ats ? 'ats' : '';
      qs<HTMLTextAreaElement>(dlg, '#dl').value = pre.feeders
        .map((f) => [f.tag ?? '', f.desc.replace(/[\t\n]/g, ' '), f.amps, `${f.poles}P`, f.rcd ? 'x' : '', f.cableLen ?? '', f.fuse ? 'NH' : '', f.meter ? 'x' : ''].join('\t'))
        .join('\n');
      qs(dlg, 'h2').textContent = $t('Tek hattan pano üret · {0}', pre.fromPage);
      if (pre.warnings.length) qs(dlg, '#de').textContent = pre.warnings.join(' · ');
      preview();
    }
  }

  /** Report pages: a report as sheets in the title block, after the current page. */
  private reportPagesDialog() {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const name = (k: string) => (k === 'plcov' ? $t('PLC genel bakış (kartlar ve kanallar)') : reportPageName(k));
    const close = openModal(
      `<h2>${$t('Rapor sayfası ekle')}</h2><p>${$t('Seçilen rapor antetli şema sayfası olarak eklenir; tablosu projeden üretilir ve her zaman günceldir.')}</p>
      <label class="fld"><span>${$t('Rapor')}</span><select id="rpt">${REPORT_PAGE_TABS.map((k) => `<option value="${k}">${esc(name(k))}</option>`).join('')}</select></label>
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('Ekle')}</button></div>`,
      (a, dlg) => {
        if (a !== 'ok') return;
        const tab = qs<HTMLSelectElement>(dlg, '#rpt').value as ReportPageTab;
        close();
        if (this.pr.pages.some((p) => p.kind === 'report' && p.report?.tab === tab)) return void toast($t('Bu raporun sayfaları zaten var; sayfa sayısını rapor sayfasından güncelleyebilirsiniz.'));
        this.mutate(() => {
          const pages = newReportPages(this.pr, this.A, tab, partOf);
          this.pr.pages.splice(this.cur + 1, 0, ...pages);
          this.cur++;
          this.sel.clear();
          this.setTool('select');
        });
      },
    );
  }

  private singleLineDialog() {
    if (this.readOnly) return void toast($t("Bu proje sizin için salt okunur; değişiklik yapılamaz."));
    const auto = singleLinePages(this.pr, this.A);
    const close = openModal(
      `<h2>${$t("Tek hat şeması")}</h2>
      <p>${$t("Tek hat şemasında bir çizgi üç fazı temsil eder. Tek hat sembolleri çok hatlı şemadaki cihazlarla aynı etiketi taşır ve onlara çapraz referans verir; malzeme listesine ikinci kez girmez, bağlantı ve tel numarası üretmez.")}</p>
      <div class="acts" style="justify-content:flex-start;flex-wrap:wrap">
        <button class="btn pri" data-m="auto"${auto.length ? '' : ' disabled'}>${$t("Projeden otomatik üret{0}", auto.length ? $t(" ({0} sayfa)", auto.length) : '')}</button>
        <button class="btn" data-m="blank">${$t("Boş tek hat sayfası")}</button>
        <button class="btn" data-m="close">${$t("Vazgeç")}</button></div>
      ${auto.length ? '' : `<p class="note">${$t("Otomatik üretim için projede motor olmalı; şalter, kontaktör, termik ve konvertör motordan geriye doğru izlenir.")}</p>`}`,
      (a) => {
        if (a !== 'auto' && a !== 'blank') return;
        close();
        this.mutate(() => {
          const pages = a === 'auto' ? auto : [{ id: newId(), desc: $t("Tek hat şeması"), kind: 'single' as const, syms: [], wires: [] }];
          this.pr.pages.splice(this.cur + 1, 0, ...pages);
          this.cur++;
          this.sel.clear();
          this.setTool('select');
        });
      },
    );
  }

  /** Builds a black box (rectangle with connection points) as a company symbol and starts placing it. */
  private blackBoxDialog() {
    const close = openModal(
      `<div class="gen"><h2>${$t("Kara kutu oluştur")}</h2>
      <p>${$t("Kütüphanede sembolü olmayan cihazlar için (yumuşak yolverici, ölçü cihazı, röle modülü…) kenarlarında bağlantı noktaları olan bir dikdörtgen. Firmanızın sembol kütüphanesine kaydedilir, sonra her projede kullanılabilir.")}</p>
      <div class="row2"><label class="fld"><span>${$t("Ad")}</span><input id="bn" placeholder="${$t("ör. Yumuşak yolverici")}"></label><label class="fld"><span>${$t("Etiket öneki")}</span><input id="bp" value="U" maxlength="4"></label></div>
      <div class="row2"><label class="fld"><span>${$t("Üst bağlantılar")}</span><input id="bt" class="m" placeholder="L1, L2, L3"></label><label class="fld"><span>${$t("Alt bağlantılar")}</span><input id="bb" class="m" placeholder="T1, T2, T3"></label></div>
      <div class="row2"><label class="fld"><span>${$t("Sol bağlantılar")}</span><input id="bl" class="m" placeholder="A1, A2"></label><label class="fld"><span>${$t("Sağ bağlantılar")}</span><input id="br" class="m" placeholder="13, 14"></label></div>
      <div class="bbprev" id="bv"></div>
      <div class="err" id="be"></div>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Kaydet ve yerleştir")}</button></div></div>`,
      async (a, dlg) => {
        if (a !== 'ok') return;
        const spec = read(dlg);
        if (![...spec.top, ...spec.bottom, ...spec.left, ...spec.right].length) return void (qs(dlg, '#be').textContent = $t("En az bir bağlantı noktası girin."));
        try {
          const r = await api.saveSymbol(blackBoxSymbol(spec));
          registerCustomSymbols([r.symbol]);
          close();
          toast($t("\"{0}\" sembol kütüphanesine eklendi; yerleştirmek için şemaya tıklayın", r.symbol.name));
          if (this.mode !== 'sch') {
            this.mode = 'sch';
            this.renderAll();
          }
          this.setTool('place', r.symbol.key);
          this.renderLib();
        } catch (e) {
          qs(dlg, '#be').textContent = (e as Error).message;
        }
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    const read = (d: HTMLElement) => ({
      name: qs<HTMLInputElement>(d, '#bn').value,
      prefix: qs<HTMLInputElement>(d, '#bp').value.toUpperCase().replace(/[^A-Z]/g, ''),
      top: pinList(qs<HTMLInputElement>(d, '#bt').value),
      bottom: pinList(qs<HTMLInputElement>(d, '#bb').value),
      left: pinList(qs<HTMLInputElement>(d, '#bl').value),
      right: pinList(qs<HTMLInputElement>(d, '#br').value),
    });
    const preview = () => {
      const b = blackBoxSymbol(read(dlg));
      const xs = b.pins.map((p) => p.x).concat(0), ys = b.pins.map((p) => p.y).concat(0);
      const r = b.prims[0] as { w: number; h: number };
      const x0 = Math.min(...xs, 0) - 6, y0 = Math.min(...ys, 0) - 6, x1 = Math.max(...xs, r.w) + 6, y1 = Math.max(...ys, r.h + 8) + 6;
      const body = b.prims
        .map((p) => (p.k === 'rect' ? `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}"/>` : p.k === 'line' ? `<path d="M${p.pts.map((q) => q.join(' ')).join('L')}"/>` : p.k === 'text' ? `<text x="${p.x}" y="${p.y}" font-size="${p.size}" text-anchor="middle">${esc(p.text)}</text>` : ''))
        .join('');
      const labels = b.pins.map((p) => `<text x="${p.x + 1}" y="${p.y - 1}" font-size="2">${esc(p.label)}</text>`).join('');
      qs(dlg, '#bv').innerHTML = `<svg viewBox="${x0} ${y0} ${x1 - x0} ${y1 - y0}" class="symprev">${body}${labels}</svg>`;
    };
    dlg.addEventListener('input', preview);
    preview();
    qs<HTMLInputElement>(dlg, '#bn').focus();
  }

  /** Parts the generator may choose from: the company's database, the built-in catalogue as fallback. */
  private async generatorParts(types: string[] = ['cb3', 'ol3', 'coil', 'coil_on', 'ptcr', 'motor3', 'motor6', 'vfd', 'ss', 'rot01', 'cb1', 'psu', 'lamp', 'pb_no', 'pb_nc', 'term', 'plcin', 'plcout', 'plcai', 'plcao'], maker = ''): Promise<Part[]> {
    await Promise.all(types.map((t) => loadTypeAll(t)));
    const out = new Map<string, Part>();
    for (const t of types) {
      // only parts that are of this type (parts without types are listed for every type)
      const list = (partsForType(t) ?? []).filter((p) => p.t.includes(t));
      // one maker for the whole board when one is chosen; where it has nothing of a kind, any maker, then the built-in catalogue
      const mine = maker ? list.filter((p) => (p.maker || '') === maker) : list;
      for (const p of mine.length ? mine : list.length ? list : CATALOG.filter((c) => c.t.includes(t))) out.set(p.pn, p);
    }
    return [...out.values()];
  }

  /** Everything the generators may fall back to when the chosen maker has no suitable part: the library and the built-in catalogue. */
  private generatorFallback(types: string[]): Part[] {
    const out = new Map<string, Part>();
    for (const t of types) {
      for (const p of CATALOG.filter((c) => c.t.includes(t))) out.set(p.pn, p);
      for (const p of (partsForType(t) ?? []).filter((x) => x.t.includes(t))) out.set(p.pn, p);
    }
    return [...out.values()];
  }

  /** Fills a maker <select> with the makers of breakers in the library (most parts first). */
  private async fillMakers(sel: HTMLSelectElement) {
    const types = ['cb3m', 'mccb3', 'lbs3', 'cb3n', 'cb1'];
    await Promise.all(types.map((t) => loadTypeAll(t)));
    const n = new Map<string, number>();
    for (const t of types) for (const p of partsForType(t) ?? []) if (p.maker && !p.shared && p.t.includes(t)) n.set(p.maker, (n.get(p.maker) ?? 0) + 1);
    sel.insertAdjacentHTML('beforeend', [...n].sort((a, b) => b[1] - a[1]).map(([m, c]) => `<option value="${esc(m)}">${esc(m)} (${c})</option>`).join(''));
  }

  /* ================= import / export ================= */
  /** Save as: a copy on the computer (a new file) or on the server (a new project); the work goes on in the copy. */
  private saveAsDialog() {
    // read now, so that the file dialog opens right on the click
    const history = this.local ? localHistory(this.id) : Promise.resolve(undefined);
    const close = openModal(
      `<h2>${$t('Farklı kaydet')}</h2>
      <p>${$t('Projenin bir kopyası kaydedilir ve çalışmaya kopyada devam edilir; şu anki proje olduğu gibi kalır.')}</p>
      <label class="fld"><span>${$t('Proje adı')}</span><input id="san" value="${esc(this.pr.name)}"></label>
      <label class="fld"><span>${$t('Nereye')}</span><select id="saw">
        <option value="local"${this.local ? ' selected' : ''}>${$t('Bilgisayarımda (yeni dosya; sunucuda saklanmaz)')}</option>
        <option value="server"${this.local ? '' : ' selected'}>${$t('Sunucuda (yeni proje)')}</option></select></label>
      <div class="acts"><button class="btn" data-m="close">${$t('Vazgeç')}</button><button class="btn pri" data-m="ok">${$t('Kaydet')}</button></div>`,
      async (a, dlg) => {
        if (a !== 'ok') return;
        const name = qs<HTMLInputElement>(dlg, '#san').value.trim() || this.pr.name;
        const copy = normaliseProject(JSON.parse(JSON.stringify({ ...embedCustomSymbols(this.pr), name })));
        try {
          let to: string;
          if (qs<HTMLSelectElement>(dlg, '#saw').value === 'local') {
            // the file dialog opens right on the click
            const r = await createLocal(name, copy, await history);
            if (!r) return;
            to = `#/l/${r.id}`;
          } else to = `#/p/${(await api.create({ data: copy })).id}`;
          close();
          toast($t('Kopya kaydedildi; çalışma kopyada sürüyor.'));
          // leaving saves what is still unsaved in this one
          location.hash = to;
        } catch (e) {
          if (!reloadIfStale(e)) toast((e as Error).message);
        }
      },
    );
  }

  private exportJson() {
    downloadFile(`${safeFileName(this.pr.name)}.enercad.json`, JSON.stringify(embedCustomSymbols(this.pr), null, 1), 'application/json');
  }
  private async importPages(file: File) {
    try {
      const incoming = normaliseProject(JSON.parse(await readFileText(file)));
      const close = openModal(
        `<h2>${$t("Sayfaları içe aktar")}</h2><p>${$t("\"{0}\" dosyasında {1} sayfa var. Bu projenin sonuna eklensin mi? Etiketler olduğu gibi aktarılır; çakışmaları Kontrol raporu gösterir.", esc(incoming.name), incoming.pages.length)}</p>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Sayfaları ekle")}</button></div>`,
        (a) => {
          if (a !== 'ok') return;
          close();
          this.mutate(() => {
            for (const p of incoming.pages) {
              p.id = newId();
              p.syms.forEach((s) => (s.id = newId()));
              p.wires.forEach((w) => (w.id = newId()));
              this.pr.pages.push(p);
            }
            this.cur = this.pr.pages.length - incoming.pages.length;
            this.sel.clear();
          });
          toast($t("{0} sayfa eklendi", incoming.pages.length));
        },
      );
    } catch (e) {
      toast(e instanceof SyntaxError ? $t("Dosya geçerli bir JSON değil.") : (e as Error).message);
    }
  }

  /** Saves pending edits first so the server renders the latest version. */
  private async serverDownload(path: string, msg: string) {
    toast(msg);
    if (this.local) return this.localDownload(path);
    if (this.rev !== this.savedRev) {
      clearTimeout(this.saveTimer);
      await this.flush();
      if (this.rev !== this.savedRev) return toast($t("Önce proje kaydedilmeli; kayıt başarısız oldu."));
    }
    const a = document.createElement('a');
    a.href = `/api/projects/${encodeURIComponent(this.id)}/${path}${path.includes('?') ? '&' : '?'}lang=${getLang()}`;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  /** A local project's PDF or Excel: the project goes with the request, the server renders it and keeps nothing. */
  private async localDownload(path: string) {
    const [file, query] = path.split('?');
    const kind = file === 'export.pdf' ? 'pdf' : file;
    try {
      const res = await fetch(`/api/export/${kind}?${query ? query + '&' : ''}lang=${getLang()}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-enercad': '1', 'x-lang': getLang() },
        body: JSON.stringify({ project: embedCustomSymbols(this.pr) }),
      });
      if (!res.ok) return void toast(((await res.json().catch(() => ({}))) as { error?: string }).error || $t("Sunucu hatası ({0})", res.status));
      const cd = res.headers.get('content-disposition') ?? '';
      const name = decodeURIComponent(/filename\*=UTF-8''([^;]+)/.exec(cd)?.[1] ?? '') || `${safeFileName(this.pr.name)}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      downloadFile(name, new Uint8Array(await res.arrayBuffer()), res.headers.get('content-type') ?? 'application/octet-stream');
    } catch {
      toast($t("Sunucuya ulaşılamadı. Bağlantınızı kontrol edin."));
    }
  }

  /** Translation of the project texts through the company dictionary (EPLAN "Translation"). */
  private async translateDialog() {
    const texts = projectTexts(this.pr);
    if (!texts.length) return toast($t('Projede çevrilecek metin yok.'));
    let lang = getLang() === 'en' ? 'de' : 'en';
    let dict: Record<string, string> = {};
    const langs = LANGS.filter((l) => l.code !== 'tr');
    const close = openModal(
      `<div class="gen"><h2>${$t('Proje metinlerini çevir')}</h2>
      <p>${$t('Sayfa adları, cihaz açıklamaları ve serbest metinler firmanızın sözlüğüyle çevrilir. Cihaz etiketleri, parça numaraları ve değerler değişmez. Programın ürettiği metinler (ör. motor listesinden gelenler) için öneri gelir; öneriyi kabul etmek için alanı doldurun ya da <b>Önerileri doldur</b> deyin.')}</p>
      <div class="navbar"><label class="fld" style="margin:0"><span>${$t('Hedef dil')}</span><select id="tl">${langs.map((l) => `<option value="${l.code}"${l.code === lang ? ' selected' : ''}>${l.name}</option>`).join('')}</select></label>
        <span class="note" id="ts"></span><span class="spacer" style="flex:1"></span>
        <button class="btn" data-m="fill">${$t('Önerileri doldur')}</button></div>
      <div class="tblw" style="max-height:48vh"><table class="t nav"><thead><tr><th>${$t('Metin')}</th><th>${$t('Adet')}</th><th>${$t('Çeviri')}</th></tr></thead><tbody id="tb"></tbody></table></div>
      <div class="err" id="te"></div>
      <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button><button class="btn" data-m="save">${$t('Sözlüğe kaydet')}</button>
        <button class="btn" data-m="pdf">${$t('Çeviriyle PDF')}</button><button class="btn" data-m="xlsx">${$t('Çeviriyle Excel')}</button><button class="btn pri" data-m="copy">${$t('Çevrilmiş kopya oluştur')}</button></div></div>`,
      async (a, dlg) => {
        const err = qs(dlg, '#te');
        err.textContent = '';
        if (a === 'fill') {
          dlg.querySelectorAll<HTMLInputElement>('input[data-src]').forEach((i) => !i.value && i.placeholder && (i.value = i.placeholder));
          return count();
        }
        try {
          const entries = collect();
          await api.saveDictionary(lang, entries);
          dict = Object.fromEntries(Object.entries(entries).filter(([, v]) => v));
          if (a === 'save') return void toast($t('Sözlük kaydedildi'));
          if (a === 'pdf') return void this.serverDownload(`export.pdf?translate=${lang}`, $t('PDF hazırlanıyor…'));
          if (a === 'xlsx') return void this.serverDownload(`reports.xlsx?translate=${lang}`, $t('Excel hazırlanıyor…'));
          if (a === 'copy') {
            const data = translateProject(this.pr, dict, lang);
            const info = LANGS.find((l) => l.code === lang)!;
            const r = await api.create({ data: { ...data, name: `${data.name} (${info.name})` } });
            close();
            toast($t('Çevrilmiş kopya oluşturuldu'));
            location.hash = `#/p/${r.id}`;
          }
        } catch (e) {
          err.textContent = (e as Error).message;
        }
      },
    );
    const dlg = document.querySelector<HTMLElement>('.modal .gen')!;
    const collect = () => {
      const out: Record<string, string> = {};
      dlg.querySelectorAll<HTMLInputElement>('input[data-src]').forEach((i) => {
        const src = texts[+i.dataset.src!].text;
        if (i.value.trim() || dict[src]) out[src] = i.value.trim();
      });
      return out;
    };
    const count = () => {
      const all = dlg.querySelectorAll<HTMLInputElement>('input[data-src]');
      const done = [...all].filter((i) => i.value.trim()).length;
      qs(dlg, '#ts').textContent = $t('{0} / {1} metin çevrildi', done, all.length);
    };
    const load = async () => {
      qs(dlg, '#tb').innerHTML = `<tr><td colspan="3" class="note">…</td></tr>`;
      try {
        await loadLang(lang);
        dict = (await api.dictionary(lang)).entries;
      } catch (e) {
        qs(dlg, '#te').textContent = (e as Error).message;
      }
      qs(dlg, '#tb').innerHTML = texts
        .slice(0, 800)
        .map((x, i) => {
          const sug = dict[x.text] ? '' : suggestTranslation(x.text, lang) ?? '';
          return `<tr><td>${esc(x.text)}</td><td class="m">${x.count}</td><td><input data-src="${i}" value="${esc(dict[x.text] ?? '')}" placeholder="${esc(sug)}" style="width:100%" dir="auto"></td></tr>`;
        })
        .join('');
      count();
    };
    qs<HTMLSelectElement>(dlg, '#tl').onchange = (e) => {
      lang = (e.target as HTMLSelectElement).value;
      void load();
    };
    dlg.addEventListener('input', count);
    void load();
  }

  private dxfDialog() {
    const close = openModal(
      `<h2>${$t("DXF dışa aktar")}</h2><p>${$t("DXF R12 biçiminde, milimetre biriminde. AutoCAD, BricsCAD, LibreCAD ve EPLAN'ın DXF içe aktarması okuyabilir. Potansiyeller ayrı katmanlardadır.")}</p>
      <label class="fld"><span>${$t("Kapsam")}</span><select id="dxfScope"><option value="cur">${$t("Bu sayfa ({0})", this.cur + 1)}</option><option value="all">${$t("Tüm sayfalar, yan yana ({0})", this.pr.pages.length)}</option></select></label>
      <label style="display:flex;gap:6px;align-items:center;font-size:12.5px"><input type="checkbox" id="dxfNet"${this.opts.net ? ' checked' : ''}> ${$t("Tel numaralarını ekle")}</label>
      <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("DXF indir")}</button></div>`,
      (m, dlg) => {
        if (m !== 'ok') return;
        const all = (qs(dlg, '#dxfScope') as HTMLSelectElement).value === 'all';
        const pages = all ? this.pr.pages.map((_, i) => i) : [this.cur];
        const bytes = projectToDxf(this.pr, this.A, { pages, netNumbers: (qs(dlg, '#dxfNet') as HTMLInputElement).checked, parts: partOf });
        const name = `${safeFileName(this.pr.name)}${all ? '' : $t(" - sayfa ") + (this.cur + 1)}.dxf`;
        downloadFile(name, bytes, 'application/dxf');
        close();
      },
    );
  }

  /* ================= events ================= */
  private on<K extends keyof DocumentEventMap>(t: Document | Window, type: K | 'beforeunload', fn: (e: any) => void, opts?: AddEventListenerOptions) {
    t.addEventListener(type, fn, opts);
    this.offs.push(() => t.removeEventListener(type, fn, opts));
  }

  private bind() {
    const svg = this.svg;
    svg.addEventListener('contextmenu', (e) => e.preventDefault());
    svg.addEventListener('pointerdown', (e) => this.onDown(e));
    svg.addEventListener('pointermove', (e) => this.onMove(e));
    svg.addEventListener('pointerup', (e) => this.onUp(e));
    svg.addEventListener('pointercancel', (e) => this.onUp(e));
    svg.addEventListener('dblclick', () => {
      if (this.tool === 'wire' && this.draft.length > 1) return this.finishWire();
      if (this.tool === 'select' && this.sel.size === 1) {
        const f = this.el.querySelector<HTMLInputElement>('#f_s_tag, #f_p_name, #f_p_text');
        f?.focus();
        f?.select();
      }
    });
    svg.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
    }, { passive: false });

    this.el.addEventListener('click', (e) => this.onClick(e));
    this.on(document, 'pointerdown', (e: PointerEvent) => {
      if (!(e.target as Element).closest?.('details.menu')) this.closeMenus();
    });
    (this.$('#libq') as HTMLInputElement).addEventListener('input', () => this.renderLib());
    (this.$('#devq') as HTMLInputElement).addEventListener('input', () => this.renderDevTree());
    this.$('#props').addEventListener('change', (e) => this.onField(e.target as HTMLInputElement));
    const file = this.$('#impFile') as HTMLInputElement;
    file.onchange = () => {
      if (file.files?.[0]) void this.importPages(file.files[0]);
      file.value = '';
    };
    this.on(document, 'keydown', (e: KeyboardEvent) => this.onKey(e));
    this.on(document, 'keyup', (e: KeyboardEvent) => e.key === ' ' && (this.spaceDown = false));
    this.on(window, 'beforeunload', (e: BeforeUnloadEvent) => {
      if (this.rev !== this.savedRev) {
        void this.flush();
        e.preventDefault();
      }
    });
  }

  /** closes the header's drop-down menus (all, or all but the one given) */
  private closeMenus(keep?: Element | null) {
    this.el.querySelectorAll<HTMLDetailsElement>('details.menu[open]').forEach((d) => d !== keep && (d.open = false));
  }

  private onClick(e: MouseEvent) {
    const t = e.target as HTMLElement;
    // only one menu open at a time; an entry closes its menu
    const menu = t.closest('details.menu');
    this.closeMenus(t.closest('.menu-pop') ? null : menu);
    const a = t.closest<HTMLElement>('[data-a]')?.dataset.a;
    if (a) return this.action(a);
    const cat = t.closest<HTMLElement>('[data-libcat]')?.dataset.libcat;
    if (cat) {
      const closed = new Set(this.ui.closed ?? []);
      // the imported symbols start closed: what is kept for them is that they were opened
      const k = cat === '@imported' ? cat + ':open' : cat;
      if (closed.has(k)) closed.delete(k);
      else closed.add(k);
      this.ui.closed = [...closed];
      this.saveUi();
      return this.renderLib();
    }
    const lib = t.closest<HTMLElement>('[data-lib]')?.dataset.lib;
    if (lib) {
      // most recent first, eight at most
      this.ui.recent = [lib, ...(this.ui.recent ?? []).filter((k) => k !== lib)].slice(0, 8);
      this.saveUi();
      if (this.tool === 'place' && this.placeType === lib) this.setTool('select');
      else {
        if (this.mode !== 'sch') {
          this.mode = 'sch';
          this.renderAll();
        }
        this.setTool('place', lib);
      }
      return;
    }
    const nav = t.closest<HTMLElement>('[data-nav]')?.dataset.nav as Editor['nav'] | undefined;
    if (nav) {
      this.nav = nav;
      return this.renderNav();
    }
    const goto = t.closest<HTMLElement>('[data-goto]')?.dataset.goto;
    if (goto) {
      const [pi, sid] = goto.split('|');
      return this.focusOn(+pi, sid);
    }
    const gw = t.closest<HTMLElement>('[data-gowire]')?.dataset.gowire;
    if (gw) {
      const [pi, wid] = gw.split('|');
      return this.focusWire(+pi, wid);
    }
    const ldev = t.closest<HTMLElement>('[data-ldev]')?.dataset.ldev;
    if (ldev) {
      this.placeDev = this.tool === 'ldev' && this.placeDev === ldev ? null : ldev;
      return this.setTool(this.placeDev ? 'ldev' : 'select');
    }
    const lgo = t.closest<HTMLElement>('[data-lgo]')?.dataset.lgo;
    if (lgo) {
      const [pi, id] = lgo.split('|');
      this.cur = +pi;
      this.sel.clear();
      this.sel.add('L:i:' + id);
      return this.renderAll();
    }
    const dm = t.closest<HTMLElement>('[data-delmacro]')?.dataset.delmacro;
    if (dm) return this.deleteMacro(dm);
    const mac = t.closest<HTMLElement>('[data-macro]')?.dataset.macro;
    if (mac) return this.startMacro(mac);
    const pg = t.closest<HTMLElement>('[data-pg]')?.dataset.pg;
    if (pg != null) {
      this.mode = 'sch';
      return this.gotoPage(+pg);
    }
    const act = t.closest<HTMLElement>('[data-act]')?.dataset.act;
    if (act === 'del') return this.delSel();
    if (act === 'rot') return this.rotSel();
    if (act === 'dup') return void (this.copySel() && this.paste(null));
    if (act === 'savemacro') return this.saveMacroDialog();
    if (act === 'readdr') return this.readdressDialog('');
    if (act === 'plcimp') return this.assignmentImport();
    if (act === 'plcaml') return this.automationMLDialog();
    if (act === 'aireview') return this.local ? toast(LOCAL_ONLY()) : this.aiReviewDialog();
    if (act === 'slboard') {
      // several busbars, couplers, more than two sources or large feeders: the power board; else the simple board
      const big = boardSpecFromSingleLine(this.page, this.pr.pages);
      const s = big?.spec;
      if (s && (s.sections.length > 1 || s.sections.some((x) => x.sources.length > 2 || x.feeders.some((f) => f.amps > 630 || f.poles === 4 || f.busway))))
        return this.boardDialog({ text: boardToText(s), title: $t('Tek hattan güç panosu üret · {0}', this.page.desc), warnings: big!.warnings });
      const b = boardFromSingleLine(this.page);
      if (!b) return toast($t('Bu sayfada bir bara ve tek hat şalterleri bulunamadı.'));
      return this.distributionDialog({ ...b, fromPage: this.page.desc });
    }
    if (act === 'marks') return this.marksDialog();
    if (act === 'readdrcard') {
      const s = this.selItems().S[0];
      return this.readdressDialog(s ? this.A.devKey.get(s.id) ?? '' : '');
    }
    if (act === 'autolayout') return this.autoLayoutDialog();
    if (act === 'view3d') {
      // on a door page: the mounting plate before it
      let pi = this.cur;
      if (this.page.layout?.door) for (let i = this.cur - 1; i >= 0; i--) if (this.pr.pages[i].kind === 'layout' && !this.pr.pages[i].layout?.door) { pi = i; break; }
      const cab = cabinet3D(this.pr, this.A, pi, partOf);
      if (!cab) return void toast($t('3D görünüm için önce bir pano yerleşimi (montaj plakası) sayfası ekleyin.'));
      toast($t('3D görünüm hazırlanıyor…'));
      const pns = [...new Set(cab.boxes.map((b) => b.pn).filter((p): p is string => !!p))];
      const wires = cabinetWiring(this.pr, this.A, pi, partOf);
      return void Promise.all([import('./view3d'), pns.length ? api.partModels(pns).catch(() => ({ models: {} })) : { models: {} }])
        .then(([m, r]) => m.open3D(cab, `${this.pr.name} · ${this.pr.pages[pi].desc}`, { models: r.models, wires }))
        .catch((e) => reloadIfStale(e) || toast((e as Error).message));
    }
    if (act === 'lfit') return this.layoutField('ly.scale', String(fitScale(this.page.layout!.w, this.page.layout!.h)));
    if (act === 'frlogo') {
      const input = this.$('#frfile') as HTMLInputElement;
      input.onchange = () => {
        const file = input.files?.[0];
        input.value = '';
        if (file) void logoDataUrl(file).then((logo) => this.mutate(() => (this.pr.frame = normaliseTitleBlock({ ...this.pr.frame, logo })))).catch((e) => toast((e as Error).message));
      };
      return input.click();
    }
    if (act === 'frlogodel') return this.mutate(() => (this.pr.frame = normaliseTitleBlock({ ...this.pr.frame, logo: '' })));
    if (act === 'frload')
      return void api.companyFrame().then(
        (r) => (r.frame ? this.mutate(() => (this.pr.frame = r.frame ?? undefined)) : toast($t('Firmanın kayıtlı antet şablonu yok.'))),
        (e) => toast((e as Error).message),
      );
    if (act === 'frsave')
      return void api.saveCompanyFrame(this.pr.frame ?? null).then(
        () => toast($t('Antet firma şablonu olarak kaydedildi; yeni projeler bununla açılır.')),
        (e) => toast((e as Error).message),
      );
    if (act === 'freeze')
      return this.mutate(() => {
        for (const p of this.pr.pages) for (const w of p.wires) if (!w.no) w.no = this.A.netLabel.get(this.A.uf.find(wireNode(w.id)));
        toast($t("Tel numaraları sabitlendi"));
      });
    if (act === 'unfreeze')
      return this.mutate(() => {
        for (const p of this.pr.pages) for (const w of p.wires) delete w.no;
        toast($t("Sabit numaralar kaldırıldı"));
      });
    if (act === 'repsync') {
      // as many pages as the report's rows need now
      const tab = this.page.report?.tab as ReportPageTab | undefined;
      if (!tab) return;
      let changed = false;
      this.mutate(() => {
        changed = syncReportPages(this.pr, this.A, tab, partOf);
        this.cur = Math.min(this.cur, this.pr.pages.length - 1);
      });
      return void toast(changed ? $t('Rapor sayfaları güncellendi') : $t('Sayfa sayısı zaten güncel'));
    }
    if (act === 'csv') {
      const R = buildReport(this.pr, this.A, this.repTab, partOf);
      downloadFile(`${safeFileName(this.pr.name)} - ${R.title}.csv`, '﻿' + reportToCsv(R), 'text/csv;charset=utf-8');
      return;
    }
    const rt = t.closest<HTMLElement>('[data-rt]')?.dataset.rt as ReportTab | undefined;
    if (rt) {
      this.repTab = rt;
      return this.renderReports();
    }
    const ign = t.closest<HTMLElement>('[data-ign]');
    if (ign) {
      // ignore a check message (or take it back): kept in the project, undoable
      const k = ign.dataset.ign!, back = !!ign.dataset.back;
      this.mutate(() => {
        const list = (this.pr.ignoredChecks ?? []).filter((x) => x !== k);
        if (!back) list.push(k);
        if (list.length) this.pr.ignoredChecks = list;
        else delete this.pr.ignoredChecks;
      });
      return;
    }
    const go = t.closest<HTMLElement>('tr[data-go]')?.dataset.go;
    if (go) {
      const [pi, sid] = go.split('|');
      this.focusOn(+pi, sid);
    }
    if (t.closest('#stChk')) {
      this.mode = 'rep';
      this.repTab = 'chk';
      this.renderAll();
    }
  }

  private action(a: string) {
    switch (a) {
      case 'undo': return this.undo();
      case 'find': return this.findDialog();
      case 'revs': return void (this.local ? this.localRevisionsDialog() : this.revisionsDialog());
      case 'allowf':
        // a click: the browser may ask the user for the file again
        return void getLocal(this.id).then(async (r) => {
          if (r && (await allowFile(r))) {
            this.savedRev = -1;
            void this.flush();
          } else toast($t('Dosyaya erişim izni verilmedi; proje bu tarayıcıda kayıtlı.'));
        });
      case 'layers': return void this.layersDialog();
      case 'share': return void (this.local ? toast(LOCAL_ONLY()) : this.shareDialog());
      case 'hloff':
        this.highlight = null;
        this.renderPage();
        return this.renderStatus();
      case 'tia': return void this.serverDownload('plc-tags.xlsx', $t("TIA Portal etiket tablosu hazırlanıyor…"));
      case 'lrail': return this.setTool(this.tool === 'lrail' ? 'select' : 'lrail');
      case 'lduct': return this.setTool(this.tool === 'lduct' ? 'select' : 'lduct');
      case 'pgen': return this.generateDialog('mcc');
      case 'cgen': return this.generateDialog('control');
      case 'kgen': return this.compensationDialog();
      case 'dgen': return this.distributionDialog();
      case 'bgen': return this.boardDialog();
      case 'paddsl': return this.singleLineDialog();
      case 'bbox': return this.blackBoxDialog();
      case 'tglleft': return this.togglePanel('left');
      case 'tglright': return this.togglePanel('right');
      case 'libopen':
        this.ui.closed = [];
        this.saveUi();
        return this.renderLib();
      case 'libclose':
        this.ui.closed = ['@recent', ...new Set(LIB_ORDER.map((k) => LIB[k].cat))];
        this.saveUi();
        return this.renderLib();
      case 'padddoor':
        return this.mutate(() => {
          // the size of the board: the nearest mounting plate before it (its first compartment on a floor-standing board)
          const plate = this.pr.pages.slice(0, this.cur + 1).reverse().find((p) => p.layout && !p.layout.door)?.layout;
          const [w, h] = plate ? doorSizes([plate])[0] : [600, 800];
          const front: Project['pages'][number] = { id: newId(), desc: $t("Pano kapağı ön görünüş"), kind: 'layout', layout: blankLayout(w, h, true), syms: [], wires: [] };
          this.pr.pages.splice(this.cur + 1, 0, front, { ...doorInside(front), desc: $t("Pano kapağı iç görünüş") });
          this.cur++;
          this.sel.clear();
          this.setTool('select');
        });
      case 'paddrep':
        return this.reportPagesDialog();
      case 'paddlayout':
        return this.mutate(() => {
          this.pr.pages.splice(this.cur + 1, 0, { id: newId(), desc: $t("Pano yerleşimi"), kind: 'layout', layout: blankLayout(), syms: [], wires: [] });
          this.cur++;
          this.sel.clear();
          this.setTool('select');
        });
      case 'redo': return this.redo();
      case 'tsel': return this.setTool('select');
      case 'twire': return this.setTool('wire');
      case 'rot': return this.rotSel();
      case 'del': return this.delSel();
      case 'zin': return this.zoomAt(1.25);
      case 'zout': return this.zoomAt(1 / 1.25);
      case 'fit': return this.fit();
      case 'grid': this.opts.grid = !this.opts.grid; this.renderTools(); return this.renderPage();
      case 'net': this.opts.net = !this.opts.net; this.renderTools(); return this.renderPage();
      case 'msch': this.mode = 'sch'; this.renderAll(); return this.applyView();
      case 'mrep': this.mode = 'rep'; return this.renderAll();
      case 'mnav': this.mode = 'nav'; return this.renderAll();
      case 'export': return this.exportJson();
      case 'saveas': return this.saveAsDialog();
      case 'pdf': return void this.serverDownload('export.pdf', $t("PDF hazırlanıyor…"));
      case 'xlsx': return void this.serverDownload('reports.xlsx', $t("Excel hazırlanıyor…"));
      case 'translate': return void this.translateDialog();
      case 'dxf': return this.dxfDialog();
      case 'import': return (this.$('#impFile') as HTMLInputElement).click();
      case 'padd':
        return this.mutate(() => {
          this.pr.pages.splice(this.cur + 1, 0, { id: newId(), desc: $t("Yeni sayfa"), syms: [], wires: [] });
          this.cur++;
          this.sel.clear();
        });
      case 'pup':
        if (this.cur > 0) this.mutate(() => this.pr.pages.splice(this.cur - 1, 0, ...this.pr.pages.splice(this.cur--, 1)));
        return;
      case 'pdn':
        if (this.cur < this.pr.pages.length - 1) this.mutate(() => this.pr.pages.splice(this.cur + 1, 0, ...this.pr.pages.splice(this.cur++, 1)));
        return;
      case 'pmacro':
        return this.savePageMacroDialog();
      case 'pdel': {
        if (this.pr.pages.length < 2) return;
        const pg = this.page;
        const close = openModal(
          `<h2>${$t("Sayfa {0} silinsin mi?", this.cur + 1)}</h2><p>${$t("\"{0}\" sayfasındaki {1} sembol ve {2} bağlantı silinecek. Geri al ile döndürebilirsiniz.", esc(pg.desc), pg.syms.length, pg.wires.length)}</p>
          <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Sayfayı sil")}</button></div>`,
          (m) => {
            if (m !== 'ok') return;
            close();
            this.mutate(() => {
              this.pr.pages.splice(this.cur, 1);
              this.cur = Math.max(0, this.cur - 1);
              this.sel.clear();
            });
          },
        );
      }
    }
  }

  private onField(t: HTMLInputElement | HTMLSelectElement) {
    const f = t.dataset.f;
    if (!f) return;
    let v = t.value;
    this.mutate(() => {
      if (f.startsWith('pr.')) return void ((this.pr as any)[f.slice(3)] = v);
      if (f === 'symfn') {
        // every copy of the drawing: the shared symbol gets the library symbol it stands for, kept in the project
        const c = customSymbol(this.selItems().S[0]?.type ?? '');
        if (c) registerCustomSymbols([{ ...c, fn: v || undefined }]);
        return void embedCustomSymbols(this.pr);
      }
      if (f.startsWith('fr.')) {
        const fr = { ...this.pr.frame }, k = f.slice(3);
        const m = /^(label|value)(\d)$/.exec(k);
        if (m) {
          const fields = [0, 1].map((i) => ({ ...{ label: '', value: '' }, ...fr.fields?.[i] }));
          fields[+m[2]][m[1] as 'label' | 'value'] = v;
          fr.fields = fields.filter((x) => x.label || x.value);
        } else (fr as any)[k] = v;
        this.pr.frame = normaliseTitleBlock(fr);
        return;
      }
      if (f.startsWith('ly.') || f.startsWith('lo.')) return this.layoutField(f, v);
      if (f.startsWith('st.')) {
        const val = f === 'st.wireNoPot' ? (t as HTMLInputElement).checked : v;
        this.pr.settings = { ...DEFAULT_SETTINGS, ...this.pr.settings, [f.slice(3)]: val };
        if (f === 'st.standard') queueMicrotask(() => this.renderLib());
        return;
      }
      if (f === 'w.no') {
        const { W } = this.selItems();
        W.forEach((w) => (v.trim() ? (w.no = v.trim()) : delete w.no));
        return;
      }
      if (f === 'pg.desc') return void (this.page.desc = v);
      if (/^(pg|s)\.(fn|loc|site|ud)$/.test(f)) {
        const target: any = f.startsWith('pg.') ? this.page : this.selItems().S[0];
        const key = f.slice(f.indexOf('.') + 1);
        if (!target) return;
        const val = v.replace(/^[=+#\s]+/, '').replace(/\s+/g, '').toLocaleUpperCase('tr');
        if (val) target[key] = val;
        else delete target[key];
        return;
      }
      const { S, W } = this.selItems();
      if (f.startsWith('w.')) {
        if (v || W.length === 1) W.forEach((w) => ((w as any)[f.slice(2)] = v));
        return;
      }
      const s = S[0];
      if (!s) return;
      if (f === 's.tag') {
        v = normaliseTag(v);
        s.tag = v;
        const L = LIB[s.type];
        if (L.role === 'main') this.mem[prefixFor(s.type, this.std)] = v;
        if (L.role === 'term') this.mem.X = v;
        return;
      }
      if (f === 's.part') {
        s.part = v;
        s.maker = v ? partOf(v)?.maker ?? s.maker : '';
        // a cable part brings its type, cores, cross-section and screen
        if (LIB[s.type].role === 'cable') Object.assign(s.props, cablePropsOf(partOf(v)));
        return;
      }
      if (f.startsWith('s.')) return void ((s as any)[f.slice(2)] = v);
      if (f.startsWith('p.')) {
        const key = f.slice(2) as keyof Sym['props'];
        (s.props as any)[key] = key === 'size' || key === 'len' || key === 'w' || key === 'h' ? +v : key === 'shield' ? ((t as HTMLInputElement).checked ? '1' : '') : v;
        if (key === 'name' && LIB[s.type].role === 'pot' && !s.props.ptype) s.props.ptype = ptypeFromName(v);
      }
    });
  }

  /**
   * Touch: one finger always pans (also when it starts on a symbol, so panning never moves one by accident); a tap
   * selects what it was on, or clears the selection; held still for a moment on something, the finger picks it up
   * (with the rest of the selection) and moves it.
   */
  private touchPan(e: PointerEvent, tap?: string, pickUp?: () => void) {
    this.drag = { k: 'pan', btn: -1, sx: e.clientX, sy: e.clientY, vx: this.view.x, vy: this.view.y, moved: false, ...(tap ? { tap } : {}) };
    this.svg.setPointerCapture(e.pointerId);
    clearTimeout(this.holdTimer);
    if (tap && pickUp && !this.readOnly)
      this.holdTimer = window.setTimeout(() => {
        const d = this.drag;
        if (d?.k !== 'pan' || d.btn !== -1 || d.moved || this.touches.size !== 1) return;
        this.drag = null;
        if (!this.sel.has(tap)) {
          this.sel.clear();
          this.sel.add(tap);
        }
        navigator.vibrate?.(15);
        pickUp();
        this.renderPage();
        this.renderProps();
      }, 450);
  }

  /** What a finger at w is on: the smallest symbol within about 10 px, else a wire that close. */
  private touchHit(w: Pt): string | undefined {
    const tol = 10 / this.view.s;
    let best: [string, number] | undefined;
    for (const s of this.page.syms) {
      const b = bbWorld(s);
      if (w[0] < b[0] - tol || w[0] > b[2] + tol || w[1] < b[1] - tol || w[1] > b[3] + tol) continue;
      const area = (b[2] - b[0]) * (b[3] - b[1]);
      if (!best || area < best[1]) best = ['s:' + s.id, area];
    }
    if (best) return best[0];
    for (const x of this.page.wires)
      for (let k = 1; k < x.pts.length; k++) {
        const [a, b] = [x.pts[k - 1], x.pts[k]], L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
        const t = Math.max(0, Math.min(1, ((w[0] - a[0]) * (b[0] - a[0]) + (w[1] - a[1]) * (b[1] - a[1])) / (L * L)));
        if (Math.hypot(w[0] - a[0] - t * (b[0] - a[0]), w[1] - a[1] - t * (b[1] - a[1])) < tol) return 'w:' + x.id;
      }
    return undefined;
  }

  /** A second finger: what the first one started ends, the two fingers zoom (spread / pinch) and pan. */
  private startPinch() {
    clearTimeout(this.holdTimer);
    if (this.drag?.k === 'box' || this.drag?.k === 'pan') this.drag = null;
    else if (this.drag) this.onUp();
    this.drag = null;
    const [a, b] = [...this.touches.values()];
    this.pinch = { d: Math.hypot(a[0] - b[0], a[1] - b[1]), cx: (a[0] + b[0]) / 2, cy: (a[1] + b[1]) / 2 };
    this.renderOverlay();
  }

  private onDown(e: PointerEvent) {
    if (e.pointerType === 'touch') {
      this.touches.set(e.pointerId, [e.clientX, e.clientY]);
      // every finger stays with the drawing, so its lifting is seen even off it
      this.svg.setPointerCapture(e.pointerId);
      if (this.touches.size === 2) return this.startPinch();
      if (this.touches.size > 2) return;
    }
    const w = this.toWorld(e);
    this.mouse = w;
    if (e.button === 1 || e.button === 2 || (this.spaceDown && e.button === 0)) {
      this.drag = { k: 'pan', btn: e.button, sx: e.clientX, sy: e.clientY, vx: this.view.x, vy: this.view.y, moved: false };
      this.svg.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    if (this.readOnly && this.tool !== 'select') this.setTool('select');
    if (this.page.kind === 'layout') return this.layoutDown(e, w);
    if (this.page.kind === 'report') return void (e.pointerType === 'touch' && this.touchPan(e));
    if (this.tool === 'place') return this.placeAt([snapV(w[0]), snapV(w[1])]);
    if (this.tool === 'macro' && this.placeMacro) {
      const m = this.placeMacro;
      this.mutate(() => {
        const r = insertMacro(this.pr, this.page, m, w, this.macroValues);
        this.sel.clear();
        r.syms.forEach((s) => this.sel.add('s:' + s.id));
        r.wires.forEach((x) => this.sel.add('w:' + x.id));
      });
      toast($t("\"{0}\" yerleştirildi", m.name));
      return this.setTool('select');
    }
    if (this.tool === 'wire') return this.wireClick(w, e.shiftKey);
    if (e.pointerType === 'touch') {
      const key = this.touchHit(w);
      return this.touchPan(e, key, () => this.startMove(w));
    }
    const t = (e.target as Element).closest<HTMLElement | SVGElement>('[data-sid],[data-wid]');
    if (t) {
      const key = t.dataset.sid ? 's:' + t.dataset.sid : 'w:' + t.dataset.wid;
      if (e.shiftKey || e.ctrlKey) {
        if (this.sel.has(key)) this.sel.delete(key);
        else this.sel.add(key);
        this.renderPage();
        return this.renderProps();
      }
      if (!this.sel.has(key)) {
        this.sel.clear();
        this.sel.add(key);
      }
      this.renderPage();
      this.renderProps();
      if (!this.readOnly) this.startMove(w);
    } else {
      if (!e.shiftKey) this.sel.clear();
      this.drag = { k: 'box', a: w, b: w };
      this.renderPage();
      this.renderProps();
    }
    this.svg.setPointerCapture(e.pointerId);
  }

  private onMove(e: PointerEvent) {
    if (e.pointerType === 'touch' && this.touches.has(e.pointerId)) {
      this.touches.set(e.pointerId, [e.clientX, e.clientY]);
      if (this.pinch && this.touches.size >= 2) {
        // the midpoint moves the drawing along, the distance between the fingers zooms about it
        const [a, b] = [...this.touches.values()];
        const d = Math.hypot(a[0] - b[0], a[1] - b[1]), cx = (a[0] + b[0]) / 2, cy = (a[1] + b[1]) / 2;
        this.view.x -= (cx - this.pinch.cx) / this.view.s;
        this.view.y -= (cy - this.pinch.cy) / this.view.s;
        if (this.pinch.d > 0 && d > 0) this.zoomAt(d / this.pinch.d, cx, cy);
        else this.applyView();
        this.pinch = { d, cx, cy };
        return;
      }
    }
    const w = this.toWorld(e);
    this.mouse = w;
    this.$('#stPos').innerHTML = `${$t("X <b>{0}</b> Y <b>{1}</b> mm · Sütun <b>{2}</b> · Sayfa <b>{3}/{4}</b>", w[0].toFixed(0), w[1].toFixed(0), colOf(w[0]), this.cur + 1, this.pr.pages.length)}`;
    const d = this.drag;
    if (!d) {
      if (this.tool !== 'select') this.renderOverlay();
      return;
    }
    if (d.k === 'pan') {
      const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
      if (Math.abs(dx) + Math.abs(dy) > (d.btn === -1 ? 8 : 3)) d.moved = true;
      this.view.x = d.vx - dx / this.view.s;
      this.view.y = d.vy - dy / this.view.s;
      return this.applyView();
    }
    if (d.k === 'lmove' || d.k === 'ldraw') return this.layoutDrag(d, w);
    if (d.k === 'move') {
      const dx = snapV(w[0] - d.a[0]), dy = snapV(w[1] - d.a[1]);
      if (dx || dy || d.moved) {
        d.moved = true;
        this.applyMove(d, dx, dy);
        this.A = analyze(this.pr, partOf);
        this.renderPage();
      }
      return;
    }
    d.b = w;
    this.renderOverlay();
  }

  private onUp(e?: PointerEvent) {
    clearTimeout(this.holdTimer);
    if (e?.pointerType === 'touch') {
      this.touches.delete(e.pointerId);
      if (this.pinch) {
        // the zoom ends with the second finger; the last one lifting starts nothing
        if (this.touches.size < 2) this.pinch = null;
        this.drag = null;
        return;
      }
    }
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    if (d.k === 'pan') {
      // a tap: selects what it was on, or on empty drawing clears the selection, as a click does
      if (d.btn === -1 && !d.moved) {
        if (this.tool !== 'select') return;
        this.sel.clear();
        if (d.tap) this.sel.add(d.tap);
        this.renderPage();
        return this.renderProps();
      }
      if (d.btn === 2 && !d.moved) {
        if (this.tool === 'wire' && this.draft.length > 1) this.finishWire();
        else if (this.tool !== 'select') this.setTool('select');
        else {
          this.sel.clear();
          this.renderPage();
          this.renderProps();
        }
      }
      return;
    }
    if (d.k === 'lmove' || d.k === 'ldraw') return this.layoutUp(d);
    if (d.k === 'move') {
      if (d.moved) {
        this.pushUndo(d.snap);
        [...d.full, ...d.att].forEach((f) => (f.w.pts = cleanPolyline(f.w.pts)));
        this.changed();
      }
      return;
    }
    const x0 = Math.min(d.a[0], d.b[0]), x1 = Math.max(d.a[0], d.b[0]), y0 = Math.min(d.a[1], d.b[1]), y1 = Math.max(d.a[1], d.b[1]);
    if (x1 - x0 > 1 || y1 - y0 > 1) {
      for (const s of this.page.syms) {
        const B = bbWorld(s);
        if (B[0] >= x0 && B[2] <= x1 && B[1] >= y0 && B[3] <= y1) this.sel.add('s:' + s.id);
      }
      for (const w of this.page.wires) if (w.pts.every((p) => p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1)) this.sel.add('w:' + w.id);
    }
    this.renderPage();
    this.renderProps();
  }

  private onKey(e: KeyboardEvent) {
    if (document.querySelector('.modal')) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && /INPUT|TEXTAREA|SELECT/.test(ae.tagName)) {
      if (e.key === 'Enter' && ae.tagName === 'INPUT') ae.blur();
      return;
    }
    const k = e.key.toLowerCase(), c = e.ctrlKey || e.metaKey;
    if (e.key === 'Escape' && this.el.querySelector('details.menu[open]')) return this.closeMenus();
    if (!c && !e.altKey && (e.key === '[' || e.key === ']')) {
      e.preventDefault();
      return this.togglePanel(e.key === '[' ? 'left' : 'right');
    }
    if (c) {
      if (k === 'f') {
        e.preventDefault();
        return this.findDialog();
      }
      if (k === 'z' && !e.shiftKey) this.undo();
      else if (k === 'y' || (k === 'z' && e.shiftKey)) this.redo();
      else if (k === 'c') { if (this.copySel()) toast($t("Kopyalandı")); return; }
      else if (k === 'v') this.paste(this.mouse);
      else if (k === 'd') { if (this.copySel()) this.paste(null); }
      else if (k === 's') { void this.flush(); }
      else if (k === 'a') {
        this.sel.clear();
        this.page.syms.forEach((s) => this.sel.add('s:' + s.id));
        this.page.wires.forEach((w) => this.sel.add('w:' + w.id));
        this.renderPage();
        this.renderProps();
      } else return;
      e.preventDefault();
      return;
    }
    switch (k) {
      case 'escape':
        if (this.draft.length) {
          if (this.draft.length > 1) this.finishWire();
          else {
            this.draft = [];
            this.renderOverlay();
          }
        } else if (this.tool !== 'select') this.setTool('select');
        else {
          this.sel.clear();
          this.renderPage();
          this.renderProps();
        }
        break;
      case 'enter': if (this.tool === 'wire' && this.draft.length > 1) this.finishWire(); break;
      case 'v': this.setTool('select'); break;
      case 'w': this.setTool('wire'); break;
      case 'r': this.rotSel(); break;
      case 'f': this.fit(); break;
      case 'delete': case 'backspace': e.preventDefault(); this.delSel(); break;
      case 'pageup': e.preventDefault(); this.gotoPage(this.cur - 1); break;
      case 'pagedown': e.preventDefault(); this.gotoPage(this.cur + 1); break;
      case ' ': this.spaceDown = true; e.preventDefault(); break;
    }
  }
}

/** Suggests the next revision label: A → B, Z → AA, 01 → 02. */
function nextRev(cur?: string): string {
  if (!cur) return 'A';
  if (/^\d+$/.test(cur)) return String(Number(cur) + 1).padStart(cur.length, '0');
  if (/^[A-Z]+$/.test(cur)) {
    const a = cur.split('');
    let i = a.length - 1;
    while (i >= 0 && a[i] === 'Z') a[i--] = 'A';
    if (i < 0) return 'A' + a.join('');
    a[i] = String.fromCharCode(a[i].charCodeAt(0) + 1);
    return a.join('');
  }
  return cur + '1';
}

/** Reads a logo picture and scales it down (max 600 × 200 px) to a PNG / JPEG data URL. */
async function logoDataUrl(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise((ok, bad) => {
      img.onload = ok;
      img.onerror = () => bad(new Error($t('Resim okunamadı; PNG ya da JPG seçin.')));
      img.src = url;
    });
    const k = Math.min(1, 600 / img.naturalWidth, 200 / img.naturalHeight);
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.naturalWidth * k));
    c.height = Math.max(1, Math.round(img.naturalHeight * k));
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    let out = c.toDataURL('image/png');
    if (out.length > MAX_LOGO) out = c.toDataURL('image/jpeg', 0.85);
    if (out.length > MAX_LOGO) throw new Error($t('Logo çok büyük; daha küçük bir resim seçin.'));
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}
